import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Paper,
  Grid,
  LinearProgress,
  Alert,
  Tabs,
  Tab,
  Tooltip,
  IconButton,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  TableSortLabel,
  Chip,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  ListItemText,
  Card,
  CardContent,
  TextField,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  OutlinedInput
} from '@mui/material';
import { Info as InfoIcon, ArrowUpward as ArrowUpwardIcon, ArrowDownward as ArrowDownwardIcon, Download as DownloadIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, Delete as DeleteIcon, Sync as SyncIcon, Replay as ReplayIcon, Refresh as RefreshIcon, DragIndicator as DragIndicatorIcon, Shield as ShieldOutlinedIcon, Undo as UndoIcon } from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Pie, Bar, Line, getElementAtEvent } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend, CategoryScale, LinearScale, BarElement, PointElement, LineElement } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { get, post, getAllFixVersions, getEnabledTeams, getTeamsWithStreams, authGet, getBugCycleTimeMetrics, fetchA11yBugAnalysis, getA11yBugs, syncEngTsiItems, getEngTsiItems, fetchProductionBugCodeFixes, fetchBugCodeAnalysis, fetchBugCodeAnalysisHistory, deleteBugCodeAnalysis, syncBugCodeAnalysisFromAdo, getEscalatedIssues, lookupEscalatedIssue, setEscalatedIssuesOrder, deleteEscalatedIssue, patchEscalatedIssueAdminMitigated } from '../../services/apiService';
import { isAdmin } from '../../services/authService';
import ProductionBugsByFixVersionChart from '../charts/ProductionBugsByFixVersionChart';
import ProductionBugsByComponentChart from '../charts/ProductionBugsByComponentChart';
import BugCycleTimeChart from '../charts/BugCycleTimeChart';
import WorkItemsDialog from '../workitems/WorkItemsDialog';
import ResolutionTimeDetailsDialog from './ResolutionTimeDetailsDialog';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getEscalatedRowStatusLabel, isEscalatedRowDone } from '../../utils/escalatedRowStatus';
import { getEscalatedItemClientLabels, rowMatchesEscalatedTicketIdSearch } from '../../utils/escalatedClients';

// Register all required Chart.js components
ChartJS.register(
  ArcElement, 
  ChartTooltip, 
  Legend, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  PointElement, 
  LineElement,
  ChartDataLabels
);

/** localStorage key for Code Analysis repo/branch log (persists until next analysis run) */
const CODE_ANALYSIS_SCAN_LOG_KEY = 'metricsPortal_codeAnalysis_scanLog';

const ALL_DATA_ROWS_PER_PAGE = 100;

function isAllDataBugClosedState(bug) {
  const s = (bug.state || '').toString().trim().toLowerCase();
  return s === 'closed' || s === 'resolved' || s === 'done' || s === 'completed';
}

/** `YYYY-MM-DD` → local start of day; invalid → null */
function parseAllDataLocalDateStart(yyyyMmDd) {
  if (!yyyyMmDd || typeof yyyyMmDd !== 'string') return null;
  const m = yyyyMmDd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const y = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10);
  const d = parseInt(m[3], 10);
  const dt = new Date(y, mo - 1, d, 0, 0, 0, 0);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

/** `YYYY-MM-DD` → local end of day; invalid → null */
function parseAllDataLocalDateEnd(yyyyMmDd) {
  if (!yyyyMmDd || typeof yyyyMmDd !== 'string') return null;
  const m = yyyyMmDd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const y = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10);
  const d = parseInt(m[3], 10);
  const dt = new Date(y, mo - 1, d, 23, 59, 59, 999);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function allDataBugMatchesCreatedDateRange(bug, fromStr, toStr) {
  const fromD = parseAllDataLocalDateStart(fromStr);
  const toD = parseAllDataLocalDateEnd(toStr);
  if (!fromD && !toD) return true;
  const cd = bug.createdDate ? new Date(bug.createdDate) : null;
  if (!cd || Number.isNaN(cd.getTime())) return false;
  if (fromD && cd < fromD) return false;
  if (toD && cd > toD) return false;
  return true;
}

/** Eng-TSI table: hide these ADO fields in the UI only (still stored in eng-tsi-items). */
const ENG_TSI_UI_HIDDEN_COLUMN_EXACT = new Set([
  'System.Tags',
  'System.AssignedTo',
  'System.CreatedBy',
  'Microsoft.VSTS.Common.Severity',
  'System.RelatedLinkCount'
]);

function isEngTsiHiddenFromDisplayColumn(col) {
  if (col == null || col === '') return false;
  const s = String(col).trim();
  if (ENG_TSI_UI_HIDDEN_COLUMN_EXACT.has(s)) return true;
  const lower = s.toLowerCase();
  const lastSeg = lower.includes('.') ? lower.split('.').pop() : lower;
  if (lastSeg === 'severity') return true;
  if (lastSeg === 'relatedlinkcount' || lower.includes('relatedlinkcount')) return true;
  const lowerSp = lower.replace(/\s+/g, ' ');
  if (lowerSp === 'investigation summary') return true;
  const alnum = lower.replace(/[^a-z0-9]/g, '');
  if (alnum.includes('investigationsummary')) return true;
  return false;
}

/** Eng-TSI column header: last segment after `.`, underscores → spaces (e.g. System.Title → Title). */
function engTsiColumnHeaderLabel(fieldRef) {
  const s = String(fieldRef || '').trim();
  if (!s) return '';
  const last = s.includes('.') ? s.split('.').pop() : s;
  const lastSeg = (last || s).trim();
  if (lastSeg.replace(/_/g, '').toLowerCase() === 'investigationclassification') {
    return 'Classification';
  }
  return lastSeg.replace(/_/g, ' ').trim();
}

function isEngTsiTitleColumn(col) {
  if (!col) return false;
  const s = String(col).trim();
  if (s === 'System.Title') return true;
  const last = s.includes('.') ? s.split('.').pop().toLowerCase() : s.toLowerCase();
  return last === 'title';
}

/** Created / closed / resolved date fields — show date only in the grid (full value still in DB). */
function isEngTsiDateOnlyColumn(col) {
  if (!col) return false;
  const lower = String(col).trim().toLowerCase();
  const last = lower.includes('.') ? lower.split('.').pop() : lower;
  return last === 'createddate' || last === 'closeddate';
}

function engTsiFormatDateOnly(raw) {
  if (raw == null || raw === '') return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw).trim() || '—';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function engTsiFormatCellDisplay(col, val) {
  if (isEngTsiDateOnlyColumn(col)) {
    return engTsiFormatDateOnly(val);
  }
  if (val == null) return '—';
  const s = typeof val === 'object' ? JSON.stringify(val) : String(val);
  const maxLen = isEngTsiTitleColumn(col) ? 2000 : 200;
  if (s.length > maxLen) return `${s.slice(0, maxLen)}…`;
  return s;
}

const ENG_TSI_TITLE_COL_PCT = 38;
const ENG_TSI_DATE_COL_PCT_EACH = 9;

/** Width % and min/max for one Eng-TSI column; handles all-date / all-title edge cases. */
function getEngTsiColumnWidthModel(col, displayCols) {
  const titles = displayCols.filter(isEngTsiTitleColumn);
  const dates = displayCols.filter(isEngTsiDateOnlyColumn);
  const others = displayCols.filter(
    (c) => !isEngTsiTitleColumn(c) && !isEngTsiDateOnlyColumn(c)
  );
  if (titles.length === 0 && others.length === 0 && dates.length > 0) {
    const pct = 100 / dates.length;
    return { widthPct: pct, minWidth: 108, maxWidth: 160, kind: 'date' };
  }
  if (dates.length === 0 && others.length === 0 && titles.length > 0) {
    const pct = 100 / titles.length;
    return { widthPct: pct, minWidth: 260, maxWidth: 560, kind: 'title' };
  }
  const used =
    (titles.length > 0 ? ENG_TSI_TITLE_COL_PCT : 0) +
    dates.length * ENG_TSI_DATE_COL_PCT_EACH;
  const otherEach = others.length > 0 ? Math.max(0, (100 - used) / others.length) : 0;
  if (isEngTsiTitleColumn(col)) {
    return {
      widthPct: ENG_TSI_TITLE_COL_PCT / Math.max(titles.length, 1),
      minWidth: 260,
      maxWidth: 560,
      kind: 'title'
    };
  }
  if (isEngTsiDateOnlyColumn(col)) {
    return { widthPct: ENG_TSI_DATE_COL_PCT_EACH, minWidth: 108, maxWidth: 140, kind: 'date' };
  }
  return { widthPct: otherEach, minWidth: 96, maxWidth: 220, kind: 'other' };
}

function getEngTsiColumnSx(col, displayCols) {
  const m = getEngTsiColumnWidthModel(col, displayCols);
  return {
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    lineHeight: 1.25,
    verticalAlign: 'bottom',
    py: 1,
    px: 1,
    width: `${m.widthPct}%`,
    minWidth: m.minWidth,
    maxWidth: m.maxWidth
  };
}

function getEngTsiBodyCellSx(col, displayCols) {
  const m = getEngTsiColumnWidthModel(col, displayCols);
  return {
    verticalAlign: 'top',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    py: 1,
    px: 1,
    width: `${m.widthPct}%`,
    minWidth: m.minWidth,
    maxWidth: m.maxWidth
  };
}

function compareEngTsiRowsForSort(rowA, rowB, col, direction) {
  const dir = direction === 'asc' ? 1 : -1;
  const va = rowA[col];
  const vb = rowB[col];
  const empty = (v) => v == null || v === '';
  if (empty(va) && empty(vb)) return 0;
  if (empty(va)) return 1;
  if (empty(vb)) return -1;

  const colStr = String(col).trim();
  const lastSeg = colStr.includes('.') ? colStr.split('.').pop().toLowerCase() : colStr.toLowerCase();
  if (lastSeg === 'id' || colStr === 'System.Id' || colStr === 'ID') {
    const na = Number(String(va).replace(/[^\d.-]/g, ''));
    const nb = Number(String(vb).replace(/[^\d.-]/g, ''));
    if (Number.isFinite(na) && Number.isFinite(nb)) {
      return (na - nb) * dir;
    }
  }
  if (isEngTsiDateOnlyColumn(col)) {
    const da = new Date(va);
    const db = new Date(vb);
    const ta = da.getTime();
    const tb = db.getTime();
    if (!Number.isNaN(ta) && !Number.isNaN(tb)) {
      return (ta - tb) * dir;
    }
  }
  const numA = Number(String(va).replace(/,/g, ''));
  const numB = Number(String(vb).replace(/,/g, ''));
  if (String(va).trim() !== '' && String(vb).trim() !== '' && Number.isFinite(numA) && Number.isFinite(numB)) {
    return (numA - numB) * dir;
  }
  return (
    String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' }) * dir
  );
}

function isEngTsiIdColumn(col) {
  if (!col) return false;
  const s = String(col).trim();
  if (s === 'System.Id' || s === 'ID') return true;
  const last = s.includes('.') ? s.split('.').pop().toLowerCase() : s.toLowerCase();
  return last === 'id';
}

function parseEngTsiWorkItemId(raw) {
  if (raw == null || raw === '') return null;
  const n = parseInt(String(raw).replace(/\D/g, ''), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Field ref whose name includes InvestigationClassification (any prefix). */
function findEngTsiClassificationColumnRef(cols) {
  if (!cols || !cols.length) return null;
  for (const c of cols) {
    const compact = String(c).replace(/[^a-z0-9]/gi, '').toLowerCase();
    if (compact.includes('investigationclassification')) return c;
  }
  return null;
}

function findEngTsiStateColumnRef(cols) {
  if (!cols || !cols.length) return null;
  for (const c of cols) {
    const t = String(c).trim();
    if (t === 'System.State') return c;
    const last = t.includes('.') ? t.split('.').pop().toLowerCase() : t.toLowerCase();
    if (last === 'state') return c;
  }
  return null;
}

function findEngTsiEnvironmentColumnRef(cols) {
  if (!cols || !cols.length) return null;
  for (const c of cols) {
    const t = String(c).trim();
    const last = t.includes('.') ? t.split('.').pop().toLowerCase() : t.toLowerCase();
    if (last === 'environment' || last.includes('environment')) return c;
  }
  return null;
}

function aggregateEngTsiFieldCounts(rows, fieldRef) {
  const counts = {};
  if (!fieldRef || !rows || !rows.length) return counts;
  for (const row of rows) {
    const v = row[fieldRef];
    const key = v == null || String(v).trim() === '' ? '(empty)' : String(v).trim();
    counts[key] = (counts[key] || 0) + 1;
  }
  return counts;
}

/** Strip ADO assignee clutter: `<email>` and `(Org)` segments; collapse spaces */
function stripAssigneeDisplayClutter(text) {
  if (text == null || text === '') return '';
  let t = String(text).trim();
  t = t.replace(/\s*<[^>]*>\s*/g, ' ').trim();
  let prev;
  do {
    prev = t;
    t = t.replace(/\s*\([^)]*\)\s*/g, ' ').trim();
  } while (t !== prev);
  return t.replace(/\s+/g, ' ').trim();
}

function normalizeProductionBugAssignee(assignedTo) {
  if (assignedTo == null || assignedTo === '') return 'Unassigned';
  const raw =
    typeof assignedTo === 'object' && assignedTo.displayName
      ? String(assignedTo.displayName).trim()
      : String(assignedTo).trim();
  if (!raw) return 'Unassigned';
  const cleaned = stripAssigneeDisplayClutter(raw);
  return cleaned || 'Unassigned';
}

function normalizeProductionBugPriorityLabel(priority) {
  if (priority == null || priority === '') return '—';
  const s = String(priority).trim();
  if (/^p\d+$/i.test(s)) return s.toUpperCase();
  if (/^\d+$/.test(s)) return `P${s}`;
  return s;
}

/**
 * Row matches selected client name (case-insensitive) if any L2 or Engineering client chip matches.
 */
function escalatedRowMatchesClientNameFilter(row, selected) {
  if (selected == null || selected === 'all') return true;
  const want = String(selected).trim().toLowerCase();
  if (want === '') return true;
  const matchItem = (item) => {
    if (item == null || item.id == null) return false;
    return getEscalatedItemClientLabels(item).some((l) => l.toLowerCase() === want);
  };
  const investigations = (row.investigations || []).filter((i) => i != null && i.id != null);
  const linkedBugs = (row.linkedBugs || []).filter((b) => b != null && b.id != null);
  return investigations.some(matchItem) || linkedBugs.some(matchItem);
}

/**
 * Table sort key (epoch ms), descending = newest first.
 * Uses the latest Investigation createdDate among L2 items; if none or no dates, uses row syncedAt.
 */
function escalatedRowSortTimestampMs(row) {
  if (row == null) return 0;
  const investigations = (row.investigations || []).filter((i) => i != null && i.id != null);
  let maxMs = 0;
  let hasCreated = false;
  for (const inv of investigations) {
    const d = inv.createdDate;
    if (d == null) continue;
    const t = d instanceof Date ? d.getTime() : new Date(d).getTime();
    if (Number.isNaN(t)) continue;
    hasCreated = true;
    if (t > maxMs) maxMs = t;
  }
  if (hasCreated) return maxMs;
  const s = row.syncedAt;
  if (s != null) {
    const t = s instanceof Date ? s.getTime() : new Date(s).getTime();
    return Number.isNaN(t) ? 0 : t;
  }
  return 0;
}

/**
 * Active rows first (manual displayOrder, then recency). Closed rows always after active,
 * sorted by recency only (displayOrder ignored).
 */
function compareEscalatedRowsForDisplay(a, b) {
  const aDone = isEscalatedRowDone(a);
  const bDone = isEscalatedRowDone(b);
  if (aDone !== bDone) return aDone ? 1 : -1;
  if (aDone && bDone) {
    return escalatedRowSortTimestampMs(b) - escalatedRowSortTimestampMs(a);
  }
  const ao = a && a.displayOrder;
  const bo = b && b.displayOrder;
  const aHas = ao != null && Number.isFinite(Number(ao));
  const bHas = bo != null && Number.isFinite(Number(bo));
  if (aHas && bHas) return Number(ao) - Number(bo);
  if (aHas && !bHas) return -1;
  if (!aHas && bHas) return 1;
  return escalatedRowSortTimestampMs(b) - escalatedRowSortTimestampMs(a);
}

/** Table body row with optional dnd-kit sort handle (`disabled` when filters hide the full list). */
function EscalatedSortableTableRow({ id, disabled, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: !!disabled
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : undefined
  };
  return (
    <TableRow ref={setNodeRef} style={style}>
      {children(disabled ? null : { attributes, listeners, isDragging })}
    </TableRow>
  );
}

/** Age in days for an escalated work item (same rules as row UI); null if unknown. */
function escalatedItemAgeDays(item) {
  if (item == null) return null;
  const ad = item.ageDays;
  if (ad != null && ad !== '' && Number.isFinite(Number(ad))) {
    return Number(ad);
  }
  const start = item.createdDate ? new Date(item.createdDate) : null;
  if (!start || Number.isNaN(start.getTime())) return null;
  const end = item.closedDate ? new Date(item.closedDate) : new Date();
  if (Number.isNaN(end.getTime())) return null;
  return Math.max(0, (end - start) / 86400000);
}

/** One-line preview for L2 / Engineering columns when the row is collapsed. */
function escalatedColumnCollapsedSummary(items) {
  const list = (items || []).filter((i) => i != null && i.id != null);
  if (list.length === 0) return '—';
  const first = list[0];
  const title =
    first.title != null && String(first.title).trim() !== '' ? String(first.title).trim() : '';
  const more = list.length > 1 ? ` (+${list.length - 1} more)` : '';
  const core = title ? `${first.id} · ${title}` : String(first.id);
  const maxLen = 120;
  const truncated = core.length > maxLen ? `${core.slice(0, maxLen)}…` : core;
  return `${truncated}${more}`;
}

/** Outlined state chip on L2 / Engineering rows: Active orange, Closed/Done green, other blue. */
function getEscalatedItemStateChipSx(stateLabel) {
  const raw =
    stateLabel === '—' || stateLabel == null ? '' : String(stateLabel).trim();
  if (!raw) {
    return { color: '#1976d2', borderColor: 'rgba(25, 118, 210, 0.45)' };
  }
  const s = raw.toLowerCase();
  if (s === 'active') {
    return { color: '#ed6c02', borderColor: 'rgba(237, 108, 2, 0.45)' };
  }
  if (s === 'closed' || s === 'done') {
    return { color: '#2e7d32', borderColor: 'rgba(46, 125, 50, 0.45)' };
  }
  return { color: '#1976d2', borderColor: 'rgba(25, 118, 210, 0.45)' };
}

/** Priority chip: background color by P1–P4; black text. TBD uses neutral gray. */
function getEscalatedPriorityChipSx(priorityNum) {
  const text = { color: 'rgba(0, 0, 0, 0.87)' };
  const base = {
    ...text,
    border: 'none',
    fontWeight: 500
  };
  if (priorityNum === 1) return { ...base, backgroundColor: '#ffcdd2' };
  if (priorityNum === 2) return { ...base, backgroundColor: '#ffe082' };
  if (priorityNum === 3) return { ...base, backgroundColor: '#90caf9' };
  if (priorityNum === 4) return { ...base, backgroundColor: '#ce93d8' };
  return { ...base, backgroundColor: '#e0e0e0' };
}

/**
 * ProductionDefects component - Displays infographics for production defects
 * @returns {JSX.Element} ProductionDefects component
 */
const ProductionDefects = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedState, setSelectedState] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('');
  const [selectedFixVersion, setSelectedFixVersion] = useState('');
  const [sortField, setSortField] = useState('age');
  const [sortDirection, setSortDirection] = useState('desc');
  const [sortFieldSwat, setSortFieldSwat] = useState('age');
  const [sortDirectionSwat, setSortDirectionSwat] = useState('desc');
  const [selectedTeamRca, setSelectedTeamRca] = useState('');
  const [selectedRca, setSelectedRca] = useState('');
  const [sortFieldRca, setSortFieldRca] = useState('closedDate');
  const [sortDirectionRca, setSortDirectionRca] = useState('desc');
  const [environmentData, setEnvironmentData] = useState(null);
  const [environmentLoading, setEnvironmentLoading] = useState(false);
  const [selectedEnvironment, setSelectedEnvironment] = useState('');
  const [selectedTeamEnv, setSelectedTeamEnv] = useState('');
  const [sortFieldEnv, setSortFieldEnv] = useState('age');
  const [sortDirectionEnv, setSortDirectionEnv] = useState('desc');
  const [analysisData, setAnalysisData] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [sortFieldAnalysis, setSortFieldAnalysis] = useState('createdDate');
  const [sortDirectionAnalysis, setSortDirectionAnalysis] = useState('desc');
  const [fixVersionsMap, setFixVersionsMap] = useState({});
  const [staticReleaseDates, setStaticReleaseDates] = useState({});
  const [showValidOnly, setShowValidOnly] = useState(false);
  const [tableExpanded, setTableExpanded] = useState(true);
  const [sortFieldPivot, setSortFieldPivot] = useState('total');
  const [sortDirectionPivot, setSortDirectionPivot] = useState('desc');
  const [teamsData, setTeamsData] = useState([]);
  const [selectedPriorities, setSelectedPriorities] = useState([]);
  const [cycleTimeData, setCycleTimeData] = useState(null);
  const [cycleTimeLoading, setCycleTimeLoading] = useState(false);
  const [cycleTimeError, setCycleTimeError] = useState(null);
  const [incomingTrends, setIncomingTrends] = useState({
    labels: [],
    dateKeys: [],
    environments: [],
    countsByPriorityAndEnv: { P1: {}, P2: {}, P3: {}, P4: {} },
    bugsByDateKey: {}
  });
  const [incomingTrendsLoading, setIncomingTrendsLoading] = useState(false);
  const [incomingTrendsError, setIncomingTrendsError] = useState(null);
  const incomingTrendsChartRefs = useRef({});
  const [resolutionDetailsOpen, setResolutionDetailsOpen] = useState(false);
  const [resolutionDetailsMonthKey, setResolutionDetailsMonthKey] = useState('');
  const [incomingDayDialogOpen, setIncomingDayDialogOpen] = useState(false);
  const [incomingDayDialogDateKey, setIncomingDayDialogDateKey] = useState('');
  const [incomingDayDialogLabel, setIncomingDayDialogLabel] = useState('');
  const [incomingDayDialogPriority, setIncomingDayDialogPriority] = useState('');
  const [incomingDayDialogBugs, setIncomingDayDialogBugs] = useState([]);
  // WIP Analysis tab state
  const [wipData, setWipData] = useState([]);
  const [wipLoading, setWipLoading] = useState(false);
  const [wipExpanded, setWipExpanded] = useState(false);
  const [wipFilterTeam, setWipFilterTeam] = useState([]);
  const [wipFilterFixVersion, setWipFilterFixVersion] = useState([]);
  const [wipFilterIteration, setWipFilterIteration] = useState([]);
  const [wipSortField, setWipSortField] = useState('createdDate');
  const [wipSortDirection, setWipSortDirection] = useState('desc');
  const [wipFixVersions, setWipFixVersions] = useState([]);
  const [wipTeams, setWipTeams] = useState([]);
  const [closedBugsForCurrentSprint, setClosedBugsForCurrentSprint] = useState([]);
  const [wipResolvedByMonthByTeam, setWipResolvedByMonthByTeam] = useState({ monthLabels: [], monthKeys: [], rows: [] });
  const [expandedResolvedStreams, setExpandedResolvedStreams] = useState({}); // stream name -> true/false; undefined = collapsed (all collapsed by default)
  const [teamsWithStreamsForResolved, setTeamsWithStreamsForResolved] = useState([]); // from team management: { id, displayName, stream }
  const [bugDetailsDialogOpen, setBugDetailsDialogOpen] = useState(false);
  const [bugDetailsDialogBugs, setBugDetailsDialogBugs] = useState([]);
  const [bugDetailsDialogTitle, setBugDetailsDialogTitle] = useState('');
  // A11Y Bug Analysis tab state
  const [a11yFilterUrl, setA11yFilterUrl] = useState('');
  const [a11yLoading, setA11yLoading] = useState(false);
  const [a11yError, setA11yError] = useState(null);
  const [a11yData, setA11yData] = useState({ columns: [], rows: [] });
  const [a11ySyncedAt, setA11ySyncedAt] = useState(null);
  const [a11yExcludeMissingDates, setA11yExcludeMissingDates] = useState(false);
  const [a11ySortBy, setA11ySortBy] = useState(null);
  const [a11ySortOrder, setA11ySortOrder] = useState('asc');
  const [a11yPage, setA11yPage] = useState(0);
  const a11yRowsPerPage = 100;
  // Code Analysis tab state
  const [codeAnalysisAdoId, setCodeAnalysisAdoId] = useState('');
  const [codeAnalysisLoading, setCodeAnalysisLoading] = useState(false);
  const [codeAnalysisError, setCodeAnalysisError] = useState(null);
  /** Last successful analysis: repos/branch log + refresh time (persisted until next run) */
  const [codeAnalysisScanLog, setCodeAnalysisScanLog] = useState(null);

  // Analysis history state
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [analysisHistoryLoading, setAnalysisHistoryLoading] = useState(false);
  const [analysisHistoryPage, setAnalysisHistoryPage] = useState(0);
  const [analysisHistoryLimit] = useState(10);
  const [analysisHistoryTotal, setAnalysisHistoryTotal] = useState(0);
  const [expandedAnalysis, setExpandedAnalysis] = useState({});
  const [syncingAdoId, setSyncingAdoId] = useState(null);
  const [regeneratingAdoId, setRegeneratingAdoId] = useState(null);

  // All Data tab: full production bugs list + multi-select filters
  const [allDataBugs, setAllDataBugs] = useState([]);
  const [allDataLoading, setAllDataLoading] = useState(false);
  const [allDataError, setAllDataError] = useState(null);
  const [allDataFilterState, setAllDataFilterState] = useState([]);
  const [allDataFilterAssignedTo, setAllDataFilterAssignedTo] = useState([]);
  const [allDataFilterTeam, setAllDataFilterTeam] = useState([]);
  const [allDataFilterIteration, setAllDataFilterIteration] = useState([]);
  const [allDataFilterPriority, setAllDataFilterPriority] = useState([]);
  const [allDataFilterCreatedFrom, setAllDataFilterCreatedFrom] = useState('');
  const [allDataFilterCreatedTo, setAllDataFilterCreatedTo] = useState('');
  const [allDataSortField, setAllDataSortField] = useState('createdDate');
  const [allDataSortDirection, setAllDataSortDirection] = useState('desc');
  const [allDataPage, setAllDataPage] = useState(0);
  const [allDataCodeFixLoading, setAllDataCodeFixLoading] = useState(false);

  // Eng-TSI tab: ADO filter → eng-tsi-items collection
  const [engTsiFilterInput, setEngTsiFilterInput] = useState('');
  const [engTsiLoading, setEngTsiLoading] = useState(false);
  const [engTsiError, setEngTsiError] = useState(null);
  const [engTsiData, setEngTsiData] = useState({ columns: [], rows: [] });
  const [engTsiSyncedAt, setEngTsiSyncedAt] = useState(null);
  const [engTsiPage, setEngTsiPage] = useState(0);
  const engTsiRowsPerPage = 100;
  const [engTsiSortField, setEngTsiSortField] = useState(null);
  const [engTsiSortDirection, setEngTsiSortDirection] = useState('asc');
  const [engTsiTableExpanded, setEngTsiTableExpanded] = useState(true);

  // Escalated Issues tab
  const [escalatedTicketInput, setEscalatedTicketInput] = useState('');
  const [escalatedLoading, setEscalatedLoading] = useState(false);
  const [escalatedError, setEscalatedError] = useState(null);
  const [escalatedRows, setEscalatedRows] = useState([]);
  /** When set, row-level refresh is in progress for this L1 ticket id */
  const [escalatedRefreshingId, setEscalatedRefreshingId] = useState(null);
  /** Row-level status filter: All | Active | Mitigated | Closed | Not applicable */
  const [escalatedStateFilter, setEscalatedStateFilter] = useState('active');
  /** When true, only rows with at least one L2 (Investigation) work item */
  const [escalatedShowL2Only, setEscalatedShowL2Only] = useState(false);
  /** When true, only rows with at least one Engineering (linked bug) work item */
  const [escalatedShowEngOnly, setEscalatedShowEngOnly] = useState(false);
  /** "all" or a client name from L2 (instance) or Engineering (client_name_dummy) */
  const [escalatedClientNameFilter, setEscalatedClientNameFilter] = useState('all');
  /** Substring match against Salesforce/Zendesk id, L2/Eng ADO ids, and l1TicketUrl */
  const [escalatedTicketIdSearch, setEscalatedTicketIdSearch] = useState('');
  /** Row keys expanded in the Escalated Issues table; omitted = collapsed (default all collapsed) */
  const [escalatedExpandedRowIds, setEscalatedExpandedRowIds] = useState(() => new Set());
  const [escalatedOrderSaving, setEscalatedOrderSaving] = useState(false);
  /** Batch refresh-all-active in progress (top toolbar) */
  const [escalatedRefreshingAll, setEscalatedRefreshingAll] = useState(false);
  /** Mongo _id of row currently being deleted (admin) */
  const [escalatedDeletingId, setEscalatedDeletingId] = useState(null);
  /** Mongo _id of row while admin "mark mitigated" PATCH is in flight */
  const [escalatedMitigatingId, setEscalatedMitigatingId] = useState(null);

  const escalatedAllowAdminDelete = isAdmin();
  /** Refresh + delete only; Mitigated admin column (shield / undo) is on the left. */
  const escalatedActionsColWidth = escalatedAllowAdminDelete ? 96 : 72;

  /** Distinct client names across loaded rows (L2 + Engineering), sorted for the dropdown. */
  const escalatedClientNameFilterOptions = useMemo(() => {
    const byKey = new Map();
    for (const row of escalatedRows) {
      const bump = (item) => {
        for (const raw of getEscalatedItemClientLabels(item)) {
          const key = raw.toLowerCase();
          if (!byKey.has(key)) byKey.set(key, raw);
        }
      };
      (row.investigations || []).forEach((i) => {
        if (i != null && i.id != null) bump(i);
      });
      (row.linkedBugs || []).forEach((b) => {
        if (b != null && b.id != null) bump(b);
      });
    }
    return [...byKey.values()].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
  }, [escalatedRows]);

  useEffect(() => {
    if (escalatedClientNameFilter === 'all') return;
    const sel = escalatedClientNameFilter.toLowerCase();
    const ok = escalatedClientNameFilterOptions.some((o) => o.toLowerCase() === sel);
    if (!ok) setEscalatedClientNameFilter('all');
  }, [escalatedClientNameFilter, escalatedClientNameFilterOptions]);

  /** Reorder applies only to non-Closed rows; Closed rows stay at the bottom. Disabled when filters hide part of the list. */
  const escalatedReorderEnabled = useMemo(
    () =>
      escalatedStateFilter === 'all' &&
      !escalatedShowL2Only &&
      !escalatedShowEngOnly &&
      escalatedClientNameFilter === 'all' &&
      !String(escalatedTicketIdSearch).trim(),
    [
      escalatedStateFilter,
      escalatedShowL2Only,
      escalatedShowEngOnly,
      escalatedClientNameFilter,
      escalatedTicketIdSearch
    ]
  );

  const filteredEscalatedRows = useMemo(() => {
    const rowStatusLabel = (row) => {
      const investigations = (row.investigations || []).filter((i) => i != null && i.id != null);
      const linkedBugs = (row.linkedBugs || []).filter((b) => b != null && b.id != null);
      return getEscalatedRowStatusLabel(investigations, linkedBugs, row.adminMitigated === true);
    };

    const hasL2Linked = (row) =>
      (row.investigations || []).some((i) => i != null && i.id != null);
    const hasEngLinked = (row) => (row.linkedBugs || []).some((b) => b != null && b.id != null);

    let rows = [...escalatedRows];
    if (escalatedShowL2Only) {
      rows = rows.filter(hasL2Linked);
    }
    if (escalatedShowEngOnly) {
      rows = rows.filter(hasEngLinked);
    }
    if (escalatedClientNameFilter !== 'all') {
      rows = rows.filter((row) => escalatedRowMatchesClientNameFilter(row, escalatedClientNameFilter));
    }
    if (String(escalatedTicketIdSearch).trim()) {
      rows = rows.filter((row) => rowMatchesEscalatedTicketIdSearch(row, escalatedTicketIdSearch));
    }

    if (escalatedStateFilter === 'active') {
      rows = rows.filter((row) => rowStatusLabel(row) === 'Active');
    } else if (escalatedStateFilter === 'mitigated') {
      rows = rows.filter((row) => rowStatusLabel(row) === 'Mitigated');
    } else if (escalatedStateFilter === 'closed') {
      rows = rows.filter((row) => rowStatusLabel(row) === 'Closed');
    } else if (escalatedStateFilter === 'not-applicable') {
      rows = rows.filter((row) => rowStatusLabel(row) === 'Not Applicable');
    }

    const sortEscalated = (list) => [...list].sort(compareEscalatedRowsForDisplay);
    return sortEscalated(rows);
  }, [
    escalatedRows,
    escalatedStateFilter,
    escalatedShowL2Only,
    escalatedShowEngOnly,
    escalatedClientNameFilter,
    escalatedTicketIdSearch
  ]);

  /** Shown when any filter narrows the list or the filtered set is smaller than all rows. */
  const showEscalatedFilteredTotalCount = useMemo(() => {
    if (escalatedRows.length === 0) return false;
    return (
      escalatedStateFilter !== 'all' ||
      escalatedClientNameFilter !== 'all' ||
      escalatedShowL2Only ||
      escalatedShowEngOnly ||
      String(escalatedTicketIdSearch).trim() !== '' ||
      filteredEscalatedRows.length !== escalatedRows.length
    );
  }, [
    escalatedRows.length,
    filteredEscalatedRows.length,
    escalatedStateFilter,
    escalatedClientNameFilter,
    escalatedShowL2Only,
    escalatedShowEngOnly,
    escalatedTicketIdSearch
  ]);

  const escalatedActiveRows = useMemo(
    () => filteredEscalatedRows.filter((row) => !isEscalatedRowDone(row)),
    [filteredEscalatedRows]
  );

  const escalatedDoneRows = useMemo(
    () => filteredEscalatedRows.filter((row) => isEscalatedRowDone(row)),
    [filteredEscalatedRows]
  );

  const showEscalatedDragColumn = escalatedReorderEnabled && escalatedActiveRows.length > 0;
  const showEscalatedMitigateColumn = escalatedAllowAdminDelete;
  const escalatedEmptyRowColSpan =
    5 + (showEscalatedDragColumn ? 1 : 0) + (showEscalatedMitigateColumn ? 1 : 0);

  const escalatedSortableIds = useMemo(
    () => escalatedActiveRows.map((r) => String(r._id != null ? r._id : r.userEnteredId)),
    [escalatedActiveRows]
  );

  const escalatedSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleEscalatedDragEnd = useCallback(
    async (event) => {
      if (!escalatedReorderEnabled) return;
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const ids = escalatedSortableIds;
      const oldIndex = ids.indexOf(String(active.id));
      const newIndex = ids.indexOf(String(over.id));
      if (oldIndex < 0 || newIndex < 0) return;
      const ordered = arrayMove(escalatedActiveRows, oldIndex, newIndex);
      const orderedUserEnteredIds = ordered.map((r) => r.userEnteredId);
      setEscalatedOrderSaving(true);
      setEscalatedError(null);
      try {
        await setEscalatedIssuesOrder(orderedUserEnteredIds);
        const rows = await getEscalatedIssues({ limit: 500 });
        setEscalatedRows(Array.isArray(rows) ? rows : []);
      } catch (err) {
        console.error('Escalated order save failed', err);
        setEscalatedError(err?.message || 'Failed to save row order');
      } finally {
        setEscalatedOrderSaving(false);
      }
    },
    [escalatedReorderEnabled, escalatedSortableIds, escalatedActiveRows]
  );

  const handleDeleteEscalatedRow = useCallback(async (row) => {
    const mongoId = row?._id;
    if (mongoId == null) {
      setEscalatedError('Cannot delete this row (missing id).');
      return;
    }
    const idStr = String(mongoId);
    const label = String(row.userEnteredId || idStr);
    if (!window.confirm(`Remove escalated issue "${label}" from the list? This cannot be undone.`)) return;
    setEscalatedDeletingId(idStr);
    setEscalatedError(null);
    try {
      await deleteEscalatedIssue(idStr);
      setEscalatedRows((prev) => prev.filter((r) => String(r._id) !== idStr));
      setEscalatedExpandedRowIds((prev) => {
        const next = new Set(prev);
        next.delete(String(row.userEnteredId));
        next.delete(idStr);
        return next;
      });
    } catch (err) {
      console.error('[EscalatedIssues] delete failed', err);
      setEscalatedError(err?.message || 'Failed to delete row');
    } finally {
      setEscalatedDeletingId(null);
    }
  }, []);

  /** Set or clear the admin Mitigated flag (PATCH). Clearing does not override ADO-derived mitigation (PBI/Feature/release). */
  const handlePatchEscalatedAdminMitigated = useCallback(async (row, adminMitigated) => {
    const mongoId = row?._id;
    if (mongoId == null) {
      setEscalatedError('Cannot update this row (missing id).');
      return;
    }
    const idStr = String(mongoId);
    setEscalatedMitigatingId(idStr);
    setEscalatedError(null);
    try {
      const updated = await patchEscalatedIssueAdminMitigated(idStr, adminMitigated);
      setEscalatedRows((prev) => {
        const list = Array.isArray(prev) ? [...prev] : [];
        const idx = list.findIndex((r) => String(r._id) === idStr);
        if (idx >= 0) {
          const am = updated && typeof updated.adminMitigated === 'boolean' ? updated.adminMitigated : !!adminMitigated;
          list[idx] = { ...list[idx], ...updated, adminMitigated: am };
          return list;
        }
        return list;
      });
    } catch (err) {
      console.error('[EscalatedIssues] adminMitigated patch failed', err);
      setEscalatedError(
        err?.message ||
          (adminMitigated ? 'Failed to mark row as Mitigated' : 'Failed to clear admin Mitigated flag')
      );
    } finally {
      setEscalatedMitigatingId(null);
    }
  }, []);

  /** Escalated Issues summary chips: same Active/Closed rule as the table Status column */
  const escalatedSummaryCounts = useMemo(() => {
    let open = 0;
    let closed = 0;
    for (const row of escalatedRows) {
      const investigations = (row.investigations || []).filter((i) => i != null && i.id != null);
      const linkedBugs = (row.linkedBugs || []).filter((b) => b != null && b.id != null);
      const label = getEscalatedRowStatusLabel(investigations, linkedBugs, row.adminMitigated === true);
      if (label === 'Closed') closed += 1;
      else open += 1;
    }
    const total = escalatedRows.length;
    const openPct = total > 0 ? Math.round((open / total) * 100) : 0;
    const closedPct = total > 0 ? Math.round((closed / total) * 100) : 0;
    return { total, open, closed, openPct, closedPct };
  }, [escalatedRows]);

  /** Mean age (days) over all L2 / Engineering work items with known age; counts = issues in that average. */
  const escalatedL2EngAgeStats = useMemo(() => {
    const l2Ages = [];
    const engAges = [];
    for (const row of escalatedRows) {
      const investigations = (row.investigations || []).filter((i) => i != null && i.id != null);
      const linkedBugs = (row.linkedBugs || []).filter((b) => b != null && b.id != null);
      for (const item of investigations) {
        const d = escalatedItemAgeDays(item);
        if (d != null) l2Ages.push(d);
      }
      for (const item of linkedBugs) {
        const d = escalatedItemAgeDays(item);
        if (d != null) engAges.push(d);
      }
    }
    const mean = (arr) => (arr.length === 0 ? null : arr.reduce((a, b) => a + b, 0) / arr.length);
    return {
      l2AvgDays: mean(l2Ages),
      l2Count: l2Ages.length,
      engAvgDays: mean(engAges),
      engCount: engAges.length
    };
  }, [escalatedRows]);

  /**
   * Conversion ratios (all loaded escalations):
   * 1) % of L1 rows with ≥1 L2 linked
   * 2) % of L2 work items that sit in an L1 row which also has ≥1 Engineering ticket (same escalation)
   */
  const escalatedConversionRatios = useMemo(() => {
    let l1WithL2 = 0;
    let l2Total = 0;
    let l2InRowsWithEng = 0;
    const nL1 = escalatedRows.length;
    for (const row of escalatedRows) {
      const investigations = (row.investigations || []).filter((i) => i != null && i.id != null);
      const linkedBugs = (row.linkedBugs || []).filter((b) => b != null && b.id != null);
      if (investigations.length > 0) l1WithL2 += 1;
      l2Total += investigations.length;
      if (investigations.length > 0 && linkedBugs.length > 0) {
        l2InRowsWithEng += investigations.length;
      }
    }
    const pctL1WithL2 = nL1 > 0 ? Math.round((l1WithL2 / nL1) * 100) : 0;
    const pctL2WithEng = l2Total > 0 ? Math.round((l2InRowsWithEng / l2Total) * 100) : 0;
    return {
      pctL1WithL2,
      pctL2WithEng,
      l1WithL2Count: l1WithL2,
      nL1,
      l2InRowsWithEngCount: l2InRowsWithEng,
      l2Total
    };
  }, [escalatedRows]);

  // Helper function to normalize version format (5.8.8 -> 5.8.08, 5.8.08 -> 5.8.08)
  const normalizeVersion = (version) => {
    if (!version) return version;
    const versionStr = String(version).trim();
    // Match pattern like 5.8.8 or 5.8.08
    const match = versionStr.match(/^(5\.\d+)\.(\d+)$/);
    if (match) {
      const majorMinor = match[1];
      const patch = match[2];
      // Pad patch version to 2 digits if it's a single digit
      const normalizedPatch = patch.length === 1 ? `0${patch}` : patch;
      return `${majorMinor}.${normalizedPatch}`;
    }
    return versionStr;
  };

  // Helper function to categorize fix versions
  const categorizeFixVersion = (fixVersion) => {
    if (!fixVersion) return { collabVersions: [], msVersions: [] };
    
    const versions = String(fixVersion).split(';').map(v => v.trim()).filter(v => v);
    const collabVersions = [];
    const msVersions = [];
    
    versions.forEach(version => {
      // Check if version starts with "5." (Collab Version pattern)
      if (/^5\./.test(version)) {
        collabVersions.push(version);
      } else {
        msVersions.push(version);
      }
    });
    
    return { collabVersions, msVersions };
  };

  // Color mapping for different states
  const getStateColor = (state) => {
    const stateLower = (state || '').toLowerCase();
    if (stateLower.includes('closed') || stateLower.includes('resolved')) {
      return '#4caf50'; // Green
    } else if (stateLower.includes('active') || stateLower.includes('new')) {
      return '#f44336'; // Red
    } else if (stateLower.includes('in progress') || stateLower.includes('assigned')) {
      return '#ff9800'; // Orange
    } else if (stateLower.includes('pending') || stateLower.includes('blocked')) {
      return '#9e9e9e'; // Grey
    } else {
      return '#2196f3'; // Blue (default)
    }
  };

  // Helper function to parse sprint dates from sprint name (e.g., "2025_S12_Jun04-Jun17")
  const parseSprintDates = (sprintName) => {
    try {
      const datePart = sprintName.split('_').pop();
      const [startPart, endPart] = datePart.split('-');
      const year = sprintName.split('_')[0];
      
      const monthMap = {
        'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5,
        'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11
      };
      
      const startMonth = startPart.substring(0, 3);
      const startDay = startPart.substring(3);
      const endMonth = endPart.substring(0, 3);
      const endDay = endPart.substring(3);
      
      const startDate = new Date(Date.UTC(parseInt(year), monthMap[startMonth], parseInt(startDay), 0, 0, 0, 0));
      const endDate = new Date(Date.UTC(parseInt(year), monthMap[endMonth], parseInt(endDay), 23, 59, 59, 999));
      
      return { startDate, endDate };
    } catch (error) {
      console.error('Error parsing sprint dates:', error);
      return null;
    }
  };

  // Helper function to extract sprint name from iteration path
  const extractSprintName = (iterationPath) => {
    if (!iterationPath) return null;
    const parts = iterationPath.split('\\');
    return parts.length > 0 ? parts[parts.length - 1] : null;
  };

  // Stream order for "Production bugs resolved by team" table (WIP Analysis)
  const RESOLVED_STREAM_ORDER = ['Core', 'Coco for HighQ', 'Coco TXM', 'Platform & Compliance', 'Modernization', 'SWAT', 'Others'];

  // Normalize stream name for grouping (e.g. "Platform and Compliance" -> "Platform & Compliance")
  const normalizeStreamName = (stream) => {
    if (!stream) return 'Others';
    const s = String(stream).trim().replace(/\s+and\s+/gi, ' & ');
    return s || 'Others';
  };

  // Team management stream lookup: use getTeamsWithStreams() data (id, displayName, stream). SWAT by name → SWAT stream; unmapped → Others.
  const getStreamForResolvedTeam = (teamName, teamsWithStreamsArr) => {
    if (!teamName) return 'Others';
    const normalized = (teamName || '').trim();
    if (!normalized) return 'Others';
    // SWAT: treat any team whose name indicates SWAT as stream "SWAT" (single team)
    if (normalized.toLowerCase().includes('swat')) return 'SWAT';
    if (!Array.isArray(teamsWithStreamsArr) || teamsWithStreamsArr.length === 0) return 'Others';
    const lowerToStream = {};
    teamsWithStreamsArr.forEach(team => {
      const stream = normalizeStreamName(team.stream || '') || 'Others';
      const id = (team.id || '').trim();
      const displayName = (team.displayName || '').trim();
      if (id) lowerToStream[id.toLowerCase()] = stream;
      if (displayName) lowerToStream[displayName.toLowerCase()] = stream;
    });
    const lower = normalized.toLowerCase();
    if (lowerToStream[lower]) return lowerToStream[lower];
    const withoutSuffix = normalized.replace(/\s+[Tt]eam\s*$/, '').trim().toLowerCase();
    if (lowerToStream[withoutSuffix]) return lowerToStream[withoutSuffix];
    for (const [key, stream] of Object.entries(lowerToStream)) {
      if (lower === key || lower.startsWith(key) || key.startsWith(lower)) {
        const diff = lower.length > key.length ? lower.substring(key.length).trim() : key.substring(lower.length).trim();
        if (diff === '' || diff === 'team') return stream;
      }
    }
    return 'Others';
  };

  // Helper function to determine current and next sprints from bugs data
  const getCurrentAndNextSprints = (bugs) => {
    const now = new Date();
    const sprintMap = new Map();
    
    // Extract all unique sprint names and their dates
    bugs.forEach(bug => {
      if (bug.iterationPath) {
        const sprintName = extractSprintName(bug.iterationPath);
        if (sprintName && !sprintMap.has(sprintName)) {
          const dates = parseSprintDates(sprintName);
          if (dates) {
            sprintMap.set(sprintName, dates);
          }
        }
      }
    });
    
    // Find current sprint (sprint that contains today's date)
    let currentSprint = null;
    let nextSprint = null;
    const sortedSprints = Array.from(sprintMap.entries())
      .map(([name, dates]) => ({ name, ...dates }))
      .sort((a, b) => a.startDate - b.startDate);
    
    for (let i = 0; i < sortedSprints.length; i++) {
      const sprint = sortedSprints[i];
      if (now >= sprint.startDate && now <= sprint.endDate) {
        currentSprint = sprint.name;
        // Next sprint is the one after current
        if (i + 1 < sortedSprints.length) {
          nextSprint = sortedSprints[i + 1].name;
        }
        break;
      } else if (now < sprint.startDate) {
        // If we haven't found current sprint yet and this sprint is in the future
        if (!currentSprint) {
          nextSprint = sprint.name;
        }
        break;
      }
    }
    
    // If no current sprint found, find the most recent past sprint and next future sprint
    if (!currentSprint) {
      for (let i = sortedSprints.length - 1; i >= 0; i--) {
        if (sortedSprints[i].endDate < now) {
          // This is the most recent past sprint, next one is the next sprint
          if (i + 1 < sortedSprints.length) {
            nextSprint = sortedSprints[i + 1].name;
          }
          break;
        }
      }
    }
    
    return { currentSprint, nextSprint, sprintMap };
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await get('/api/workitems/production-bugs/public/stats');
        
        if (response.success) {
          setStats(response.data);
        } else {
          throw new Error(response.message || 'Failed to fetch statistics');
        }
      } catch (err) {
        console.error('Error fetching production defects stats:', err);
        setError('Failed to load production defects statistics. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  // Fetch incoming trends data (created per day by priority for the last 15 days)
  useEffect(() => {
    const fetchIncomingTrends = async () => {
      try {
        setIncomingTrendsLoading(true);
        setIncomingTrendsError(null);

        const toYmd = (d) => d.toISOString().split('T')[0];

        // Build the 15-day label range (UTC YYYY-MM-DD keys for bucketing)
        const dateKeys = [];
        const labels = [];
        for (let i = 14; i >= 0; i--) {
          const d = new Date();
          d.setUTCDate(d.getUTCDate() - i);
          const key = toYmd(d);
          dateKeys.push(key);
          labels.push(d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }));
        }
        const startDate = dateKeys[0];

        // Pull only the last ~15 days of bugs (server filters by createdDate >= startDate)
        const response = await get('/api/workitems/production-bugs/public', { startDate, pageSize: 10000 });
        if (!response.success) {
          throw new Error(response.message || 'Failed to fetch production bugs for incoming trends');
        }

        const bugs = response.data || [];
        const priorities = ['P1', 'P2', 'P3', 'P4'];

        // Normalize + filter to only bugs within our 15-day window
        const normalized = bugs
          .map((bug) => {
            const priority = bug?.priority;
            const environment = (bug?.environment || 'Unknown').toString().trim() || 'Unknown';
            const createdDate = bug?.createdDate ? new Date(bug.createdDate) : null;
            if (!createdDate || Number.isNaN(createdDate.getTime())) return null;
            const key = toYmd(createdDate);
            return {
              key,
              priority,
              environment,
              bug: {
                id: bug?.id,
                title: bug?.title,
                state: bug?.state,
                team: bug?.team,
                priority: bug?.priority,
                environment: environment,
                createdDate: bug?.createdDate,
                closedDate: bug?.closedDate
              }
            };
          })
          .filter((x) => x && dateKeys.includes(x.key));

        // Environments list (sorted by total volume desc, then alpha)
        const envTotals = new Map();
        normalized.forEach(({ environment }) => {
          envTotals.set(environment, (envTotals.get(environment) || 0) + 1);
        });
        const environments = Array.from(envTotals.entries())
          .sort((a, b) => {
            const diff = (b[1] || 0) - (a[1] || 0);
            if (diff !== 0) return diff;
            return String(a[0]).localeCompare(String(b[0]));
          })
          .map(([env]) => env);

        // countsByPriorityAndEnv[priority][environment] = [count per day...]
        const countsByPriorityAndEnv = { P1: {}, P2: {}, P3: {}, P4: {} };
        priorities.forEach((p) => {
          environments.forEach((env) => {
            countsByPriorityAndEnv[p][env] = dateKeys.map(() => 0);
          });
        });

        const dateIndex = new Map(dateKeys.map((k, idx) => [k, idx]));
        normalized.forEach(({ priority, environment, key }) => {
          if (!priorities.includes(priority)) return;
          const idx = dateIndex.get(key);
          if (idx === undefined) return;
          if (!countsByPriorityAndEnv[priority][environment]) {
            countsByPriorityAndEnv[priority][environment] = dateKeys.map(() => 0);
          }
          countsByPriorityAndEnv[priority][environment][idx] += 1;
        });

        // bugsByDateKey: all bugs on that day (all priorities), for dialog
        const bugsByDateKey = {};
        dateKeys.forEach((k) => {
          bugsByDateKey[k] = [];
        });
        normalized.forEach(({ key, bug }) => {
          if (!bugsByDateKey[key]) bugsByDateKey[key] = [];
          bugsByDateKey[key].push(bug);
        });
        Object.keys(bugsByDateKey).forEach((k) => {
          bugsByDateKey[k].sort((a, b) => {
            // Sort by priority (P1..P4), then most recent first, then id desc
            const p = (x) => {
              const m = String(x?.priority || '').match(/P(\d+)/i);
              return m ? parseInt(m[1], 10) : 999;
            };
            const pa = p(a);
            const pb = p(b);
            if (pa !== pb) return pa - pb;
            const da = a?.createdDate ? new Date(a.createdDate).getTime() : 0;
            const db = b?.createdDate ? new Date(b.createdDate).getTime() : 0;
            if (da !== db) return db - da;
            return (parseInt(b?.id, 10) || 0) - (parseInt(a?.id, 10) || 0);
          });
        });

        setIncomingTrends({ labels, dateKeys, environments, countsByPriorityAndEnv, bugsByDateKey });
      } catch (err) {
        console.error('Error fetching incoming trends:', err);
        setIncomingTrendsError('Failed to load incoming trends. Please try again later.');
        setIncomingTrends({
          labels: [],
          dateKeys: [],
          environments: [],
          countsByPriorityAndEnv: { P1: {}, P2: {}, P3: {}, P4: {} },
          bugsByDateKey: {}
        });
      } finally {
        setIncomingTrendsLoading(false);
      }
    };

    fetchIncomingTrends();
  }, []);

  const getAdoWorkItemUrl = (id) => {
    const adoOrg = process.env.REACT_APP_AZURE_DEVOPS_ORG || 'tr-legal.visualstudio.com';
    const projectName = process.env.REACT_APP_AZURE_DEVOPS_PROJECT || 'HighQ Tech';
    const baseUrl = adoOrg.includes('visualstudio.com') 
      ? `https://${adoOrg}` 
      : `https://dev.azure.com/${adoOrg}`;
    return `${baseUrl}/${encodeURIComponent(projectName)}/_workitems/edit/${id}`;
  };

  /** Age display for escalated work items (prefers server ageDays; else from created/closed). */
  const escalatedItemAgeLabel = (item) => {
    if (item == null) return '—';
    const ad = item.ageDays;
    if (ad != null && ad !== '' && Number.isFinite(Number(ad))) {
      return `${Math.round(Number(ad) * 10) / 10}d`;
    }
    const start = item.createdDate ? new Date(item.createdDate) : null;
    if (!start || Number.isNaN(start.getTime())) return '—';
    const end = item.closedDate ? new Date(item.closedDate) : new Date();
    if (Number.isNaN(end.getTime())) return '—';
    const days = Math.max(0, (end - start) / 86400000);
    return `${Math.round(days * 10) / 10}d`;
  };

  const escalatedItemAgeTooltip = (item) => {
    const ageText = escalatedItemAgeLabel(item);
    if (ageText === '—') return 'Age unavailable (missing dates)';
    return `Age: ${ageText} (from created date; uses closed date when set, otherwise today)`;
  };

  const escalatedOverallStatusTooltip = (status, row) => {
    if (status === 'Closed') {
      return 'Closed: every L2 and Engineering item is Done, Closed, or Completed. Resolved does not count as closed for the row.';
    }
    if (status === 'Mitigated') {
      if (row && row.adminMitigated === true) {
        return 'Mitigated: an admin marked this row. It becomes Closed when every L2 and Engineering item is Done, Closed, or Completed.';
      }
      return 'Mitigated: L2 links to a PBI or Feature, an engineering Bug has a real release (not None/TBD), or an admin marked the row.';
    }
    if (status === 'Not Applicable') {
      return 'Not applicable: no L2 (Investigation) or Engineering work items are linked yet.';
    }
    return 'Active: at least one L2 item is open, or only Engineering items are linked. Engineering state alone does not close the row.';
  };

  const escalatedStatusChipColor = (status) => {
    if (status === 'Closed') return 'success';
    if (status === 'Mitigated') return 'info';
    if (status === 'Not Applicable') return 'default';
    return 'warning';
  };

  /** Escalated Issues: ID line, then chips Client → Priority → State → Release (eng) → Age; title below. Pass { showReleaseChip: true } for Engineering Bug/PBI. */
  const renderEscalatedWorkItemBlocks = (items, options = {}) => {
    const showReleaseChip = options.showReleaseChip === true;
    return (items || []).map((item, idx) => {
      const id = item.id;
      const stateLabel =
        item.state != null && String(item.state).trim() !== '' ? String(item.state).trim() : '—';
      const ageText = escalatedItemAgeLabel(item);
      const titleText = (item.title != null && String(item.title).trim() !== '')
        ? String(item.title).trim()
        : '—';
      const clientLabels = getEscalatedItemClientLabels(item);
      const rawFv = item.fixVersionDummy;
      const releaseValue =
        rawFv != null && String(rawFv).trim() !== '' ? String(rawFv).trim() : 'TBD';
      const clientTooltip =
        /^investigation$/i.test(item.workItemType || '')
          ? 'Client (from instance URL: first path segment after /, or subdomain if path is empty)'
          : 'Client (from client_name_dummy)';
      const pr = item.priority;
      const priorityChipLabel =
        pr != null && pr >= 1 && pr <= 4 ? `P${pr}` : 'TBD';
      const rawLc = item.latestCommentText != null ? String(item.latestCommentText).trim() : '';
      const lcAuthor = item.latestCommentAuthor != null ? String(item.latestCommentAuthor).trim() : '';
      const lcDateRaw = item.latestCommentDate != null ? String(item.latestCommentDate).trim() : '';
      let lcDateLabel = '';
      if (lcDateRaw) {
        try {
          const d = new Date(lcDateRaw);
          lcDateLabel = Number.isNaN(d.getTime()) ? lcDateRaw : d.toLocaleString();
        } catch {
          lcDateLabel = lcDateRaw;
        }
      }
      const hasCommentData = Boolean(rawLc || lcDateRaw);
      const latestCommentTooltip = [
        rawLc || '(No comment text)',
        lcAuthor ? `Author: ${lcAuthor}` : null,
        lcDateLabel ? `Posted: ${lcDateLabel}` : null
      ]
        .filter(Boolean)
        .join('\n\n');
      const wtNorm = String(item.workItemType || '').trim();
      const showPbiSuffix =
        showReleaseChip && /^product backlog item$/i.test(wtNorm);
      return (
        <Box
          key={`${id}-${idx}`}
          sx={{
            mb: idx < (items || []).length - 1 ? 1.5 : 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            maxWidth: '100%'
          }}
        >
          <Box sx={{ width: '100%', pb: 0.75, display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 0.5 }}>
            <Link
              href={getAdoWorkItemUrl(id)}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={{ fontWeight: 500, display: 'inline-block' }}
            >
              {id}
            </Link>
            {showPbiSuffix ? (
              <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                (PBI)
              </Typography>
            ) : null}
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              alignContent: 'flex-start',
              gap: 0.75,
              rowGap: 0.75,
              columnGap: 0.75,
              width: '100%'
            }}
          >
            {clientLabels.length > 0 ? (
              clientLabels.map((cl, ci) => (
                <Tooltip key={`${id}-c-${ci}`} title={clientTooltip}>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={cl}
                    sx={{ flexShrink: 0 }}
                  />
                </Tooltip>
              ))
            ) : (
              <Tooltip title={clientTooltip}>
                <Chip size="small" variant="outlined" label="TBD" sx={{ flexShrink: 0 }} />
              </Tooltip>
            )}
            <Tooltip title="Priority (ADO); P1–P4 when set, else TBD">
              <Chip
                size="small"
                variant="filled"
                label={priorityChipLabel}
                sx={{ ...getEscalatedPriorityChipSx(pr), flexShrink: 0 }}
              />
            </Tooltip>
            <Tooltip title={stateLabel === '—' ? 'State' : `State: ${stateLabel}`}>
              <Chip
                size="small"
                variant="outlined"
                label={stateLabel}
                sx={{ ...getEscalatedItemStateChipSx(stateLabel), flexShrink: 0 }}
              />
            </Tooltip>
            {showReleaseChip ? (
              <Tooltip title="Release target (ADO fix_version_dummy); TBD if not set">
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Release: ${releaseValue}`}
                  sx={{ flexShrink: 0 }}
                />
              </Tooltip>
            ) : null}
            <Tooltip title={escalatedItemAgeTooltip(item)}>
              <span style={{ display: 'inline-flex' }}>
                <Chip size="small" variant="outlined" label={ageText} sx={{ flexShrink: 0 }} />
              </span>
            </Tooltip>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4, display: 'block' }}>
            {titleText}
          </Typography>
          <Tooltip
            title={
              hasCommentData ? latestCommentTooltip : 'No discussion comments on this work item'
            }
          >
            <Box sx={{ mt: 2, cursor: hasCommentData ? 'help' : 'default' }}>
              {hasCommentData ? (
                <>
                  <Typography
                    variant="caption"
                    component="div"
                    sx={{
                      fontWeight: 600,
                      color: 'text.primary',
                      lineHeight: 1.35
                    }}
                  >
                    {lcDateLabel || '—'}
                  </Typography>
                  <Typography
                    variant="caption"
                    component="div"
                    color="text.secondary"
                    sx={{
                      mt: 0.25,
                      lineHeight: 1.35,
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      wordBreak: 'break-word'
                    }}
                  >
                    {rawLc || '—'}
                  </Typography>
                </>
              ) : (
                <Typography variant="caption" color="text.disabled">
                  —
                </Typography>
              )}
            </Box>
          </Tooltip>
        </Box>
      );
    });
  };

  const handleIncomingBarClick = (chartKey, datasetPriorities) => (event) => {
    try {
      const chart = incomingTrendsChartRefs.current?.[chartKey];
      if (!chart) return;
      const elements = getElementAtEvent(chart, event);
      if (!elements || elements.length === 0) return;
      const { datasetIndex, index } = elements[0];
      const priority = datasetPriorities?.[datasetIndex];
      const dateKey = incomingTrends.dateKeys?.[index];
      const label = incomingTrends.labels?.[index];
      if (!dateKey) return;

      const bugsForDayAll = incomingTrends.bugsByDateKey?.[dateKey] || [];
      const bugsForDayAndPriority = priority
        ? bugsForDayAll.filter((b) => b?.priority === priority)
        : bugsForDayAll;
      setIncomingDayDialogDateKey(dateKey);
      setIncomingDayDialogLabel(label || dateKey);
      setIncomingDayDialogPriority(priority || null);
      setIncomingDayDialogBugs(bugsForDayAndPriority);
      setIncomingDayDialogOpen(true);
    } catch (err) {
      console.error('Error handling incoming trends chart click:', err);
    }
  };

  const closeIncomingDayDialog = () => {
    setIncomingDayDialogOpen(false);
  };

  // Fetch environment data when tab is active
  useEffect(() => {
    const fetchEnvironmentData = async () => {
      if (activeTab === 4) { // By Environment tab
        try {
          setEnvironmentLoading(true);
          const response = await get('/api/workitems/production-bugs/public/by-environment');
          
          if (response.success) {
            setEnvironmentData(response.data);
          } else {
            throw new Error(response.message || 'Failed to fetch environment data');
          }
        } catch (err) {
          console.error('Error fetching environment data:', err);
          setError('Failed to load environment data. Please try again later.');
        } finally {
          setEnvironmentLoading(false);
        }
      }
    };

    fetchEnvironmentData();
  }, [activeTab]);

  // Fetch fix versions and static release dates when Analysis tab is active
  useEffect(() => {
    const fetchFixVersions = async () => {
      if (activeTab === 5) { // Analysis tab
        try {
          // Fetch fix versions from API
          const response = await getAllFixVersions();
          
          // Handle different response structures
          let fixVersions = [];
          if (response && response.data && Array.isArray(response.data)) {
            fixVersions = response.data;
          } else if (response && response.data && response.data.data && Array.isArray(response.data.data)) {
            fixVersions = response.data.data;
          } else if (Array.isArray(response)) {
            fixVersions = response;
          }
          
          // Filter active releases only
          const activeFixVersions = fixVersions.filter(fv => fv.active);
          
          // Create a map of version -> releaseDate
          const versionMap = {};
          activeFixVersions.forEach(fv => {
            if (fv.version && fv.releaseDate) {
              versionMap[fv.version] = fv.releaseDate;
            }
          });
          
          console.log('Analysis tab - Fix versions map:', versionMap);
          setFixVersionsMap(versionMap);
          
          // Fetch static release dates from JSON file
          try {
            // Add cache busting to ensure fresh data
            const staticResponse = await fetch(`/scripts/release-dates.json?t=${Date.now()}`);
            if (staticResponse.ok) {
              const staticData = await staticResponse.json();
              const staticMap = {};
              if (staticData.releases && Array.isArray(staticData.releases)) {
                staticData.releases.forEach(release => {
                  if (release.version && release.releaseDate) {
                    const normalizedVersion = normalizeVersion(release.version);
                    // Store with normalized version as key
                    // Also store with original version for backward compatibility
                    staticMap[normalizedVersion] = release.releaseDate;
                    staticMap[release.version] = release.releaseDate;
                    
                    // Also check if we need to add it to versionMap (if not already there)
                    if (!versionMap[normalizedVersion] && !versionMap[release.version]) {
                      // Don't override if already in versionMap
                    }
                  }
                });
              }
              console.log('Analysis tab - Static release dates map:', staticMap);
              setStaticReleaseDates(staticMap);
            } else {
              console.warn('Failed to fetch static release dates:', staticResponse.status);
            }
          } catch (staticErr) {
            console.warn('Could not load static release dates:', staticErr);
          }
        } catch (err) {
          console.error('Error fetching fix versions:', err);
          // Don't set error, just log it - release dates will show as N/A
        }
      }
    };

    fetchFixVersions();
  }, [activeTab]);

  // Fetch cycle time metrics when Analysis tab is active or priorities change
  useEffect(() => {
    const fetchCycleTimeMetrics = async () => {
      if (activeTab === 5) { // Analysis tab
        try {
          setCycleTimeLoading(true);
          setCycleTimeError(null);
          // Pass selected priorities to filter the data
          const response = await getBugCycleTimeMetrics(selectedPriorities.length > 0 ? selectedPriorities : null);
          if (response.success) {
            setCycleTimeData(response.data || []);
          } else {
            throw new Error(response.message || 'Failed to fetch cycle time metrics');
          }
        } catch (err) {
          console.error('Error fetching cycle time metrics:', err);
          setCycleTimeError(err.message || 'Failed to load cycle time metrics');
          setCycleTimeData([]);
        } finally {
          setCycleTimeLoading(false);
        }
      }
    };
    
    fetchCycleTimeMetrics();
  }, [activeTab, selectedPriorities]);

  // Fetch analysis data when Analysis tab is active
  useEffect(() => {
    const fetchAnalysisData = async () => {
      if (activeTab === 5) { // Analysis tab
        try {
          setAnalysisLoading(true);
          // Request a large page size to get all bugs (or handle pagination if needed)
          const response = await get('/api/workitems/production-bugs/public', { pageSize: 10000 });
          
          console.log('Analysis tab - Full API response:', response);
          
          if (response.success) {
            // Handle both direct data array and wrapped response
            const bugs = response.data || response || [];
            console.log('Analysis tab - Bugs array length:', Array.isArray(bugs) ? bugs.length : 'Not an array');
            console.log('Analysis tab - First bug sample:', Array.isArray(bugs) && bugs.length > 0 ? bugs[0] : 'No bugs');
            
            if (!Array.isArray(bugs)) {
              console.error('Analysis tab - Response data is not an array:', bugs);
              setAnalysisData([]);
              return;
            }
            
            // Filter for production bugs with fixVersion (not null/blank/None/TBD)
            // Note: API already filters for production bugs, but we double-check issueSource
            let validCount = 0;
            const bugsWithFixVersion = bugs.filter(bug => {
              // Check issueSource - API already filters for production, but double-check
              const issueSource = (bug.issueSource || '').toLowerCase();
              const isProduction = !issueSource || issueSource === 'production' || issueSource.includes('production');
              
              // Check both fixVersion and fixVersionDummy fields (API returns fixVersionDummy in formatted response)
              const fixVersion = bug.fixVersionDummy || bug.fixVersion || '';
              const fixVersionStr = String(fixVersion).trim();
              
              // Exclude null, blank, "none", and "tbd" values (case-insensitive)
              const fixVersionLower = fixVersionStr.toLowerCase();
              const hasValidFixVersion = fixVersionStr && 
                                        fixVersionStr !== '' && 
                                        fixVersionLower !== 'none' && 
                                        fixVersionLower !== 'tbd';
              
              const isValid = isProduction && hasValidFixVersion;
              
              if (isValid && validCount < 5) {
                console.log('Valid bug found:', { 
                  id: bug.id, 
                  fixVersion: fixVersion, 
                  fixVersionDummy: bug.fixVersionDummy,
                  fixVersionField: bug.fixVersion,
                  issueSource: bug.issueSource,
                  hasValidFixVersion,
                  isProduction
                });
                validCount++;
              }
              
              return isValid;
            });
            
            console.log('Analysis tab - Filtered bugs with fixVersion:', bugsWithFixVersion.length);
            console.log('Analysis tab - Sample of filtered bugs:', bugsWithFixVersion.slice(0, 3));
            setAnalysisData(bugsWithFixVersion);
          } else {
            console.error('Analysis tab - API response not successful:', response);
            throw new Error(response.message || 'Failed to fetch analysis data');
          }
        } catch (err) {
          console.error('Error fetching analysis data:', err);
          setError('Failed to load analysis data. Please try again later.');
          setAnalysisData([]);
        } finally {
          setAnalysisLoading(false);
        }
      }
    };

    fetchAnalysisData();
  }, [activeTab]);

  // Fetch WIP Analysis data when WIP Analysis tab is active
  useEffect(() => {
    const fetchWipData = async () => {
      if (activeTab === 6) { // WIP Analysis tab
        try {
          setWipLoading(true);
          setError(null);
          
          console.log('WIP Analysis - Fetching production bugs...');
          // Use public production-bugs endpoint (already filters for Bug + Production)
          const response = await get('/api/workitems/production-bugs/public', { 
            pageSize: 10000 
          });
          
          console.log('WIP Analysis - API response:', response);
          
          // Handle different response structures
          let allBugs = [];
          if (response && response.success !== false) {
            // Response might be { success: true, data: [...] } or { data: [...] } or just [...]
            if (response.data && Array.isArray(response.data)) {
              allBugs = response.data;
            } else if (response.success && response.data && Array.isArray(response.data)) {
              allBugs = response.data;
            } else if (Array.isArray(response)) {
              allBugs = response;
            }
          } else if (Array.isArray(response)) {
            allBugs = response;
          }
          
          if (!Array.isArray(allBugs)) {
            console.error('WIP Analysis - Response data is not an array. Response:', response);
            setWipData([]);
            setWipResolvedByMonthByTeam({ monthLabels: [], monthKeys: [], rows: [] });
            return;
          }
          
          console.log(`WIP Analysis - Total bugs from API: ${allBugs.length}`);
          
          // Filter to only include bugs that are not closed or removed
          // API already filters for workItemType == 'Bug' and issueSource == 'Production'
          const productionBugs = allBugs.filter(bug => {
            // Double-check workItemType (should already be filtered by API)
            if (bug.workItemType && bug.workItemType !== 'Bug') {
              return false;
            }
            
            // Double-check issueSource (should already be filtered by API)
            const issueSource = (bug.issueSource || '').toLowerCase();
            if (issueSource && issueSource !== 'production' && !issueSource.includes('production')) {
              return false;
            }
            
            // Check state - exclude closed, completed, done, or removed
            const state = (bug.state || '').toLowerCase();
            if (state.includes('closed') || state.includes('completed') || state.includes('done') || state === 'removed') {
              return false;
            }
            
            return true;
          });
          
          console.log(`WIP Analysis - Filtered production bugs (non-closed): ${productionBugs.length}`);
          
          // Calculate age for each bug
          const bugsWithAge = productionBugs.map(bug => {
            const createdDate = bug.createdDate ? new Date(bug.createdDate) : null;
            const age = createdDate && !isNaN(createdDate.getTime()) 
              ? Math.floor((new Date() - createdDate) / (1000 * 60 * 60 * 24))
              : null;
            return { ...bug, age };
          });
          
          setWipData(bugsWithAge);
          console.log(`WIP Analysis - Set ${bugsWithAge.length} bugs to state`);
          
          // Also fetch closed bugs for current sprint (will be used in 2D table)
          // We'll determine current sprint from the data and fetch closed bugs
          const { currentSprint: detectedSprint } = getCurrentAndNextSprints(bugsWithAge);
          if (detectedSprint) {
            // Filter for closed bugs in current sprint
            const closedBugs = allBugs.filter(bug => {
              // Check workItemType and issueSource
              if (bug.workItemType && bug.workItemType !== 'Bug') return false;
              const issueSource = (bug.issueSource || '').toLowerCase();
              if (issueSource && issueSource !== 'production' && !issueSource.includes('production')) {
                return false;
              }
              
              // Check if closed
              const state = (bug.state || '').toLowerCase();
              const isClosed = state.includes('closed') || state.includes('completed') || state.includes('done');
              if (!isClosed) return false;
              
              // Check if in current sprint
              const team = (bug.team || '').trim();
              const isSWAT = team.toLowerCase().includes('swat');
              if (isSWAT) return true; // SWAT team bugs always in current sprint
              
              if (!bug.iterationPath) return false;
              const sprintName = extractSprintName(bug.iterationPath);
              return sprintName === detectedSprint;
            });
            
            // Calculate age for closed bugs
            const closedBugsWithAge = closedBugs.map(bug => {
              const createdDate = bug.createdDate ? new Date(bug.createdDate) : null;
              const age = createdDate && !isNaN(createdDate.getTime()) 
                ? Math.floor((new Date() - createdDate) / (1000 * 60 * 60 * 24))
                : null;
              return { ...bug, age };
            });
            
            setClosedBugsForCurrentSprint(closedBugsWithAge);
            console.log(`WIP Analysis - Found ${closedBugsWithAge.length} closed bugs in current sprint`);
          } else {
            setClosedBugsForCurrentSprint([]);
          }
          
          // Build resolved-by-month-by-team for last 12 months (same window as other metrics)
          const closedProductionBugs = allBugs.filter(bug => {
            if (bug.workItemType && bug.workItemType !== 'Bug') return false;
            const issueSource = (bug.issueSource || '').toLowerCase();
            if (issueSource && issueSource !== 'production' && !issueSource.includes('production')) return false;
            const state = (bug.state || '').toLowerCase();
            if (!state.includes('closed') && !state.includes('completed') && !state.includes('done')) return false;
            const closureDate = bug.closedDate ? new Date(bug.closedDate) : (bug.resolutionDate ? new Date(bug.resolutionDate) : null);
            return closureDate && !isNaN(closureDate.getTime());
          });
          
          const now = new Date();
          const twelveMonthsAgo = new Date(now);
          twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
          const monthLabels = [];
          const monthKeys = [];
          let currentMonth = new Date(twelveMonthsAgo);
          currentMonth.setDate(1);
          
          while (currentMonth <= now) {
            const monthKey = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}`;
            monthKeys.push(monthKey);
            monthLabels.push(currentMonth.toLocaleString('default', { month: 'short', year: 'numeric' }));
            currentMonth.setMonth(currentMonth.getMonth() + 1);
          }
          
          const matrix = new Map(); // team -> Map(monthKey -> count)
          closedProductionBugs.forEach(bug => {
            const closureDate = bug.closedDate ? new Date(bug.closedDate) : (bug.resolutionDate ? new Date(bug.resolutionDate) : null);
            if (!closureDate || isNaN(closureDate.getTime())) return;
            const monthKey = `${closureDate.getFullYear()}-${String(closureDate.getMonth() + 1).padStart(2, '0')}`;
            if (!monthKeys.includes(monthKey)) return;
            const team = bug.team || 'Unknown';
            if (!matrix.has(team)) matrix.set(team, new Map());
            const teamMap = matrix.get(team);
            teamMap.set(monthKey, (teamMap.get(monthKey) || 0) + 1);
          });
          
          const sortedTeams = Array.from(matrix.keys()).sort();
          const rows = sortedTeams.map(team => {
            const teamMap = matrix.get(team);
            const row = { team };
            let total = 0;
            monthKeys.forEach(mk => {
              const count = teamMap.get(mk) || 0;
              row[mk] = count;
              total += count;
            });
            row.total = total;
            return row;
          });
          rows.sort((a, b) => b.total - a.total);
          setWipResolvedByMonthByTeam({ monthLabels, monthKeys, rows });
          
          // Update teams list with teams from data (in addition to API teams)
          const dataTeamNames = [...new Set(bugsWithAge.map(bug => bug.team).filter(Boolean))].sort();
          setWipTeams(prevTeams => {
            const combined = [...new Set([...prevTeams, ...dataTeamNames])].sort();
            return combined;
          });
          
          // Update fix versions list with versions from data (in addition to API versions)
          const dataFixVersions = [...new Set(bugsWithAge.map(bug => bug.fixVersion).filter(Boolean))].sort();
          setWipFixVersions(prevVersions => {
            const combined = [...new Set([...prevVersions, ...dataFixVersions])].sort();
            return combined;
          });
        } catch (err) {
          console.error('Error fetching WIP Analysis data:', err);
          setError('Failed to load WIP Analysis data. Please try again later.');
          setWipData([]);
          setWipResolvedByMonthByTeam({ monthLabels: [], monthKeys: [], rows: [] });
        } finally {
          setWipLoading(false);
        }
      }
    };

    fetchWipData();
  }, [activeTab]);

  // Load persisted A11Y bug analysis when A11Y tab is selected
  useEffect(() => {
    if (activeTab === 7) {
      getA11yBugs()
        .then((data) => {
          if (data.queryUrl) setA11yFilterUrl(data.queryUrl);
          if (data.columns && data.columns.length) setA11yData({ columns: data.columns, rows: data.rows || [] });
          if (data.syncedAt) setA11ySyncedAt(data.syncedAt);
        })
        .catch(() => {});
    }
  }, [activeTab]);

  // Load persisted Eng-TSI rows when Eng-TSI tab is selected
  useEffect(() => {
    if (activeTab === 10) {
      getEngTsiItems()
        .then((data) => {
          if (data.queryUrl) setEngTsiFilterInput(data.queryUrl);
          if (data.columns && data.columns.length) setEngTsiData({ columns: data.columns, rows: data.rows || [] });
          if (data.syncedAt) setEngTsiSyncedAt(data.syncedAt);
        })
        .catch(() => {});
    }
  }, [activeTab]);

  // Fetch fix versions for WIP Analysis tab
  useEffect(() => {
    const fetchWipFixVersions = async () => {
      if (activeTab === 6) { // WIP Analysis tab
        try {
          const response = await getAllFixVersions();
          
          let fixVersions = [];
          if (response && response.data && Array.isArray(response.data)) {
            fixVersions = response.data;
          } else if (response && response.data && response.data.data && Array.isArray(response.data.data)) {
            fixVersions = response.data.data;
          } else if (Array.isArray(response)) {
            fixVersions = response;
          }
          
          // Extract version strings
          const versionStrings = fixVersions.map(fv => fv.version || fv).filter(Boolean);
          setWipFixVersions(versionStrings);
        } catch (err) {
          console.error('Error fetching fix versions for WIP Analysis:', err);
          setWipFixVersions([]);
        }
      }
    };

    fetchWipFixVersions();
  }, [activeTab]);

  // Fetch teams data when WIP Analysis tab is active (using public endpoints: enabled + streams for resolved-by-team mapping)
  useEffect(() => {
    if (activeTab === 6) {
      const fetchTeams = async () => {
        try {
          const [enabledResponse, streamsResponse] = await Promise.all([
            get('/api/admin/teams/enabled'),
            getTeamsWithStreams()
          ]);
          let teams = [];
          if (enabledResponse && enabledResponse.success && enabledResponse.data) {
            teams = enabledResponse.data;
          } else if (Array.isArray(enabledResponse)) {
            teams = enabledResponse;
          }
          const apiTeamNames = teams.map(team => team.id || team.displayName || team).filter(Boolean);
          setWipTeams(apiTeamNames);
          // Team management streams: all teams with id, displayName, stream (for resolved-by-team table)
          const streamsData = Array.isArray(streamsResponse) ? streamsResponse : (streamsResponse?.data || []);
          setTeamsWithStreamsForResolved(streamsData);
        } catch (err) {
          console.error('Error fetching teams for WIP Analysis:', err);
          setWipTeams([]);
          setTeamsWithStreamsForResolved([]);
        }
      };
      fetchTeams();
    }
    
    // Fetch teams data when analysis tab is active
    if (activeTab === 5) {
      const fetchTeams = async () => {
        try {
          // Fetch all teams and filter by enabled property on frontend
          // This ensures we have the enabled field to filter by
          const response = await authGet('/api/admin/teams');
          console.log('=== FULL API RESPONSE FROM /api/admin/teams ===');
          console.log(JSON.stringify(response, null, 2));
          console.log('=== END OF FULL API RESPONSE ===');
          console.log('API response from /api/admin/teams:', response);
          console.log('Response type:', typeof response);
          console.log('Response keys:', Object.keys(response || {}));
          
          // Extract teams from response
          let teams = [];
          if (response && response.success && response.data) {
            teams = response.data;
            console.log('Extracted teams from response.data:', teams);
          } else if (Array.isArray(response)) {
            teams = response;
            console.log('Response is array, using directly:', teams);
          } else {
            console.log('Unexpected response structure:', response);
          }
          
          // Debug: Log first few teams to see structure
          if (teams.length > 0) {
            console.log('Sample team structure (first team):', teams[0]);
            console.log('Sample team enabled value:', teams[0]?.enabled, 'Type:', typeof teams[0]?.enabled);
          }
          
          // Filter to only include teams with enabled === true
          const enabledTeams = teams.filter(team => {
            const isEnabled = team.enabled === true;
            if (!isEnabled && team.id && team.id.includes('Core')) {
              console.log(`Team "${team.id}" has enabled=${team.enabled} (type: ${typeof team.enabled}), filtered out`);
            }
            return isEnabled;
          });
          console.log('Fetched enabled teams (after filtering by enabled === true):', enabledTeams);
          console.log('Number of enabled teams:', enabledTeams.length);
          console.log('Total teams fetched:', teams.length);
          
          // Check if HQ-Core-SQ2 is in the results
          const hqCoreSQ2 = enabledTeams.find(t => t.id === 'HQ-Core-SQ2' || t.id?.includes('Core-SQ2'));
          console.log('HQ-Core-SQ2 in enabled teams?', hqCoreSQ2);
          
          setTeamsData(enabledTeams);
        } catch (err) {
          console.error('Error fetching teams:', err);
          // Fallback to enabled endpoint if auth fails
          try {
            const fallbackResponse = await get('/api/admin/teams/enabled');
            let teams = [];
            if (fallbackResponse && fallbackResponse.success && fallbackResponse.data) {
              teams = fallbackResponse.data;
            } else if (Array.isArray(fallbackResponse)) {
              teams = fallbackResponse;
            }
            console.log('Fallback: Using enabled endpoint, teams:', teams);
            setTeamsData(teams);
          } catch (fallbackErr) {
            console.error('Error with fallback:', fallbackErr);
            setTeamsData([]);
          }
        }
      };
      fetchTeams();
    }
  }, [activeTab]);

  // Load analysis history when Code Analysis tab is active
  const loadAnalysisHistory = async (page = 0) => {
    try {
      setAnalysisHistoryLoading(true);
      const offset = page * analysisHistoryLimit;
      const result = await fetchBugCodeAnalysisHistory({
        limit: analysisHistoryLimit,
        offset
      });
      setAnalysisHistory(result.data);
      setAnalysisHistoryTotal(result.total);
      setAnalysisHistoryPage(page);
    } catch (error) {
      console.error('Error loading analysis history:', error);
    } finally {
      setAnalysisHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 8) { // Code Analysis tab
      loadAnalysisHistory(0);
      try {
        const raw = localStorage.getItem(CODE_ANALYSIS_SCAN_LOG_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed === 'object') setCodeAnalysisScanLog(parsed);
        }
      } catch (e) {
        /* ignore */
      }
    }
  }, [activeTab]);

  // Escalated Issues tab: load persisted rows from DB
  useEffect(() => {
    const loadEscalated = async () => {
      if (activeTab !== 0) return;
      setEscalatedError(null);
      try {
        const rows = await getEscalatedIssues({ limit: 500 });
        setEscalatedRows(Array.isArray(rows) ? rows : []);
      } catch (err) {
        console.error('Escalated Issues: failed to load', err);
        setEscalatedError(err?.message || 'Failed to load escalated issues');
        setEscalatedRows([]);
      }
    };
    loadEscalated();
  }, [activeTab]);

  // All Data tab: full production bugs (all states) for filtered table
  const loadAllDataBugs = useCallback(async () => {
    setAllDataLoading(true);
    setAllDataError(null);
    try {
      const response = await get('/api/workitems/production-bugs/public', { pageSize: 10000 }, 'json', {
        useCache: false
      });
      let raw = [];
      if (response && response.success !== false) {
        if (response.data && Array.isArray(response.data)) raw = response.data;
      } else if (Array.isArray(response)) {
        raw = response;
      }
      if (!Array.isArray(raw)) {
        setAllDataBugs([]);
        return;
      }
      const now = new Date();
      const normalized = raw.map((bug) => {
        const createdDate = bug.createdDate ? new Date(bug.createdDate) : null;
        const closed = bug.closedDate ? new Date(bug.closedDate) : null;
        const age =
          createdDate && !Number.isNaN(createdDate.getTime())
            ? Math.round(
                ((closed && !Number.isNaN(closed.getTime()) ? closed : now) - createdDate) / (1000 * 60 * 60 * 24)
              )
            : null;
        const assignedToDisplay = normalizeProductionBugAssignee(bug.assignedTo);
        const priorityLabel = normalizeProductionBugPriorityLabel(bug.priority);
        const fixVersion = bug.fixVersionDummy || bug.fixVersion || '';
        const team = (bug.team && String(bug.team).trim()) || 'Unknown';
        const iterationPath = bug.iterationPath || '';
        return {
          ...bug,
          age,
          assignedToDisplay,
          priorityLabel,
          fixVersion,
          team,
          iterationPath
        };
      });
      setAllDataBugs(normalized);
      setAllDataPage(0);
    } catch (err) {
      console.error('All Data tab: failed to load production bugs', err);
      setAllDataError(err?.message || 'Failed to load production bugs');
      setAllDataBugs([]);
    } finally {
      setAllDataLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab !== 9) return;
    loadAllDataBugs();
  }, [activeTab, loadAllDataBugs]);

  const handleFetchAllDataCodeFixes = async () => {
    try {
      setAllDataCodeFixLoading(true);
      setAllDataError(null);
      await fetchProductionBugCodeFixes();
      await loadAllDataBugs();
    } catch (err) {
      setAllDataError(err?.message || 'Failed to fetch Code Fix for production bugs');
    } finally {
      setAllDataCodeFixLoading(false);
    }
  };

  useEffect(() => {
    setAllDataPage(0);
  }, [
    allDataFilterState,
    allDataFilterAssignedTo,
    allDataFilterTeam,
    allDataFilterIteration,
    allDataFilterPriority,
    allDataFilterCreatedFrom,
    allDataFilterCreatedTo
  ]);

  const persistCodeAnalysisScanLog = (payload) => {
    setCodeAnalysisScanLog(payload);
    try {
      localStorage.setItem(CODE_ANALYSIS_SCAN_LOG_KEY, JSON.stringify(payload));
    } catch (e) {
      /* ignore */
    }
  };

  // Handle analysis history page change
  const handleAnalysisHistoryPageChange = (event, newPage) => {
    loadAnalysisHistory(newPage);
  };

  // Handle delete analysis record
  const handleDeleteAnalysis = async (id) => {
    try {
      await deleteBugCodeAnalysis(id);
      // Reload current page
      loadAnalysisHistory(analysisHistoryPage);
    } catch (error) {
      console.error('Error deleting analysis:', error);
    }
  };

  // Sync work item from ADO and refresh analysis table state
  const handleSyncFromAdo = async (record) => {
    const adoId = record.adoId;
    try {
      setSyncingAdoId(adoId);
      await syncBugCodeAnalysisFromAdo(adoId);
      loadAnalysisHistory(analysisHistoryPage);
    } catch (err) {
      console.error('Error syncing from ADO:', err);
      setCodeAnalysisError(err.message || 'Failed to sync from ADO');
    } finally {
      setSyncingAdoId(null);
    }
  };

  // Regenerate analysis (run analysis again for this ADO ID)
  const handleRegenerateAnalysis = async (record) => {
    const adoId = record.adoId;
    try {
      setRegeneratingAdoId(adoId);
      setCodeAnalysisError(null);
      const data = await fetchBugCodeAnalysis(adoId);
      if (data.scanLog || data.lastRefreshAt) {
        persistCodeAnalysisScanLog({
          scanLog: data.scanLog,
          lastRefreshAt: data.lastRefreshAt,
          adoId: data.adoId || adoId
        });
      }
      loadAnalysisHistory(analysisHistoryPage);
    } catch (err) {
      console.error('Error regenerating analysis:', err);
      setCodeAnalysisError(err.message || 'Failed to regenerate analysis');
    } finally {
      setRegeneratingAdoId(null);
    }
  };

  // Toggle analysis expansion
  const toggleAnalysisExpansion = async (recordId) => {
    if (expandedAnalysis[recordId]) {
      // Collapse
      setExpandedAnalysis(prev => ({ ...prev, [recordId]: null }));
    } else {
      // Expand - load full analysis if not already loaded
      if (!expandedAnalysis[recordId]) {
        try {
          const { fetchBugCodeAnalysisById } = await import('../../services/apiService');
          const fullAnalysis = await fetchBugCodeAnalysisById(recordId);
          setExpandedAnalysis(prev => ({
            ...prev,
            [recordId]: fullAnalysis.analysisMarkdown
          }));
        } catch (error) {
          console.error('Error loading analysis:', error);
        }
      }
    }
  };

  if (loading) {
    return (
      <Box>
        <LinearProgress />
        <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
          Loading production defects statistics...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!stats) {
    return (
      <Alert severity="info">
        No production defects data available.
      </Alert>
    );
  }

  const total = stats.total || 0;
  const stateBreakdown = stats.byState || [];
  const customerBreakdown = stats.byCustomer || [];
  const customerBreakdownOpen = stats.byCustomerOpen || [];
  const metrics = stats.metrics || {};
  const charts = stats.charts || {};
  const createdVsResolvedData = charts.createdVsResolved || [];
  const resolutionTimeData = charts.resolutionTime || [];
  const dataStartDate = stats.dataStartDate || null;
  const allNonClosedBugs = stats.allNonClosedBugs || [];
  const allSwatPbis = stats.allSwatPbis || [];

  // SLA breakdown (14 days) by state, shared by the top summary card and the SLA table below
  const SLA_DAYS = 14;
  const slaByState = {};
  allNonClosedBugs.forEach((bug) => {
    const state = bug.state || 'Unknown';
    if (!slaByState[state]) {
      slaByState[state] = { withinSla: 0, outsideSla: 0 };
    }
    if (bug.age != null && bug.age > SLA_DAYS) {
      slaByState[state].outsideSla += 1;
    } else {
      slaByState[state].withinSla += 1;
    }
  });
  const slaStates = Object.keys(slaByState).sort();
  const slaTotals = slaStates.reduce(
    (acc, state) => {
      acc.withinSla += slaByState[state].withinSla;
      acc.outsideSla += slaByState[state].outsideSla;
      return acc;
    },
    { withinSla: 0, outsideSla: 0 }
  );

  // Calculate backlog trend (cumulative: created - resolved)
  // Start with current open bugs and work backwards through the months
  const currentOpenBugs = allNonClosedBugs.length || 0;
  const backlogData = [];
  
  // Work backwards from the most recent month
  let runningBacklog = currentOpenBugs;
  for (let i = createdVsResolvedData.length - 1; i >= 0; i--) {
    const item = createdVsResolvedData[i];
    // When going backwards: current backlog - created this month + resolved this month
    // This gives us the backlog at the start of this month
    backlogData.unshift({
      month: item.month,
      backlog: Math.max(0, runningBacklog) // Ensure backlog doesn't go negative
    });
    // Update running backlog for next iteration (going further back)
    runningBacklog = runningBacklog - (item.created || 0) + (item.resolved || 0);
  }
  const filterOptions = stats.filterOptions || { states: [], teams: [] };
  const rcaBreakdown = stats.rcaBreakdown || { allClosed: [], last6Months: [] };
  const closedBugsWithRcaLast6Months = stats.closedBugsWithRcaLast6Months || [];
  const rcaFilterOptions = stats.rcaFilterOptions || { teams: [] };
  
  // Get unique fix versions for filter dropdown
  const uniqueFixVersions = [...new Set(allNonClosedBugs.map(bug => bug.fixVersion).filter(Boolean))].sort();
  
  // Filter non-closed bugs based on selected filters
  const filteredNonClosedBugs = allNonClosedBugs.filter(bug => {
    if (selectedState && bug.state !== selectedState) return false;
    if (selectedTeam && bug.team !== selectedTeam) return false;
    if (selectedFixVersion && bug.fixVersion !== selectedFixVersion) return false;
    return true;
  });
  
  // Handle column sorting
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };
  
  // Sort filtered bugs
  const sortedNonClosedBugs = [...filteredNonClosedBugs].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];
    
    // Handle date fields
    if (sortField === 'createdDate') {
      aValue = aValue ? new Date(aValue).getTime() : 0;
      bValue = bValue ? new Date(bValue).getTime() : 0;
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
    
    // Handle numeric fields
    if (sortField === 'age' || sortField === 'id') {
      aValue = Number(aValue) || 0;
      bValue = Number(bValue) || 0;
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
    
    // Handle priority field (P1, P2, P3, etc.)
    if (sortField === 'priority') {
      // Extract numeric value from priority (e.g., "P1" -> 1, "P2" -> 2)
      const getPriorityValue = (priority) => {
        if (!priority) return 999; // Unknown priorities go last
        const match = String(priority).match(/P?(\d+)/i);
        return match ? parseInt(match[1], 10) : 999;
      };
      aValue = getPriorityValue(aValue);
      bValue = getPriorityValue(bValue);
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }

    if (sortField === 'assignedTo') {
      aValue = normalizeProductionBugAssignee(a.assignedTo);
      bValue = normalizeProductionBugAssignee(b.assignedTo);
    }
    
    // Handle string fields
    aValue = String(aValue || '').toLowerCase();
    bValue = String(bValue || '').toLowerCase();
    
    if (sortDirection === 'asc') {
      return aValue.localeCompare(bValue);
    } else {
      return bValue.localeCompare(aValue);
    }
  });

  // Handle SWAT PBI column sorting
  const handleSortSwat = (field) => {
    if (sortFieldSwat === field) {
      setSortDirectionSwat(sortDirectionSwat === 'asc' ? 'desc' : 'asc');
    } else {
      setSortFieldSwat(field);
      setSortDirectionSwat('asc');
    }
  };

  // Sort SWAT PBIs
  const sortedSwatPbis = [...allSwatPbis].sort((a, b) => {
    let aValue = a[sortFieldSwat];
    let bValue = b[sortFieldSwat];

    if (sortFieldSwat === 'age' || sortFieldSwat === 'id') {
      aValue = Number(aValue) || 0;
      bValue = Number(bValue) || 0;
      return sortDirectionSwat === 'asc' ? aValue - bValue : bValue - aValue;
    }

    if (sortFieldSwat === 'priority') {
      const getPriorityValue = (priority) => {
        if (!priority) return 999;
        const match = String(priority).match(/P?(\d+)/i);
        return match ? parseInt(match[1], 10) : 999;
      };
      aValue = getPriorityValue(aValue);
      bValue = getPriorityValue(bValue);
      return sortDirectionSwat === 'asc' ? aValue - bValue : bValue - aValue;
    }

    aValue = String(aValue || '').toLowerCase();
    bValue = String(bValue || '').toLowerCase();

    return sortDirectionSwat === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
  });

  // Calculate total of Closed, Done, and Removed states (combined as "Closed")
  const closedTotal = stateBreakdown
    .filter(item => {
      const stateLower = (item.state || '').toLowerCase();
      return stateLower.includes('removed') || 
             stateLower.includes('closed') || 
             stateLower.includes('done');
    })
    .reduce((sum, item) => sum + (item.count || 0), 0);

  // Filter out Removed, Closed, and Done states from display and percentage calculation
  // Removed should be treated the same as Closed (not shown separately)
  const filteredStateBreakdown = stateBreakdown.filter(item => {
    const stateLower = (item.state || '').toLowerCase();
    return !stateLower.includes('removed') && 
           !stateLower.includes('closed') && 
           !stateLower.includes('done');
  });

  // Calculate total of "not closed" issues (exclude Removed, Closed, and Done states)
  const notClosedTotal = filteredStateBreakdown
    .reduce((sum, item) => sum + (item.count || 0), 0);

  // Get top 7 states by count (excluding Removed, Closed, and Done)
  const topStates = filteredStateBreakdown.slice(0, 7);
  
  // Get top 15 customers for all issues and open issues
  const topCustomersAll = customerBreakdown.slice(0, 15);
  const topCustomersOpen = customerBreakdownOpen.slice(0, 15);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  /** Re-query ADO for one L1 ticket and reload table (Investigations + linked Bugs). */
  const runEscalatedLookupForTicket = async (
    ticketId,
    { fromRowRefresh = false, suppressRowSpinner = false } = {}
  ) => {
    const id = String(ticketId || '').trim();
    if (!id) return;
    if (fromRowRefresh && !suppressRowSpinner) {
      setEscalatedRefreshingId(id);
    } else if (!fromRowRefresh) {
      setEscalatedLoading(true);
    }
    setEscalatedError(null);
    try {
      console.log('[EscalatedIssues] lookup', { ticketId: id, fromRowRefresh });
      const result = await lookupEscalatedIssue(id);
      console.log('[EscalatedIssues] lookup API response', {
        userEnteredId: result?.userEnteredId,
        investigationCount: result?.investigations?.length ?? 0,
        linkedBugCount: result?.linkedBugs?.length ?? 0
      });
      if (fromRowRefresh && result) {
        setEscalatedRows((prev) => {
          const list = Array.isArray(prev) ? [...prev] : [];
          const idx = list.findIndex((r) => String(r.userEnteredId) === id);
          if (idx >= 0) {
            list[idx] = result;
            return list;
          }
          return [result, ...list];
        });
      } else {
        const rows = await getEscalatedIssues({ limit: 500 });
        let next = Array.isArray(rows) ? [...rows] : [];
        if (result) {
          const rid = String(result.userEnteredId ?? id);
          const idx = next.findIndex((r) => String(r.userEnteredId) === rid);
          if (idx >= 0) {
            next[idx] = result;
          } else {
            next = [result, ...next];
          }
        }
        setEscalatedRows(next);
        setEscalatedTicketInput('');
      }
    } catch (err) {
      console.error('[EscalatedIssues] lookup failed', err);
      setEscalatedError(err?.message || 'Lookup failed');
    } finally {
      if (fromRowRefresh && !suppressRowSpinner) setEscalatedRefreshingId(null);
      else if (!fromRowRefresh) setEscalatedLoading(false);
    }
  };

  /** Re-query ADO for every row that is not Closed (same as each row’s refresh, run sequentially). */
  const runEscalatedRefreshAllActive = async () => {
    const ids = escalatedRows
      .filter((row) => !isEscalatedRowDone(row))
      .map((r) => String(r.userEnteredId).trim())
      .filter(Boolean);
    if (ids.length === 0) return;
    setEscalatedRefreshingAll(true);
    setEscalatedError(null);
    try {
      for (const id of ids) {
        await runEscalatedLookupForTicket(id, { fromRowRefresh: true, suppressRowSpinner: true });
      }
    } finally {
      setEscalatedRefreshingAll(false);
    }
  };

  // Generate colors for pie charts
  const generatePieColors = (count) => {
    const colors = [
      '#1A2E22', '#2C4A3A', '#4caf50', '#2196f3', '#ff9800',
      '#f44336', '#9c27b0', '#00bcd4', '#ffeb3b', '#795548',
      '#607d8b', '#e91e63', '#009688', '#3f51b5', '#ff5722'
    ];
    return colors.slice(0, count);
  };

  // Generate graded colors for heat blocks based on value (green for customers)
  const getGradedColor = (value, maxValue) => {
    if (maxValue === 0) return '#e0e0e0';
    const ratio = value / maxValue;
    // Use a gradient from light green to dark green
    // For higher values, use darker green; for lower values, use lighter green
    if (ratio >= 0.8) return '#1B5E20'; // Dark green
    if (ratio >= 0.6) return '#2E7D32'; // Medium-dark green
    if (ratio >= 0.4) return '#388E3C'; // Medium green
    if (ratio >= 0.2) return '#4CAF50'; // Light-medium green
    return '#81C784'; // Light green
  };

  // Generate graded colors for RCA heat blocks (orange/amber)
  const getGradedColorRca = (value, maxValue) => {
    if (maxValue === 0) return '#e0e0e0';
    const ratio = value / maxValue;
    // Use a gradient from light orange/amber to dark orange/amber
    // For higher values, use darker orange; for lower values, use lighter orange/amber
    if (ratio >= 0.8) return '#E65100'; // Dark orange
    if (ratio >= 0.6) return '#F57C00'; // Medium-dark orange
    if (ratio >= 0.4) return '#FF9800'; // Medium orange
    if (ratio >= 0.2) return '#FFB74D'; // Light-medium orange/amber
    return '#FFE0B2'; // Light amber
  };

  // Heat Map Component - Grid of colored blocks
  const HeatMap = ({ data, title, useRcaColors = false }) => {
    if (!data || data.length === 0) {
      return (
        <Alert severity="info">
          No data available.
        </Alert>
      );
    }

    const maxValue = Math.max(...data.map(item => item.count));
    const total = data.reduce((sum, item) => sum + item.count, 0);
    
    // Sort data to identify top item
    const sortedData = [...data].sort((a, b) => b.count - a.count);
    const topValue = sortedData.length > 0 ? sortedData[0].count : 0;

    return (
      <Box>
        <Grid container spacing={2}>
          {data.map((item, index) => {
            const percentage = total > 0 ? ((item.count / total) * 100).toFixed(1) : 0;
            const color = useRcaColors 
              ? getGradedColorRca(item.count, maxValue)
              : getGradedColor(item.count, maxValue);
            
            // For RCA: use black text for all items
            // For Customer: use white text only for top item, black for others
            const isTopItem = item.count === topValue && !useRcaColors;
            const textColor = useRcaColors ? '#000000' : (isTopItem ? 'white' : '#000000');
            const textShadow = useRcaColors ? 'none' : (isTopItem ? '0 1px 2px rgba(0,0,0,0.3)' : 'none');
            const numberTextShadow = useRcaColors ? 'none' : (isTopItem ? '0 2px 4px rgba(0,0,0,0.4)' : 'none');
            
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: color,
                    borderRadius: 2,
                    minHeight: 120,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    transition: 'all 0.3s ease',
                    boxShadow: 2,
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: textColor,
                      fontWeight: 600,
                      fontSize: '0.95rem',
                      mb: 1,
                      textShadow: textShadow,
                      lineHeight: 1.3,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical'
                    }}
                  >
                    {item.customer || item.rca || 'Unknown/Blank'}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                    <Typography
                      variant="h4"
                      sx={{
                        color: textColor,
                        fontWeight: 'bold',
                        fontSize: '2rem',
                        textShadow: numberTextShadow,
                        lineHeight: 1
                      }}
                    >
                      {item.count}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: textColor,
                        fontWeight: 600,
                        fontSize: '1.1rem',
                        textShadow: textShadow
                      }}
                    >
                      {percentage}%
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  };

  // Prepare chart data for all issues
  const allIssuesChartData = topCustomersAll.length > 0 ? {
    labels: topCustomersAll.map(item => item.customer || 'Unknown'),
    datasets: [
      {
        label: 'All Issues',
        data: topCustomersAll.map(item => item.count),
        backgroundColor: generatePieColors(topCustomersAll.length),
        borderColor: '#fff',
        borderWidth: 2,
      },
    ],
  } : null;

  // Prepare chart data for open issues
  const openIssuesChartData = topCustomersOpen.length > 0 ? {
    labels: topCustomersOpen.map(item => item.customer || 'Unknown'),
    datasets: [
      {
        label: 'Open Issues',
        data: topCustomersOpen.map(item => item.count),
        backgroundColor: generatePieColors(topCustomersOpen.length),
        borderColor: '#fff',
        borderWidth: 2,
      },
    ],
  } : null;

  // Chart options for pie charts
  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          boxWidth: 15,
          padding: 12,
          generateLabels: (chart) => {
            const data = chart.data;
            if (data.labels.length && data.datasets.length) {
              return data.labels.map((label, i) => {
                const value = data.datasets[0].data[i];
                const backgroundColor = data.datasets[0].backgroundColor[i];
                const total = data.datasets[0].data.reduce((a, b) => a + b, 0);
                const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
                
                return {
                  text: `${label} (${value} - ${percentage}%)`,
                  fillStyle: backgroundColor,
                  strokeStyle: backgroundColor,
                  lineWidth: 1,
                  hidden: false,
                  index: i
                };
              });
            }
            return [];
          }
        }
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.label || '';
            const value = context.raw || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
            return `${label}: ${value} (${percentage}%)`;
          }
        }
      }
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
          Production Defects Overview
        </Typography>
        {dataStartDate && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Data from {new Date(dataStartDate).toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </Typography>
        )}
      </Box>

      {/* Tabs */}
      <Paper elevation={1} sx={{ mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          aria-label="production defects tabs"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
                  {/* Explicit `value` props keep tab → content (activeTab === N) mapping stable
                      even though the A11Y Bug Analysis (7) and Code Analysis (8) tabs are removed. */}
                  <Tab label="Escalated Issues" value={0} />
                  <Tab label="Overview" value={1} />
                  <Tab label="By Customer" value={2} />
                  <Tab label="By RCA" value={3} />
                  <Tab label="By Environment" value={4} />
                  <Tab label="All Data" value={9} />
                  <Tab label="Eng-TSI" value={10} />
                </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 1 && (
        <>
      {/* Combined Total and State Breakdown - Infographic Cards in Top Bar */}
      <Paper 
        elevation={2} 
        sx={{ 
          p: 2, 
          mb: 3,
          backgroundColor: 'background.paper'
        }}
      >
        <Box 
          sx={{ 
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            flexWrap: { xs: 'nowrap', md: 'nowrap' }
          }}
        >
          {/* Total Count Card */}
          <Paper 
            elevation={1} 
            sx={{ 
              p: 2, 
              textAlign: 'center',
              background: 'linear-gradient(135deg, #1A2E22 0%, #2C4A3A 100%)',
              color: 'white',
              flex: { xs: '1 1 100%', md: '1.2 1 0' },
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}
          >
            <Typography variant="body2" sx={{ mb: 0.5, opacity: 0.9 }}>
              Total Production Issues
            </Typography>
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 'bold',
                mb: 0.5
              }}
            >
              {total.toLocaleString()}
            </Typography>
          </Paper>

          {/* Closed Card (Combined: Closed + Done + Removed) */}
          {closedTotal > 0 && (
            <Paper 
              elevation={1} 
              sx={{ 
                p: 2, 
                textAlign: 'center',
                borderLeft: '4px solid #4caf50',
                backgroundColor: 'background.default',
                flex: { xs: '1 1 100%', md: '1 1 0' },
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}
            >
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 'bold', 
                  color: '#4caf50',
                  mb: 0.5
                }}
              >
                {closedTotal.toLocaleString()}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
              >
                Closed
              </Typography>
            </Paper>
          )}

          {/* Bugs Outside SLA Card */}
          {allNonClosedBugs.length > 0 && (
            <Paper
              elevation={1}
              sx={{
                p: 2,
                textAlign: 'center',
                borderLeft: '4px solid #d32f2f',
                backgroundColor: 'background.default',
                flex: { xs: '1 1 100%', md: '1 1 0' },
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 'bold',
                  color: '#d32f2f',
                  mb: 0.5
                }}
              >
                {slaTotals.outsideSla.toLocaleString()}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
              >
                Outside SLA
              </Typography>
            </Paper>
          )}

          {/* State Breakdown Cards */}
          {topStates.map((item, index) => {
            // Calculate percentage based on "not closed" issues, not all issues
            const percentage = notClosedTotal > 0 ? (item.count / notClosedTotal) * 100 : 0;
            const color = getStateColor(item.state);
            
            return (
              <Paper 
                key={index}
                elevation={1} 
                sx={{ 
                  p: 2, 
                  textAlign: 'center',
                  borderLeft: `4px solid ${color}`,
                  backgroundColor: 'background.default',
                  flex: { xs: '1 1 100%', md: '1 1 0' },
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}
              >
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 'bold', 
                    color: color,
                    mb: 0.5
                  }}
                >
                  {item.count.toLocaleString()}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  {item.state}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: color,
                    fontWeight: 500
                  }}
                >
                  {percentage.toFixed(1)}%
                </Typography>
              </Paper>
            );
          })}
        </Box>
      </Paper>

      {stateBreakdown.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No state breakdown data available.
        </Alert>
      )}

      {/* Charts Section */}
      {charts && (createdVsResolvedData.length > 0 || resolutionTimeData.length > 0) && (
        <Grid container spacing={3} sx={{ mt: 3 }}>
          {/* Metrics column - Avg Bugs/Week + Avg Closure Time */}
          {metrics && (
            <Grid item xs={12} md={2}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, height: '100%' }}>
                <Paper
                  elevation={1}
                  variant="outlined"
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1, position: 'relative' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                      Avg Bugs/Week
                    </Typography>
                    <Tooltip
                      title="Average number of production bugs created and closed per week over the last 3 months."
                      arrow
                      placement="top"
                    >
                      <IconButton size="small" sx={{ p: 0.25, position: 'absolute', right: 0 }}>
                        <InfoIcon fontSize="small" color="action" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 'bold',
                          color: 'primary.main'
                        }}
                      >
                        {metrics.avgIncomingPerWeek?.toFixed(1) || '0.0'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        Incoming
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 'bold',
                          color: 'success.main'
                        }}
                      >
                        {metrics.avgClosedPerWeek?.toFixed(1) || '0.0'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        Closed
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', mt: 1 }}>
                    {metrics.period || '3 months'}
                  </Typography>
                </Paper>

                <Paper
                  elevation={1}
                  variant="outlined"
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1, position: 'relative' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                      Avg Closure Time
                    </Typography>
                    <Tooltip
                      title="Average time (in days) from creation to closure for P1, P2, and P3 production bugs over the last 3 months (color-coded: red = P1, orange = P2, blue = P3). Lower values indicate faster resolution times."
                      arrow
                      placement="top"
                    >
                      <IconButton size="small" sx={{ p: 0.25, position: 'absolute', right: 0 }}>
                        <InfoIcon fontSize="small" color="action" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
                    {['P1', 'P2', 'P3'].map((priority) => {
                      const days = metrics.avgClosureTime?.[priority];
                      const color = priority === 'P1' ? '#c62828' : priority === 'P2' ? '#e65100' : '#1565c0';

                      return (
                        <Typography
                          key={priority}
                          variant="h4"
                          sx={{ fontWeight: 'bold', color }}
                        >
                          {days !== null && days !== undefined ? `${days}d` : 'N/A'}
                        </Typography>
                      );
                    })}
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', mt: 1 }}>
                    {metrics.period || '3 months'}
                  </Typography>
                </Paper>
              </Box>
            </Grid>
          )}

          {/* Created vs Resolved Chart */}
          {createdVsResolvedData.length > 0 && (
            <Grid item xs={12} md={metrics ? 5 : 6}>
              <Paper elevation={2} sx={{ p: 3 }} data-testid="production-bugs-created-resolved-chart">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Created vs Resolved
                  </Typography>
                  <Tooltip 
                    title="Monthly comparison of production bugs created versus resolved over the last 12 months. This chart helps visualize the balance between incoming issues and resolution capacity."
                    arrow
                    placement="top"
                  >
                    <IconButton size="small">
                      <InfoIcon fontSize="small" color="action" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={{ height: 300 }}>
                  <Bar
                    data={{
                      labels: createdVsResolvedData.map(item => item.month),
                      datasets: [
                        {
                          label: 'Created',
                          data: createdVsResolvedData.map(item => item.created),
                          backgroundColor: '#f44336',
                          borderColor: '#d32f2f',
                          borderWidth: 1,
                          order: 1
                        },
                        {
                          label: 'Resolved',
                          data: createdVsResolvedData.map(item => item.resolved),
                          backgroundColor: '#4caf50',
                          borderColor: '#388e3c',
                          borderWidth: 1,
                          order: 1
                        },
                        {
                          label: 'Backlog',
                          type: 'line',
                          data: backlogData.map(item => item.backlog),
                          borderColor: '#2196f3',
                          backgroundColor: 'transparent',
                          borderWidth: 4,
                          pointRadius: 6,
                          pointHoverRadius: 8,
                          pointBackgroundColor: '#2196f3',
                          pointBorderColor: '#fff',
                          pointBorderWidth: 2,
                          fill: false,
                          tension: 0.4,
                          yAxisID: 'y1',
                          order: 2
                        }
                      ]
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      interaction: {
                        mode: 'index',
                        intersect: false
                      },
                      plugins: {
                        legend: {
                          position: 'bottom',
                        },
                        tooltip: {
                          mode: 'index',
                          intersect: false
                        },
                        datalabels: {
                          anchor: 'end',
                          align: 'top',
                          formatter: (value, context) => {
                            // Only show labels for bar charts, not the line
                            if (context.dataset.type === 'line') {
                              return '';
                            }
                            return value;
                          },
                          color: '#000',
                          font: {
                            weight: 'bold',
                            size: 11
                          },
                          clip: false
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: {
                            stepSize: 1
                          },
                          suggestedMax: (() => {
                            const maxValue = Math.max(
                              ...createdVsResolvedData.map(item => Math.max(item.created || 0, item.resolved || 0))
                            );
                            // Add 20% padding to ensure labels are visible above bars
                            return Math.ceil(maxValue * 1.2);
                          })()
                        },
                        y1: {
                          type: 'linear',
                          display: true,
                          position: 'right',
                          beginAtZero: true,
                          grid: {
                            drawOnChartArea: false
                          },
                          title: {
                            display: true,
                            text: 'Backlog',
                            color: '#2196f3',
                            font: {
                              weight: 'bold'
                            }
                          },
                          ticks: {
                            color: '#2196f3',
                            stepSize: (() => {
                              const maxBacklog = Math.max(...backlogData.map(item => item.backlog || 0));
                              if (maxBacklog <= 10) return 2;
                              if (maxBacklog <= 50) return 5;
                              if (maxBacklog <= 100) return 10;
                              return 20;
                            })()
                          },
                          suggestedMax: (() => {
                            const maxBacklog = Math.max(...backlogData.map(item => item.backlog || 0));
                            // Round up to nearest nice number for better scale
                            if (maxBacklog === 0) return 10;
                            if (maxBacklog <= 10) return 10;
                            if (maxBacklog <= 20) return 20;
                            if (maxBacklog <= 50) return Math.ceil(maxBacklog / 10) * 10;
                            if (maxBacklog <= 100) return Math.ceil(maxBacklog / 20) * 20;
                            return Math.ceil(maxBacklog / 50) * 50;
                          })(),
                          suggestedMin: 0
                        }
                      },
                      layout: {
                        padding: {
                          top: 20
                        }
                      }
                    }}
                  />
                </Box>
              </Paper>
            </Grid>
          )}

          {/* Resolution Time Chart */}
          {resolutionTimeData.length > 0 && (
            <Grid item xs={12} md={metrics ? 5 : 6}>
              <Paper elevation={2} sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Average Resolution Time
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip title="View / export the bugs behind any month (click a bar to open it directly)" arrow placement="top">
                      <IconButton
                        size="small"
                        aria-label="View or export resolution time details"
                        onClick={() => {
                          const latest = resolutionTimeData[resolutionTimeData.length - 1];
                          setResolutionDetailsMonthKey(latest?.monthKey || '');
                          setResolutionDetailsOpen(true);
                        }}
                      >
                        <DownloadIcon fontSize="small" color="action" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip
                      title="Average resolution time (in days) for production bugs closed each month over the last 12 months. Lower values indicate faster resolution times."
                      arrow
                      placement="top"
                    >
                      <IconButton size="small">
                        <InfoIcon fontSize="small" color="action" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                <Box sx={{ height: 300 }}>
                  <Bar
                    data={{
                      labels: resolutionTimeData.map(item => item.month),
                      datasets: [
                        {
                          label: 'Avg Resolution Time (days)',
                          data: resolutionTimeData.map(item => item.avgResolutionDays),
                          backgroundColor: '#2196f3',
                          borderColor: '#1976d2',
                          borderWidth: 1
                        }
                      ]
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      onClick: (event, elements) => {
                        const monthKey = elements.length > 0 ? resolutionTimeData[elements[0].index]?.monthKey : null;
                        if (!monthKey) return;
                        setResolutionDetailsMonthKey(monthKey);
                        setResolutionDetailsOpen(true);
                      },
                      onHover: (event, elements) => {
                        const target = event.native?.target;
                        if (target) target.style.cursor = elements.length > 0 ? 'pointer' : 'default';
                      },
                      plugins: {
                        legend: {
                          position: 'bottom',
                        },
                        tooltip: {
                          callbacks: {
                            label: (context) => {
                              const count = resolutionTimeData[context.dataIndex]?.count || 0;
                              return `${context.parsed.y} days (${count} bugs)`;
                            }
                          }
                        },
                        datalabels: {
                          anchor: 'end',
                          align: 'top',
                          formatter: (value) => {
                            return value > 0 ? `${value}` : '';
                          },
                          color: '#000',
                          font: {
                            weight: 'bold',
                            size: 11
                          },
                          clip: false
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          suggestedMax: (() => {
                            const maxValue = Math.max(
                              ...resolutionTimeData.map(item => item.avgResolutionDays || 0)
                            );
                            // Add 20% padding to ensure labels are visible above bars
                            return maxValue > 0 ? Math.ceil(maxValue * 1.2) : undefined;
                          })()
                        }
                      },
                      layout: {
                        padding: {
                          top: 20
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Days'
                          },
                          ticks: {
                            stepSize: 1
                          }
                        }
                      }
                    }}
                  />
                </Box>
              </Paper>
            </Grid>
          )}

          <ResolutionTimeDetailsDialog
            open={resolutionDetailsOpen}
            onClose={() => setResolutionDetailsOpen(false)}
            months={resolutionTimeData.filter((item) => item.monthKey)}
            initialMonthKey={resolutionDetailsMonthKey}
            getAdoWorkItemUrl={getAdoWorkItemUrl}
          />

          {/* Incoming Trends - Bugs Dialog */}
          <Dialog
            open={incomingDayDialogOpen}
            onClose={closeIncomingDayDialog}
            maxWidth="lg"
            fullWidth
          >
            <DialogTitle>
              {incomingDayDialogPriority ? `${incomingDayDialogPriority} bugs created on ` : 'Bugs created on '}
              {incomingDayDialogLabel}
              {incomingDayDialogDateKey ? ` (${incomingDayDialogDateKey})` : ''}
            </DialogTitle>
            <DialogContent dividers>
              {incomingDayDialogBugs.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No bugs found for this day.
                </Typography>
              ) : (
                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>ADO ID</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Priority</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Team</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Environment</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>State</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {incomingDayDialogBugs.map((bug) => {
                        const adoUrl = getAdoWorkItemUrl(bug.id);
                        return (
                          <TableRow key={bug.id} hover>
                            <TableCell>
                              <Link href={adoUrl} target="_blank" rel="noopener noreferrer">
                                {bug.id}
                              </Link>
                            </TableCell>
                            <TableCell>{bug.priority || ''}</TableCell>
                            <TableCell sx={{ maxWidth: 520 }}>
                              <Typography variant="body2" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {bug.title || ''}
                              </Typography>
                            </TableCell>
                            <TableCell>{bug.team || ''}</TableCell>
                            <TableCell>{bug.environment || 'Unknown'}</TableCell>
                            <TableCell>{bug.state || ''}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={closeIncomingDayDialog} variant="contained">
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* Production Defects by Fix Version Chart */}
          <Grid item xs={12} md={6}>
            <ProductionBugsByFixVersionChart />
          </Grid>

          {/* Production Defects by Component Chart */}
          <Grid item xs={12} md={6}>
            <ProductionBugsByComponentChart />
          </Grid>
        </Grid>
      )}

      {/* Incoming Trends */}
      {stats && allNonClosedBugs.length > 0 && (
        <Grid container spacing={3} sx={{ mt: 3 }}>
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Incoming trends
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Bugs created per day (last 15 days), split by priority
                  </Typography>
                </Box>
                <Tooltip
                  title="Daily incoming production bugs over the last 15 days, shown separately for P1–P4."
                  arrow
                  placement="top"
                >
                  <IconButton size="small">
                    <InfoIcon fontSize="small" color="action" />
                  </IconButton>
                </Tooltip>
              </Box>

              {incomingTrendsLoading && <LinearProgress sx={{ mb: 2 }} />}
              {incomingTrendsError && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {incomingTrendsError}
                </Alert>
              )}

              {!incomingTrendsLoading && !incomingTrendsError && incomingTrends.labels.length > 0 && (
                <Grid container spacing={2}>
                  {allNonClosedBugs.length > 0 && (
                    <Grid item xs={12} md={4}>
                      <Paper elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            Non-Closed Bugs by State vs SLA
                          </Typography>
                          <Tooltip
                            title="Count of non-closed bugs by state, split by whether their age is within or outside the 14-day SLA."
                            arrow
                            placement="top"
                          >
                            <IconButton size="small">
                              <InfoIcon fontSize="small" color="action" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>State</TableCell>
                                <TableCell sx={{ fontWeight: 600 }} align="right">In SLA</TableCell>
                                <TableCell sx={{ fontWeight: 600 }} align="right">Out</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {slaStates.map((state) => {
                                const { withinSla, outsideSla } = slaByState[state];
                                return (
                                  <TableRow key={state} hover>
                                    <TableCell>{state}</TableCell>
                                    <TableCell align="right">{withinSla}</TableCell>
                                    <TableCell align="right">{outsideSla}</TableCell>
                                  </TableRow>
                                );
                              })}
                              <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                                <TableCell sx={{ fontWeight: 700 }} align="right">{slaTotals.withinSla}</TableCell>
                                <TableCell sx={{ fontWeight: 700 }} align="right">{slaTotals.outsideSla}</TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Paper>
                    </Grid>
                  )}
                  {[
                    { key: 'P1P2', priorities: ['P1', 'P2'], label: 'P1 / P2' },
                    { key: 'P3P4', priorities: ['P3', 'P4'], label: 'P3 / P4' }
                  ].map(({ key, priorities, label }) => {
                    const priorityColors = {
                      P1: '#c62828',
                      P2: '#ef6c00',
                      P3: '#1565c0',
                      P4: '#6a1b9a'
                    };

                    const datasets = priorities.map((priority) => {
                      const envs = incomingTrends.environments || [];
                      const countsByEnv = incomingTrends.countsByPriorityAndEnv?.[priority] || {};
                      const data = incomingTrends.dateKeys.map((_, i) =>
                        envs.reduce((sum, env) => sum + (countsByEnv[env]?.[i] || 0), 0)
                      );
                      return {
                        label: priority,
                        data,
                        backgroundColor: priorityColors[priority],
                        borderColor: priorityColors[priority],
                        borderWidth: 1
                      };
                    });

                    // Suggested max based on total stack height per day
                    const totals = incomingTrends.dateKeys.map((_, i) =>
                      datasets.reduce((sum, ds) => sum + (ds.data?.[i] || 0), 0)
                    );
                    const maxTotal = totals.length > 0 ? Math.max(...totals) : 0;
                    const suggestedMax = maxTotal > 0 ? Math.ceil(maxTotal * 1.2) : 5;

                    return (
                      <Grid key={key} item xs={12} md={4}>
                        <Paper elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                            {label}
                          </Typography>
                          <Box sx={{ height: 340 }}>
                            <Bar
                              ref={(chart) => {
                                incomingTrendsChartRefs.current[key] = chart;
                              }}
                              onClick={handleIncomingBarClick(key, priorities)}
                              data={{
                                labels: incomingTrends.labels,
                                datasets
                              }}
                              options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                  legend: {
                                    position: 'bottom',
                                    labels: {
                                      boxWidth: 10,
                                      boxHeight: 10,
                                      padding: 10,
                                      font: { size: 9 }
                                    }
                                  },
                                  tooltip: {
                                    callbacks: {
                                      label: (context) => `${context.dataset.label}: ${context.parsed.y}`,
                                      footer: (items) => {
                                        const total = items.reduce((sum, it) => sum + (it.parsed?.y || 0), 0);
                                        return `Total: ${total}`;
                                      }
                                    }
                                  },
                                  datalabels: {
                                    display: false
                                  }
                                },
                                scales: {
                                  x: {
                                    stacked: true,
                                    ticks: {
                                      maxRotation: 0,
                                      autoSkip: true,
                                      maxTicksLimit: 8
                                    }
                                  },
                                  y: {
                                    stacked: true,
                                    beginAtZero: true,
                                    suggestedMax,
                                    ticks: { stepSize: 1 }
                                  }
                                }
                              }}
                            />
                          </Box>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* All Non-Closed Bugs Table with Filters */}
      {stats && allNonClosedBugs.length > 0 && (
        <Grid container spacing={3} sx={{ mt: 3 }}>
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  All Non-Closed Bugs
                </Typography>
                <Tooltip 
                  title="Complete list of all non-closed production bugs. Use filters to narrow down by State, Team, and Fix Version."
                  arrow
                  placement="top"
                >
                  <IconButton size="small">
                    <InfoIcon fontSize="small" color="action" />
                  </IconButton>
                </Tooltip>
              </Box>
              
              {/* Filters */}
              <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>State</InputLabel>
                  <Select
                    value={selectedState}
                    label="State"
                    onChange={(e) => setSelectedState(e.target.value)}
                  >
                    <MenuItem value="">All States</MenuItem>
                    {filterOptions.states.map((state) => (
                      <MenuItem key={state} value={state}>
                        {state}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Team</InputLabel>
                  <Select
                    value={selectedTeam}
                    label="Team"
                    onChange={(e) => setSelectedTeam(e.target.value)}
                  >
                    <MenuItem value="">All Teams</MenuItem>
                    {filterOptions.teams.map((team) => (
                      <MenuItem key={team} value={team}>
                        {team}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Fix Version</InputLabel>
                  <Select
                    value={selectedFixVersion}
                    label="Fix Version"
                    onChange={(e) => setSelectedFixVersion(e.target.value)}
                  >
                    <MenuItem value="">All Fix Versions</MenuItem>
                    {uniqueFixVersions.map((fixVersion) => (
                      <MenuItem key={fixVersion} value={fixVersion}>
                        {fixVersion}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
                  <Typography variant="body2" color="text.secondary">
                    Showing {filteredNonClosedBugs.length} of {allNonClosedBugs.length} bugs
                  </Typography>
                </Box>
              </Box>
              
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell 
                        sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('id')}
                      >
                        <Box display="flex" alignItems="center">
                          ID
                          {sortField === 'id' && (
                            sortDirection === 'asc' ? 
                              <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                              <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell 
                        sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('title')}
                      >
                        <Box display="flex" alignItems="center">
                          Title
                          {sortField === 'title' && (
                            sortDirection === 'asc' ? 
                              <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                              <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell 
                        sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('state')}
                      >
                        <Box display="flex" alignItems="center">
                          State
                          {sortField === 'state' && (
                            sortDirection === 'asc' ? 
                              <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                              <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell 
                        sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('priority')}
                      >
                        <Box display="flex" alignItems="center">
                          Priority
                          {sortField === 'priority' && (
                            sortDirection === 'asc' ? 
                              <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                              <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell 
                        sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('team')}
                      >
                        <Box display="flex" alignItems="center">
                          Team
                          {sortField === 'team' && (
                            sortDirection === 'asc' ? 
                              <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                              <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell 
                        sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('fixVersion')}
                      >
                        <Box display="flex" alignItems="center">
                          Fix Version
                          {sortField === 'fixVersion' && (
                            sortDirection === 'asc' ? 
                              <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                              <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell 
                        sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('iterationPath')}
                      >
                        <Box display="flex" alignItems="center">
                          Iteration Path
                          {sortField === 'iterationPath' && (
                            sortDirection === 'asc' ? 
                              <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                              <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell 
                        sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('customer')}
                      >
                        <Box display="flex" alignItems="center">
                          Customer
                          {sortField === 'customer' && (
                            sortDirection === 'asc' ? 
                              <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                              <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell 
                        sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('assignedTo')}
                      >
                        <Box display="flex" alignItems="center">
                          Assigned to
                          {sortField === 'assignedTo' && (
                            sortDirection === 'asc' ? 
                              <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                              <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell 
                        sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                        align="right"
                        onClick={() => handleSort('age')}
                      >
                        <Box display="flex" alignItems="center" justifyContent="flex-end">
                          Age (days)
                          {sortField === 'age' && (
                            sortDirection === 'asc' ? 
                              <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                              <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedNonClosedBugs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No bugs found matching the selected filters.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortedNonClosedBugs.map((bug) => {
                        // Generate ADO URL
                        const adoOrg = process.env.REACT_APP_AZURE_DEVOPS_ORG || 'tr-legal.visualstudio.com';
                        const projectName = process.env.REACT_APP_AZURE_DEVOPS_PROJECT || 'HighQ Tech';
                        const baseUrl = adoOrg.includes('visualstudio.com') 
                          ? `https://${adoOrg}` 
                          : `https://dev.azure.com/${adoOrg}`;
                        const adoUrl = `${baseUrl}/${encodeURIComponent(projectName)}/_workitems/edit/${bug.id}`;
                        
                        // Get age color
                        const getAgeColor = (age) => {
                          if (age == null) return 'default';
                          if (age > 180) return '#d32f2f'; // Red
                          if (age > 90) return '#f57c00'; // Orange
                          if (age > 30) return '#fbc02d'; // Yellow
                          return '#388e3c'; // Green
                        };
                        
                        const ageColor = getAgeColor(bug.age);

                        // SLA: a non-closed bug older than 14 days breaches SLA (light red row);
                        // within SLA → light green. Keep cell text black for readability.
                        const slaBreached = bug.age != null && bug.age > 14;
                        const rowBg = slaBreached ? '#fdecea' : '#edf7ed';
                        const rowBgHover = slaBreached ? '#fadbd6' : '#dcefdc';

                        return (
                          <TableRow
                            key={bug.id}
                            hover
                            sx={{
                              // Paint the cells (not the row) so the theme's global
                              // nth-of-type(even) row stripe can't override the SLA color.
                              '& .MuiTableCell-root': { backgroundColor: rowBg, color: '#000' },
                              '&:hover .MuiTableCell-root': { backgroundColor: rowBgHover },
                            }}
                          >
                            <TableCell>
                              <Link
                                href={adoUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="hover"
                                sx={{ 
                                  fontWeight: 'medium',
                                  color: 'primary.main'
                                }}
                              >
                                #{bug.id}
                              </Link>
                            </TableCell>
                            <TableCell sx={{ maxWidth: 400, wordWrap: 'break-word' }}>
                              <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                {bug.title || 'Untitled'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={bug.state || 'Unknown'}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={bug.priority || 'Unknown'}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {bug.team || 'Unknown'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {bug.fixVersion || 'Not Specified'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {bug.iterationPath ? bug.iterationPath.split('\\').pop() : 'Not Specified'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {bug.customer || 'Unknown'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {normalizeProductionBugAssignee(bug.assignedTo)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip
                                title={
                                  bug.createdDate
                                    ? new Date(bug.createdDate).toLocaleString('en-US', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        hour12: true
                                      })
                                    : 'Created date unknown'
                                }
                              >
                                <Box component="span" sx={{ display: 'inline-flex' }}>
                                  {bug.age != null ? (
                                    <Chip
                                      label={`${bug.age} days`}
                                      size="small"
                                      sx={{
                                        backgroundColor: ageColor,
                                        color: 'white',
                                        fontWeight: 'bold'
                                      }}
                                    />
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">-</Typography>
                                  )}
                                </Box>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* SWAT PBI Table */}
      {stats && allSwatPbis.length > 0 && (
        <Grid container spacing={3} sx={{ mt: 3 }}>
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  SWAT PBI
                </Typography>
                <Tooltip
                  title="All non-closed Product Backlog Items allocated to Area Path 'HighQ Tech\SWAT'."
                  arrow
                  placement="top"
                >
                  <IconButton size="small">
                    <InfoIcon fontSize="small" color="action" />
                  </IconButton>
                </Tooltip>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {sortedSwatPbis.length} non-closed PBI{sortedSwatPbis.length !== 1 ? 's' : ''}
                </Typography>
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSortSwat('id')}
                      >
                        <Box display="flex" alignItems="center">
                          ID
                          {sortFieldSwat === 'id' && (
                            sortDirectionSwat === 'asc' ?
                              <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> :
                              <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSortSwat('title')}
                      >
                        <Box display="flex" alignItems="center">
                          Title
                          {sortFieldSwat === 'title' && (
                            sortDirectionSwat === 'asc' ?
                              <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> :
                              <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSortSwat('state')}
                      >
                        <Box display="flex" alignItems="center">
                          State
                          {sortFieldSwat === 'state' && (
                            sortDirectionSwat === 'asc' ?
                              <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> :
                              <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSortSwat('priority')}
                      >
                        <Box display="flex" alignItems="center">
                          Priority
                          {sortFieldSwat === 'priority' && (
                            sortDirectionSwat === 'asc' ?
                              <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> :
                              <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSortSwat('assignedTo')}
                      >
                        <Box display="flex" alignItems="center">
                          Assigned to
                          {sortFieldSwat === 'assignedTo' && (
                            sortDirectionSwat === 'asc' ?
                              <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> :
                              <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                        align="right"
                        onClick={() => handleSortSwat('age')}
                      >
                        <Box display="flex" alignItems="center" justifyContent="flex-end">
                          Age (days)
                          {sortFieldSwat === 'age' && (
                            sortDirectionSwat === 'asc' ?
                              <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> :
                              <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedSwatPbis.map((pbi) => {
                      const adoOrg = process.env.REACT_APP_AZURE_DEVOPS_ORG || 'tr-legal.visualstudio.com';
                      const projectName = process.env.REACT_APP_AZURE_DEVOPS_PROJECT || 'HighQ Tech';
                      const baseUrl = adoOrg.includes('visualstudio.com')
                        ? `https://${adoOrg}`
                        : `https://dev.azure.com/${adoOrg}`;
                      const adoUrl = `${baseUrl}/${encodeURIComponent(projectName)}/_workitems/edit/${pbi.id}`;

                      const getAgeColor = (age) => {
                        if (age == null) return 'default';
                        if (age > 180) return '#d32f2f';
                        if (age > 90) return '#f57c00';
                        if (age > 30) return '#fbc02d';
                        return '#388e3c';
                      };
                      const ageColor = getAgeColor(pbi.age);

                      return (
                        <TableRow key={pbi.id} hover>
                          <TableCell>
                            <Link
                              href={adoUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                              sx={{ fontWeight: 'medium', color: 'primary.main' }}
                            >
                              #{pbi.id}
                            </Link>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 400, wordWrap: 'break-word' }}>
                            <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                              {pbi.title || 'Untitled'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={pbi.state || 'Unknown'} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Chip label={pbi.priority || 'Unknown'} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {normalizeProductionBugAssignee(pbi.assignedTo)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {pbi.age != null ? (
                              <Chip
                                label={`${pbi.age} days`}
                                size="small"
                                sx={{ backgroundColor: ageColor, color: 'white', fontWeight: 'bold' }}
                              />
                            ) : (
                              <Typography variant="body2" color="text.secondary">-</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      )}
        </>
      )}

      {activeTab === 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 1 }}>
            <Tooltip title="Re-query Azure DevOps for every active (non-Closed) escalated issue">
              <span>
                <IconButton
                  color="primary"
                  onClick={runEscalatedRefreshAllActive}
                  disabled={
                    escalatedRefreshingAll ||
                    escalatedLoading ||
                    escalatedRows.filter((r) => !isEscalatedRowDone(r)).length === 0
                  }
                  aria-label="Refresh all active escalated issues"
                  size="medium"
                >
                  {escalatedRefreshingAll ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    <RefreshIcon />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={2}>
              <Paper
                elevation={1}
                sx={{
                  p: 2,
                  minHeight: 128,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.75,
                  borderRadius: 2,
                  bgcolor: 'grey.50',
                  border: 1,
                  borderColor: 'divider',
                  borderLeft: 4,
                  borderLeftColor: 'error.main',
                  boxSizing: 'border-box'
                }}
              >
                <Typography
                  sx={{
                    color: 'error.main',
                    fontWeight: 800,
                    fontSize: { xs: '1.875rem', sm: '2.25rem' },
                    lineHeight: 1.05,
                    letterSpacing: '-0.03em'
                  }}
                >
                  {escalatedSummaryCounts.open}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                  Open
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'error.main', fontSize: '0.875rem' }}>
                  {escalatedSummaryCounts.openPct}%
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Paper
                elevation={1}
                sx={{
                  p: 2,
                  minHeight: 128,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.75,
                  borderRadius: 2,
                  bgcolor: 'grey.50',
                  border: 1,
                  borderColor: 'divider',
                  borderLeft: 4,
                  borderLeftColor: 'success.main',
                  boxSizing: 'border-box'
                }}
              >
                <Typography
                  sx={{
                    color: 'success.main',
                    fontWeight: 800,
                    fontSize: { xs: '1.875rem', sm: '2.25rem' },
                    lineHeight: 1.05,
                    letterSpacing: '-0.03em'
                  }}
                >
                  {escalatedSummaryCounts.closed}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                  Closed
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main', fontSize: '0.875rem' }}>
                  {escalatedSummaryCounts.closedPct}%
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Paper
                elevation={1}
                sx={{
                  p: 2,
                  minHeight: 128,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.75,
                  borderRadius: 2,
                  bgcolor: 'grey.50',
                  border: 1,
                  borderColor: 'divider',
                  borderLeft: 4,
                  borderLeftColor: 'primary.main',
                  boxSizing: 'border-box'
                }}
              >
                <Typography
                  sx={{
                    color: 'primary.main',
                    fontWeight: 800,
                    fontSize: { xs: '1.875rem', sm: '2.25rem' },
                    lineHeight: 1.05,
                    letterSpacing: '-0.03em'
                  }}
                >
                  {escalatedL2EngAgeStats.l2AvgDays != null
                    ? `${Math.round(escalatedL2EngAgeStats.l2AvgDays)}d`
                    : '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.75rem', textAlign: 'center' }}>
                  Avg. L2 age
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main', fontSize: '0.875rem' }}>
                  ({escalatedL2EngAgeStats.l2Count} {escalatedL2EngAgeStats.l2Count === 1 ? 'issue' : 'issues'})
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Paper
                elevation={1}
                sx={{
                  p: 2,
                  minHeight: 128,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.75,
                  borderRadius: 2,
                  bgcolor: 'grey.50',
                  border: 1,
                  borderColor: 'divider',
                  borderLeft: 4,
                  borderLeftColor: 'info.main',
                  boxSizing: 'border-box'
                }}
              >
                <Typography
                  sx={{
                    color: 'info.main',
                    fontWeight: 800,
                    fontSize: { xs: '1.875rem', sm: '2.25rem' },
                    lineHeight: 1.05,
                    letterSpacing: '-0.03em'
                  }}
                >
                  {escalatedL2EngAgeStats.engAvgDays != null
                    ? `${Math.round(escalatedL2EngAgeStats.engAvgDays)}d`
                    : '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.75rem', textAlign: 'center' }}>
                  Avg. Engineering age
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'info.main', fontSize: '0.875rem' }}>
                  ({escalatedL2EngAgeStats.engCount} {escalatedL2EngAgeStats.engCount === 1 ? 'issue' : 'issues'})
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Paper
                elevation={1}
                sx={{
                  p: 2,
                  minHeight: 128,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.75,
                  borderRadius: 2,
                  bgcolor: 'grey.50',
                  border: 1,
                  borderColor: 'divider',
                  borderLeft: 4,
                  borderLeftColor: 'warning.main',
                  boxSizing: 'border-box'
                }}
              >
                <Typography
                  sx={{
                    color: 'warning.main',
                    fontWeight: 800,
                    fontSize: { xs: '1.875rem', sm: '2.25rem' },
                    lineHeight: 1.05,
                    letterSpacing: '-0.03em'
                  }}
                >
                  {escalatedConversionRatios.pctL1WithL2}%
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.75rem', textAlign: 'center', lineHeight: 1.35, px: 0.5 }}>
                  L1 with L2 linked
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'warning.main', fontSize: '0.8rem', textAlign: 'center' }}>
                  {escalatedConversionRatios.nL1 === 0
                    ? '(0 L1)'
                    : `(${escalatedConversionRatios.l1WithL2Count}/${escalatedConversionRatios.nL1} L1)`}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Paper
                elevation={1}
                sx={{
                  p: 2,
                  minHeight: 128,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.75,
                  borderRadius: 2,
                  bgcolor: 'grey.50',
                  border: 1,
                  borderColor: 'divider',
                  borderLeft: 4,
                  borderLeftColor: 'secondary.main',
                  boxSizing: 'border-box'
                }}
              >
                <Typography
                  sx={{
                    color: 'secondary.main',
                    fontWeight: 800,
                    fontSize: { xs: '1.875rem', sm: '2.25rem' },
                    lineHeight: 1.05,
                    letterSpacing: '-0.03em'
                  }}
                >
                  {escalatedConversionRatios.pctL2WithEng}%
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.75rem', textAlign: 'center', lineHeight: 1.35, px: 0.5 }}>
                  L2 with Engineering linked
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'secondary.main', fontSize: '0.8rem', textAlign: 'center' }}>
                  {escalatedConversionRatios.l2Total === 0
                    ? '(0 L2)'
                    : `(${escalatedConversionRatios.l2InRowsWithEngCount}/${escalatedConversionRatios.l2Total} L2)`}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr auto 1fr' },
              alignItems: 'start',
              columnGap: { md: 2 },
              rowGap: { xs: 2.5, md: 2 },
              mb: 2
            }}
          >
            <Box
              sx={{
                justifySelf: { xs: 'stretch', md: 'start' },
                width: { xs: '100%', md: 340 },
                maxWidth: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 2.5
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, width: '100%' }}>
                <TextField
                  size="small"
                  label="Salesforce ID"
                  placeholder="e.g. Salesforce / Zendesk ID"
                  value={escalatedTicketInput}
                  onChange={(e) => {
                    setEscalatedTicketInput(e.target.value);
                    setEscalatedError(null);
                  }}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    '& .MuiInputLabel-root': {
                      fontStyle: 'italic',
                      fontWeight: 400,
                      color: 'text.secondary'
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && escalatedTicketInput.trim() && !escalatedLoading && !escalatedRefreshingAll) {
                      runEscalatedLookupForTicket(escalatedTicketInput, { fromRowRefresh: false });
                    }
                  }}
                />
                <Tooltip title="Fetch Investigations and linked work items from Azure DevOps">
                  <span>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => runEscalatedLookupForTicket(escalatedTicketInput, { fromRowRefresh: false })}
                      disabled={
                        escalatedLoading || escalatedRefreshingAll || !escalatedTicketInput.trim()
                      }
                      sx={{ flexShrink: 0, minWidth: 104, height: 40 }}
                    >
                      {escalatedLoading ? (
                        <CircularProgress size={22} color="inherit" />
                      ) : (
                        'Get Details'
                      )}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
              <TextField
                size="small"
                label="Search by ID"
                placeholder="Any Salesforce, L2, or Engineering ticket #"
                value={escalatedTicketIdSearch}
                onChange={(e) => setEscalatedTicketIdSearch(e.target.value)}
                fullWidth
                sx={{
                  '& .MuiInputLabel-root': {
                    fontStyle: 'italic',
                    fontWeight: 400,
                    color: 'text.secondary'
                  }
                }}
              />
            </Box>
            <Box
              sx={{
                justifySelf: { xs: 'center', md: 'center' },
                width: { xs: 'min(100%, 280px)', md: 280 },
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                alignItems: 'stretch'
              }}
            >
              <FormControl size="small" fullWidth>
                <InputLabel id="escalated-client-name-filter-label">Client</InputLabel>
                <Select
                  labelId="escalated-client-name-filter-label"
                  label="Client"
                  value={
                    escalatedClientNameFilter === 'all'
                      ? 'all'
                      : escalatedClientNameFilterOptions.find(
                          (o) => o.toLowerCase() === escalatedClientNameFilter.toLowerCase()
                        ) ?? 'all'
                  }
                  onChange={(e) => setEscalatedClientNameFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  {escalatedClientNameFilterOptions.map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel id="escalated-state-filter-label">Status</InputLabel>
                <Select
                  labelId="escalated-state-filter-label"
                  label="Status"
                  value={escalatedStateFilter}
                  onChange={(e) => setEscalatedStateFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="mitigated">Mitigated</MenuItem>
                  <MenuItem value="closed">Closed</MenuItem>
                  <MenuItem value="not-applicable">Not applicable</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box
              sx={{
                justifySelf: { xs: 'stretch', md: 'end' },
                display: 'flex',
                flexDirection: 'column',
                alignItems: { xs: 'stretch', md: 'flex-end' },
                gap: 0.25,
                minWidth: { md: 220 }
              }}
            >
              {showEscalatedFilteredTotalCount && (
                <Tooltip title="Rows matching current filters / total escalated issues">
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      alignSelf: { md: 'flex-end' },
                      fontWeight: 700,
                      letterSpacing: '0.03em',
                      mb: 0.5,
                      userSelect: 'none'
                    }}
                  >
                    {filteredEscalatedRows.length} / {escalatedRows.length}
                  </Typography>
                </Tooltip>
              )}
              <FormControlLabel
                control={
                  <Switch
                    checked={escalatedShowL2Only}
                    onChange={(e) => setEscalatedShowL2Only(e.target.checked)}
                    size="small"
                  />
                }
                label="Show only L2"
                sx={{
                  m: 0,
                  justifyContent: { xs: 'space-between', md: 'flex-end' },
                  width: { xs: '100%', md: 'auto' },
                  '& .MuiFormControlLabel-label': { fontSize: '0.875rem' }
                }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={escalatedShowEngOnly}
                    onChange={(e) => setEscalatedShowEngOnly(e.target.checked)}
                    size="small"
                  />
                }
                label="Show only Engineering"
                sx={{
                  m: 0,
                  justifyContent: { xs: 'space-between', md: 'flex-end' },
                  width: { xs: '100%', md: 'auto' },
                  '& .MuiFormControlLabel-label': { fontSize: '0.875rem' }
                }}
              />
            </Box>
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
            Escalated Issues
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter a ticket ID (matches Zendesk Support ID in ADO). We query Investigations, resolve linked Bugs, and
            save to the database.
          </Typography>
          {escalatedError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEscalatedError(null)}>
              {escalatedError}
            </Alert>
          )}
          {escalatedLoading && (
            <Box sx={{ py: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary">
                Querying Azure DevOps…
              </Typography>
            </Box>
          )}
          {(escalatedOrderSaving || escalatedRefreshingAll) && <LinearProgress sx={{ mb: 1 }} />}
          <TableContainer component={Paper} elevation={1}>
            <Table
              size="small"
              sx={{
                tableLayout: 'fixed',
                width: '100%'
              }}
            >
              <TableHead>
                <TableRow>
                  {showEscalatedDragColumn && (
                    <TableCell sx={{ width: 40 }} padding="checkbox" aria-label="Reorder active rows" />
                  )}
                  {showEscalatedMitigateColumn && (
                    <TableCell
                      align="center"
                      padding="checkbox"
                      sx={{ width: 48 }}
                      aria-label="Mark or clear admin Mitigated (admin)"
                    />
                  )}
                  <TableCell
                    sx={{
                      width: '12%',
                      fontStyle: 'italic',
                      fontWeight: 400,
                      color: 'text.secondary'
                    }}
                  >
                    Salesforce ticket
                  </TableCell>
                  <TableCell sx={{ width: '34%', minWidth: 200 }}>
                    L2 Ticket
                  </TableCell>
                  <TableCell sx={{ width: '34%', minWidth: 200 }}>
                    Engineering Ticket
                  </TableCell>
                  <TableCell align="center" sx={{ width: '10%' }}>
                    Status
                  </TableCell>
                  <TableCell
                    align="right"
                    padding="checkbox"
                    sx={{ width: escalatedActionsColWidth }}
                    aria-label={escalatedAllowAdminDelete ? 'Row actions' : 'Refresh'}
                  />
                </TableRow>
              </TableHead>
              <TableBody>
                {escalatedRows.length === 0 && !escalatedLoading ? (
                  <TableRow>
                    <TableCell colSpan={escalatedEmptyRowColSpan}>
                      <Typography variant="body2" color="text.secondary">
                        No rows yet. Enter a ticket ID and click Get Details.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : escalatedRows.length > 0 &&
                  filteredEscalatedRows.length === 0 &&
                  !escalatedLoading ? (
                  <TableRow>
                    <TableCell colSpan={escalatedEmptyRowColSpan}>
                      <Typography variant="body2" color="text.secondary">
                        {(() => {
                          const allDone = escalatedRows.every((row) => {
                            const inv = (row.investigations || []).filter((i) => i != null && i.id != null);
                            const bugs = (row.linkedBugs || []).filter((b) => b != null && b.id != null);
                            return getEscalatedRowStatusLabel(inv, bugs, row.adminMitigated === true) === 'Closed';
                          });
                          const noExtraFilters =
                            escalatedStateFilter === 'active' &&
                            !escalatedShowL2Only &&
                            !escalatedShowEngOnly &&
                            escalatedClientNameFilter === 'all' &&
                            !String(escalatedTicketIdSearch).trim();
                          if (noExtraFilters && allDone) {
                            return (
                              <>
                                No rows are Active (all are Closed, Mitigated, or Not applicable). Set Status to
                                &quot;All&quot;, &quot;Closed&quot;, or &quot;Not applicable&quot; to see them.
                              </>
                            );
                          }
                          return (
                            <>
                              No rows match the current filters. Adjust Status, Client, Search by ID, or the L2 /
                              Engineering toggles above.
                            </>
                          );
                        })()}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {escalatedActiveRows.length > 0 && (
                      <DndContext
                        sensors={escalatedSensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleEscalatedDragEnd}
                      >
                        <SortableContext items={escalatedSortableIds} strategy={verticalListSortingStrategy}>
                          {escalatedActiveRows.map((row) => {
                        const investigations = (row.investigations || []).filter((i) => i != null && i.id != null);
                        const linkedBugs = (row.linkedBugs || []).filter((b) => b != null && b.id != null);
                        const rowId = row.userEnteredId;
                        const rowKey = String(row._id != null ? row._id : rowId);
                        const rowExpanded = escalatedExpandedRowIds.has(rowKey);
                        const rowCollapsed = !rowExpanded;
                        const rowRefreshing = escalatedRefreshingId === rowId;
                        const mongoId = row._id != null ? String(row._id) : null;
                        const rowDeleting = mongoId != null && escalatedDeletingId === mongoId;
                        const rowMitigating = mongoId != null && escalatedMitigatingId === mongoId;
                        const escalatedStatus = getEscalatedRowStatusLabel(
                          investigations,
                          linkedBugs,
                          row.adminMitigated === true
                        );
                        const showAdminMitigateBtn =
                          escalatedAllowAdminDelete &&
                          mongoId &&
                          (escalatedStatus === 'Active' || escalatedStatus === 'Not Applicable');
                        const showAdminClearMitigateBtn =
                          escalatedAllowAdminDelete &&
                          mongoId &&
                          escalatedStatus === 'Mitigated' &&
                          row.adminMitigated === true;
                        const toggleRowExpanded = () => {
                          setEscalatedExpandedRowIds((prev) => {
                            const next = new Set(prev);
                            if (next.has(rowKey)) next.delete(rowKey);
                            else next.add(rowKey);
                            return next;
                          });
                        };
                            return (
                              <EscalatedSortableTableRow key={row._id || rowId} id={rowKey} disabled={!escalatedReorderEnabled}>
                                {(drag) => (
                                  <>
                                    {drag ? (
                                      <TableCell
                                        sx={{ width: 40, verticalAlign: 'top', pt: 0.75 }}
                                        {...drag.listeners}
                                        {...drag.attributes}
                                      >
                                        <Tooltip title="Drag to reorder active tickets (saved for everyone). Closed rows stay at the bottom.">
                                          <DragIndicatorIcon sx={{ cursor: 'grab', color: 'text.secondary' }} fontSize="small" />
                                        </Tooltip>
                                      </TableCell>
                                    ) : null}
                                    {showEscalatedMitigateColumn ? (
                                      <TableCell align="center" sx={{ width: 48, verticalAlign: 'middle' }}>
                                        {showAdminMitigateBtn ? (
                                          <Tooltip title="Mark this row as Mitigated (admin only). Becomes Closed when all linked items are closed.">
                                            <span>
                                              <IconButton
                                                size="small"
                                                aria-label={`Mark escalated issue ${rowId} as Mitigated`}
                                                color="info"
                                                onClick={() => handlePatchEscalatedAdminMitigated(row, true)}
                                                disabled={
                                                  escalatedLoading ||
                                                  escalatedRefreshingAll ||
                                                  !!escalatedRefreshingId ||
                                                  rowDeleting ||
                                                  rowMitigating
                                                }
                                              >
                                                {rowMitigating ? (
                                                  <CircularProgress size={20} color="inherit" />
                                                ) : (
                                                  <ShieldOutlinedIcon fontSize="small" />
                                                )}
                                              </IconButton>
                                            </span>
                                          </Tooltip>
                                        ) : showAdminClearMitigateBtn ? (
                                          <Tooltip title="Clear the admin Mitigated flag. If Azure DevOps links still match mitigation rules (PBI/Feature or bug release), the row can stay Mitigated until ADO is updated.">
                                            <span>
                                              <IconButton
                                                size="small"
                                                aria-label={`Clear admin Mitigated for escalated issue ${rowId}`}
                                                color="warning"
                                                onClick={() => handlePatchEscalatedAdminMitigated(row, false)}
                                                disabled={
                                                  escalatedLoading ||
                                                  escalatedRefreshingAll ||
                                                  !!escalatedRefreshingId ||
                                                  rowDeleting ||
                                                  rowMitigating
                                                }
                                              >
                                                {rowMitigating ? (
                                                  <CircularProgress size={20} color="inherit" />
                                                ) : (
                                                  <UndoIcon fontSize="small" />
                                                )}
                                              </IconButton>
                                            </span>
                                          </Tooltip>
                                        ) : null}
                                      </TableCell>
                                    ) : null}
                                <TableCell sx={{ width: '12%', verticalAlign: 'top' }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.25 }}>
                            <Tooltip title={rowCollapsed ? 'Expand L2 & Engineering details' : 'Collapse L2 & Engineering details'}>
                              <IconButton
                                size="small"
                                aria-expanded={rowExpanded}
                                aria-label={rowCollapsed ? 'Expand row details' : 'Collapse row details'}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleRowExpanded();
                                }}
                                sx={{ mt: -0.25 }}
                              >
                                {rowCollapsed ? (
                                  <ExpandMoreIcon fontSize="small" />
                                ) : (
                                  <ExpandLessIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Box sx={{ minWidth: 0, pt: 0.25 }}>
                              {row.l1TicketUrl ? (
                                <Link
                                  href={row.l1TicketUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  underline="hover"
                                >
                                  {rowId}
                                </Link>
                              ) : (
                                rowId
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={{
                            width: '34%',
                            minWidth: 200,
                            verticalAlign: 'top',
                            overflow: 'hidden',
                            wordBreak: 'break-word'
                          }}
                        >
                          <Collapse in={rowExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ width: '100%' }}>
                              {investigations.length ? renderEscalatedWorkItemBlocks(investigations) : '—'}
                            </Box>
                          </Collapse>
                          {rowCollapsed ? (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              title={escalatedColumnCollapsedSummary(investigations)}
                              sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                wordBreak: 'break-word',
                                lineHeight: 1.35
                              }}
                            >
                              {escalatedColumnCollapsedSummary(investigations)}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell
                          sx={{
                            width: '34%',
                            minWidth: 200,
                            verticalAlign: 'top',
                            overflow: 'hidden',
                            wordBreak: 'break-word'
                          }}
                        >
                          <Collapse in={rowExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ width: '100%' }}>
                              {linkedBugs.length
                                ? renderEscalatedWorkItemBlocks(linkedBugs, { showReleaseChip: true })
                                : '—'}
                            </Box>
                          </Collapse>
                          {rowCollapsed ? (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              title={escalatedColumnCollapsedSummary(linkedBugs)}
                              sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                wordBreak: 'break-word',
                                lineHeight: 1.35
                              }}
                            >
                              {escalatedColumnCollapsedSummary(linkedBugs)}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ width: '10%', verticalAlign: 'middle' }}
                        >
                          <Tooltip title={escalatedOverallStatusTooltip(escalatedStatus, row)}>
                            <Chip
                              size="small"
                              label={escalatedStatus}
                              color={escalatedStatusChipColor(escalatedStatus)}
                              variant={escalatedStatus === 'Not Applicable' ? 'outlined' : 'filled'}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right" sx={{ width: escalatedActionsColWidth, verticalAlign: 'middle' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.25 }}>
                            <Tooltip title="Re-fetch this ticket from Azure DevOps and update this row only">
                              <span>
                                <IconButton
                                  size="small"
                                  aria-label={`Refresh ticket ${rowId}`}
                                  onClick={() => runEscalatedLookupForTicket(rowId, { fromRowRefresh: true })}
                                  disabled={
                                    !!escalatedRefreshingId ||
                                    escalatedLoading ||
                                    escalatedRefreshingAll ||
                                    rowRefreshing ||
                                    rowDeleting ||
                                    rowMitigating
                                  }
                                >
                                  {rowRefreshing ? (
                                    <CircularProgress size={20} color="inherit" />
                                  ) : (
                                    <RefreshIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                            {escalatedAllowAdminDelete && mongoId ? (
                              <Tooltip title="Delete this escalated issue from the database (admin only)">
                                <span>
                                  <IconButton
                                    size="small"
                                    aria-label={`Delete escalated issue ${rowId}`}
                                    color="error"
                                    onClick={() => handleDeleteEscalatedRow(row)}
                                    disabled={
                                      escalatedLoading ||
                                      escalatedRefreshingAll ||
                                      !!escalatedRefreshingId ||
                                      rowDeleting ||
                                      rowMitigating
                                    }
                                  >
                                    {rowDeleting ? (
                                      <CircularProgress size={20} color="inherit" />
                                    ) : (
                                      <DeleteIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ) : null}
                          </Box>
                        </TableCell>
                              </>
                            )}
                          </EscalatedSortableTableRow>
                        );
                      })}
                        </SortableContext>
                      </DndContext>
                    )}
                    {escalatedDoneRows.map((row) => {
                      const investigations = (row.investigations || []).filter((i) => i != null && i.id != null);
                      const linkedBugs = (row.linkedBugs || []).filter((b) => b != null && b.id != null);
                      const rowId = row.userEnteredId;
                      const rowKey = String(row._id != null ? row._id : rowId);
                      const rowExpanded = escalatedExpandedRowIds.has(rowKey);
                      const rowCollapsed = !rowExpanded;
                      const rowRefreshing = escalatedRefreshingId === rowId;
                      const mongoId = row._id != null ? String(row._id) : null;
                      const rowDeleting = mongoId != null && escalatedDeletingId === mongoId;
                      const rowMitigating = mongoId != null && escalatedMitigatingId === mongoId;
                      const escalatedStatus = getEscalatedRowStatusLabel(
                        investigations,
                        linkedBugs,
                        row.adminMitigated === true
                      );
                      const showAdminMitigateBtn =
                        escalatedAllowAdminDelete &&
                        mongoId &&
                        (escalatedStatus === 'Active' || escalatedStatus === 'Not Applicable');
                      const showAdminClearMitigateBtn =
                        escalatedAllowAdminDelete &&
                        mongoId &&
                        escalatedStatus === 'Mitigated' &&
                        row.adminMitigated === true;
                      const toggleRowExpanded = () => {
                        setEscalatedExpandedRowIds((prev) => {
                          const next = new Set(prev);
                          if (next.has(rowKey)) next.delete(rowKey);
                          else next.add(rowKey);
                          return next;
                        });
                      };
                      return (
                        <TableRow key={row._id || rowId}>
                          {showEscalatedDragColumn && (
                            <TableCell sx={{ width: 40, verticalAlign: 'top' }} padding="checkbox" aria-hidden />
                          )}
                          {showEscalatedMitigateColumn ? (
                            <TableCell align="center" sx={{ width: 48, verticalAlign: 'middle' }}>
                              {showAdminMitigateBtn ? (
                                <Tooltip title="Mark this row as Mitigated (admin only). Becomes Closed when all linked items are closed.">
                                  <span>
                                    <IconButton
                                      size="small"
                                      aria-label={`Mark escalated issue ${rowId} as Mitigated`}
                                      color="info"
                                      onClick={() => handlePatchEscalatedAdminMitigated(row, true)}
                                      disabled={
                                        escalatedLoading ||
                                        escalatedRefreshingAll ||
                                        !!escalatedRefreshingId ||
                                        rowDeleting ||
                                        rowMitigating
                                      }
                                    >
                                      {rowMitigating ? (
                                        <CircularProgress size={20} color="inherit" />
                                      ) : (
                                        <ShieldOutlinedIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              ) : showAdminClearMitigateBtn ? (
                                <Tooltip title="Clear the admin Mitigated flag. If Azure DevOps links still match mitigation rules (PBI/Feature or bug release), the row can stay Mitigated until ADO is updated.">
                                  <span>
                                    <IconButton
                                      size="small"
                                      aria-label={`Clear admin Mitigated for escalated issue ${rowId}`}
                                      color="warning"
                                      onClick={() => handlePatchEscalatedAdminMitigated(row, false)}
                                      disabled={
                                        escalatedLoading ||
                                        escalatedRefreshingAll ||
                                        !!escalatedRefreshingId ||
                                        rowDeleting ||
                                        rowMitigating
                                      }
                                    >
                                      {rowMitigating ? (
                                        <CircularProgress size={20} color="inherit" />
                                      ) : (
                                        <UndoIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              ) : null}
                            </TableCell>
                          ) : null}
                          <TableCell sx={{ width: '12%', verticalAlign: 'top' }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.25 }}>
                              <Tooltip title={rowCollapsed ? 'Expand L2 & Engineering details' : 'Collapse L2 & Engineering details'}>
                                <IconButton
                                  size="small"
                                  aria-expanded={rowExpanded}
                                  aria-label={rowCollapsed ? 'Expand row details' : 'Collapse row details'}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleRowExpanded();
                                  }}
                                  sx={{ mt: -0.25 }}
                                >
                                  {rowCollapsed ? (
                                    <ExpandMoreIcon fontSize="small" />
                                  ) : (
                                    <ExpandLessIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                              <Box sx={{ minWidth: 0, pt: 0.25 }}>
                                {row.l1TicketUrl ? (
                                  <Link
                                    href={row.l1TicketUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    underline="hover"
                                  >
                                    {rowId}
                                  </Link>
                                ) : (
                                  rowId
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell
                            sx={{
                              width: '34%',
                              minWidth: 200,
                              verticalAlign: 'top',
                              overflow: 'hidden',
                              wordBreak: 'break-word'
                            }}
                          >
                            <Collapse in={rowExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ width: '100%' }}>
                                {investigations.length ? renderEscalatedWorkItemBlocks(investigations) : '—'}
                              </Box>
                            </Collapse>
                            {rowCollapsed ? (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                title={escalatedColumnCollapsedSummary(investigations)}
                                sx={{
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  wordBreak: 'break-word',
                                  lineHeight: 1.35
                                }}
                              >
                                {escalatedColumnCollapsedSummary(investigations)}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell
                            sx={{
                              width: '34%',
                              minWidth: 200,
                              verticalAlign: 'top',
                              overflow: 'hidden',
                              wordBreak: 'break-word'
                            }}
                          >
                            <Collapse in={rowExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ width: '100%' }}>
                                {linkedBugs.length
                                  ? renderEscalatedWorkItemBlocks(linkedBugs, { showReleaseChip: true })
                                  : '—'}
                              </Box>
                            </Collapse>
                            {rowCollapsed ? (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                title={escalatedColumnCollapsedSummary(linkedBugs)}
                                sx={{
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  wordBreak: 'break-word',
                                  lineHeight: 1.35
                                }}
                              >
                                {escalatedColumnCollapsedSummary(linkedBugs)}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell align="center" sx={{ width: '10%', verticalAlign: 'middle' }}>
                            <Tooltip title={escalatedOverallStatusTooltip(escalatedStatus, row)}>
                              <Chip
                                size="small"
                                label={escalatedStatus}
                                color={escalatedStatusChipColor(escalatedStatus)}
                                variant={escalatedStatus === 'Not Applicable' ? 'outlined' : 'filled'}
                              />
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right" sx={{ width: escalatedActionsColWidth, verticalAlign: 'middle' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.25 }}>
                              <Tooltip title="Re-fetch this ticket from Azure DevOps and update this row only">
                                <span>
                                  <IconButton
                                    size="small"
                                    aria-label={`Refresh ticket ${rowId}`}
                                    onClick={() => runEscalatedLookupForTicket(rowId, { fromRowRefresh: true })}
                                    disabled={
                                      !!escalatedRefreshingId ||
                                      escalatedLoading ||
                                      escalatedRefreshingAll ||
                                      rowRefreshing ||
                                      rowDeleting ||
                                      rowMitigating
                                    }
                                  >
                                    {rowRefreshing ? (
                                      <CircularProgress size={20} color="inherit" />
                                    ) : (
                                      <RefreshIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                              {escalatedAllowAdminDelete && mongoId ? (
                                <Tooltip title="Delete this escalated issue from the database (admin only)">
                                  <span>
                                    <IconButton
                                      size="small"
                                      aria-label={`Delete escalated issue ${rowId}`}
                                      color="error"
                                      onClick={() => handleDeleteEscalatedRow(row)}
                                      disabled={
                                        escalatedLoading ||
                                        escalatedRefreshingAll ||
                                        !!escalatedRefreshingId ||
                                        rowDeleting ||
                                        rowMitigating
                                      }
                                    >
                                      {rowDeleting ? (
                                        <CircularProgress size={20} color="inherit" />
                                      ) : (
                                        <DeleteIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              ) : null}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {activeTab === 9 && (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ mb: 1, fontWeight: 600 }}>
            All Data
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            All production bugs (every state). Filter by created date, state, assignee, team, iteration path, and priority.
          </Typography>
          {allDataLoading && (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          )}
          {allDataError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAllDataError(null)}>
              {allDataError}
            </Alert>
          )}
          {!allDataLoading && !allDataError && (
            <Paper elevation={2} sx={{ p: 2 }}>
              {(() => {
                const workItemStateKey = (bug) =>
                  (bug.state && String(bug.state).trim()) || '—';

                const getFilteredAllDataExcept = (exclude) =>
                  allDataBugs.filter((bug) => {
                    const stateKey = workItemStateKey(bug);
                    if (
                      exclude !== 'state' &&
                      allDataFilterState.length > 0 &&
                      !allDataFilterState.includes(stateKey)
                    ) {
                      return false;
                    }
                    if (
                      exclude !== 'assignedTo' &&
                      allDataFilterAssignedTo.length > 0 &&
                      !allDataFilterAssignedTo.includes(bug.assignedToDisplay)
                    ) {
                      return false;
                    }
                    if (exclude !== 'team' && allDataFilterTeam.length > 0 && !allDataFilterTeam.includes(bug.team)) {
                      return false;
                    }
                    if (
                      exclude !== 'iteration' &&
                      allDataFilterIteration.length > 0 &&
                      !allDataFilterIteration.includes(bug.iterationPath || '')
                    ) {
                      return false;
                    }
                    if (
                      exclude !== 'priority' &&
                      allDataFilterPriority.length > 0 &&
                      !allDataFilterPriority.includes(bug.priorityLabel)
                    ) {
                      return false;
                    }
                    if (
                      exclude !== 'createdDate' &&
                      !allDataBugMatchesCreatedDateRange(bug, allDataFilterCreatedFrom, allDataFilterCreatedTo)
                    ) {
                      return false;
                    }
                    return true;
                  });

                const availableStates = [
                  ...new Set(getFilteredAllDataExcept('state').map((b) => workItemStateKey(b)))
                ].sort((a, b) => a.localeCompare(b));
                allDataFilterState.forEach((v) => {
                  if (!availableStates.includes(v)) availableStates.push(v);
                });

                const availableAssignees = [
                  ...new Set(getFilteredAllDataExcept('assignedTo').map((b) => b.assignedToDisplay).filter(Boolean))
                ].sort((a, b) => a.localeCompare(b));
                allDataFilterAssignedTo.forEach((v) => {
                  if (!availableAssignees.includes(v)) availableAssignees.push(v);
                });

                const availableTeamsAll = [
                  ...new Set(getFilteredAllDataExcept('team').map((b) => b.team).filter(Boolean))
                ].sort();
                allDataFilterTeam.forEach((v) => {
                  if (!availableTeamsAll.includes(v)) availableTeamsAll.push(v);
                });

                const availableIterations = [
                  ...new Set(getFilteredAllDataExcept('iteration').map((b) => b.iterationPath || ''))
                ].sort();
                allDataFilterIteration.forEach((v) => {
                  if (!availableIterations.includes(v)) availableIterations.push(v);
                });

                const availablePriorities = [
                  ...new Set(getFilteredAllDataExcept('priority').map((b) => b.priorityLabel).filter(Boolean))
                ].sort((a, b) => {
                  const na = parseInt(String(a).replace(/\D/g, ''), 10);
                  const nb = parseInt(String(b).replace(/\D/g, ''), 10);
                  if (!Number.isNaN(na) && !Number.isNaN(nb) && na !== nb) return na - nb;
                  return String(a).localeCompare(String(b));
                });
                allDataFilterPriority.forEach((v) => {
                  if (!availablePriorities.includes(v)) availablePriorities.push(v);
                });

                const filteredAllData = getFilteredAllDataExcept(null);

                const sortedAllData = [...filteredAllData].sort((a, b) => {
                  let aValue = a[allDataSortField];
                  let bValue = b[allDataSortField];
                  if (allDataSortField === 'assignedTo') {
                    aValue = a.assignedToDisplay;
                    bValue = b.assignedToDisplay;
                  }
                  if (allDataSortField === 'codeFix') {
                    const score = (x) => {
                      if (x === true) return 2;
                      if (x === false) return 1;
                      return 0;
                    };
                    const sa = score(a.codeFix);
                    const sb = score(b.codeFix);
                    return allDataSortDirection === 'asc' ? sa - sb : sb - sa;
                  }
                  if (allDataSortField === 'priorityLabel') {
                    const getPriorityValue = (priority) => {
                      if (!priority || priority === '—') return 999;
                      const match = String(priority).match(/P?(\d+)/i);
                      return match ? parseInt(match[1], 10) : 999;
                    };
                    const na = getPriorityValue(a.priorityLabel);
                    const nb = getPriorityValue(b.priorityLabel);
                    return allDataSortDirection === 'asc' ? na - nb : nb - na;
                  }
                  if (allDataSortField === 'createdDate') {
                    aValue = a.createdDate ? new Date(a.createdDate).getTime() : 0;
                    bValue = b.createdDate ? new Date(b.createdDate).getTime() : 0;
                    return allDataSortDirection === 'asc' ? aValue - bValue : bValue - aValue;
                  }
                  if (allDataSortField === 'age' || allDataSortField === 'id') {
                    aValue = Number(aValue) || 0;
                    bValue = Number(bValue) || 0;
                    return allDataSortDirection === 'asc' ? aValue - bValue : bValue - aValue;
                  }
                  aValue = String(aValue || '').toLowerCase();
                  bValue = String(bValue || '').toLowerCase();
                  return allDataSortDirection === 'asc'
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
                });

                const handleAllDataSort = (field) => {
                  if (allDataSortField === field) {
                    setAllDataSortDirection(allDataSortDirection === 'asc' ? 'desc' : 'asc');
                  } else {
                    setAllDataSortField(field);
                    setAllDataSortDirection(
                      field === 'createdDate' || field === 'age' || field === 'codeFix' ? 'desc' : 'asc'
                    );
                  }
                };

                const iterationMenuLabel = (path) => {
                  if (!path) return '(None)';
                  return path.split('\\').pop() || path;
                };

                const getAgeColorAll = (age) => {
                  if (age == null) return 'default';
                  if (age > 180) return '#d32f2f';
                  if (age > 90) return '#f57c00';
                  if (age > 30) return '#fbc02d';
                  return '#388e3c';
                };

                const allDataRowCount = sortedAllData.length;
                const allDataPageCount = Math.max(1, Math.ceil(allDataRowCount / ALL_DATA_ROWS_PER_PAGE));
                const allDataSafePage = Math.min(allDataPage, allDataPageCount - 1);
                const allDataPagedRows = sortedAllData.slice(
                  allDataSafePage * ALL_DATA_ROWS_PER_PAGE,
                  allDataSafePage * ALL_DATA_ROWS_PER_PAGE + ALL_DATA_ROWS_PER_PAGE
                );

                const renderAllDataPaginationBar = () => (
                  <TablePagination
                    component="div"
                    count={allDataRowCount}
                    page={allDataSafePage}
                    onPageChange={(_, newPage) => setAllDataPage(newPage)}
                    rowsPerPage={ALL_DATA_ROWS_PER_PAGE}
                    rowsPerPageOptions={[ALL_DATA_ROWS_PER_PAGE]}
                    labelDisplayedRows={({ from, to, count }) =>
                      `${from}–${to} of ${count !== -1 ? count : to}`
                    }
                    sx={{ borderColor: 'divider', '.MuiTablePagination-toolbar': { minHeight: 48, px: 0 } }}
                  />
                );

                return (
                  <>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                      {isAdmin() && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleFetchAllDataCodeFixes}
                          disabled={allDataCodeFixLoading || allDataLoading}
                        >
                          {allDataCodeFixLoading ? 'Fetching Code Fixes…' : 'Fetch Code Fixes'}
                        </Button>
                      )}
                      <TextField
                        label="Created from"
                        type="date"
                        size="small"
                        value={allDataFilterCreatedFrom}
                        onChange={(e) => setAllDataFilterCreatedFrom(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ minWidth: 158 }}
                      />
                      <TextField
                        label="Created to"
                        type="date"
                        size="small"
                        value={allDataFilterCreatedTo}
                        onChange={(e) => setAllDataFilterCreatedTo(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ minWidth: 158 }}
                      />
                      {(allDataFilterCreatedFrom || allDataFilterCreatedTo) && (
                        <Button size="small" onClick={() => { setAllDataFilterCreatedFrom(''); setAllDataFilterCreatedTo(''); }}>
                          Clear dates
                        </Button>
                      )}
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel id="all-data-state-label">State</InputLabel>
                        <Select
                          labelId="all-data-state-label"
                          multiple
                          value={allDataFilterState}
                          onChange={(e) => setAllDataFilterState(e.target.value)}
                          input={<OutlinedInput label="State" />}
                          renderValue={(selected) =>
                            selected.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">
                                All states
                              </Typography>
                            ) : selected.length === 1 ? (
                              selected[0]
                            ) : (
                              `${selected.length} selected`
                            )
                          }
                          MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
                        >
                          {availableStates.map((st) => (
                            <MenuItem key={st} value={st}>
                              <Checkbox checked={allDataFilterState.indexOf(st) > -1} />
                              <ListItemText primary={st} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel id="all-data-assign-label">Assigned to</InputLabel>
                        <Select
                          labelId="all-data-assign-label"
                          multiple
                          value={allDataFilterAssignedTo}
                          onChange={(e) => setAllDataFilterAssignedTo(e.target.value)}
                          input={<OutlinedInput label="Assigned to" />}
                          renderValue={(selected) =>
                            selected.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">
                                All assignees
                              </Typography>
                            ) : selected.length === 1 ? (
                              selected[0]
                            ) : (
                              `${selected.length} selected`
                            )
                          }
                          MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
                        >
                          {availableAssignees.map((name) => (
                            <MenuItem key={name} value={name}>
                              <Checkbox checked={allDataFilterAssignedTo.indexOf(name) > -1} />
                              <ListItemText primary={name} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel id="all-data-team-label">Team</InputLabel>
                        <Select
                          labelId="all-data-team-label"
                          multiple
                          value={allDataFilterTeam}
                          onChange={(e) => setAllDataFilterTeam(e.target.value)}
                          input={<OutlinedInput label="Team" />}
                          renderValue={(selected) =>
                            selected.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">
                                All teams
                              </Typography>
                            ) : selected.length === 1 ? (
                              selected[0]
                            ) : (
                              `${selected.length} selected`
                            )
                          }
                          MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
                        >
                          {availableTeamsAll.map((team) => (
                            <MenuItem key={team} value={team}>
                              <Checkbox checked={allDataFilterTeam.indexOf(team) > -1} />
                              <ListItemText primary={team} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel id="all-data-iter-label">Iteration</InputLabel>
                        <Select
                          labelId="all-data-iter-label"
                          multiple
                          value={allDataFilterIteration}
                          onChange={(e) => setAllDataFilterIteration(e.target.value)}
                          input={<OutlinedInput label="Iteration" />}
                          renderValue={(selected) =>
                            selected.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">
                                All iterations
                              </Typography>
                            ) : selected.length === 1 ? (
                              iterationMenuLabel(selected[0])
                            ) : (
                              `${selected.length} selected`
                            )
                          }
                          MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
                        >
                          {availableIterations.map((iter) => (
                            <MenuItem key={iter || '__empty__'} value={iter}>
                              <Checkbox checked={allDataFilterIteration.indexOf(iter) > -1} />
                              <ListItemText primary={iterationMenuLabel(iter)} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel id="all-data-pri-label">Priority</InputLabel>
                        <Select
                          labelId="all-data-pri-label"
                          multiple
                          value={allDataFilterPriority}
                          onChange={(e) => setAllDataFilterPriority(e.target.value)}
                          input={<OutlinedInput label="Priority" />}
                          renderValue={(selected) =>
                            selected.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">
                                All priorities
                              </Typography>
                            ) : selected.length === 1 ? (
                              selected[0]
                            ) : (
                              `${selected.length} selected`
                            )
                          }
                          MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
                        >
                          {availablePriorities.map((p) => (
                            <MenuItem key={p} value={p}>
                              <Checkbox checked={allDataFilterPriority.indexOf(p) > -1} />
                              <ListItemText primary={p} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Typography variant="body2" color="text.secondary" sx={{ ml: { xs: 0, sm: 'auto' } }}>
                        {filteredAllData.length} match{filteredAllData.length === 1 ? '' : 'es'} · {allDataBugs.length} loaded
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        borderBottom: 1,
                        borderColor: 'divider',
                        mb: 1
                      }}
                    >
                      {renderAllDataPaginationBar()}
                    </Box>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                      <Table
                        size="small"
                        sx={{
                          tableLayout: 'fixed',
                          width: '100%',
                          minWidth: 1120
                        }}
                      >
                        <colgroup>
                          <col style={{ width: '5%' }} />
                          <col style={{ width: '6%' }} />
                          <col style={{ width: '25%' }} />
                          <col style={{ width: '11%' }} />
                          <col style={{ width: '5%' }} />
                          <col style={{ width: '10%' }} />
                          <col style={{ width: '12%' }} />
                          <col style={{ width: '9%' }} />
                          <col style={{ width: '6%' }} />
                          <col style={{ width: '5%' }} />
                          <col style={{ width: '6%' }} />
                        </colgroup>
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{ fontWeight: 600, whiteSpace: 'nowrap', py: 1 }}
                              onClick={() => handleAllDataSort('id')}
                              style={{ cursor: 'pointer' }}
                            >
                              ID
                              {allDataSortField === 'id' &&
                                (allDataSortDirection === 'asc' ? (
                                  <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
                                ) : (
                                  <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
                                ))}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{ fontWeight: 600, whiteSpace: 'nowrap', py: 1 }}
                              onClick={() => handleAllDataSort('codeFix')}
                              style={{ cursor: 'pointer' }}
                            >
                              Code Fix
                              {allDataSortField === 'codeFix' &&
                                (allDataSortDirection === 'asc' ? (
                                  <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
                                ) : (
                                  <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
                                ))}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, py: 1 }}>Title</TableCell>
                            <TableCell
                              sx={{ fontWeight: 600, whiteSpace: 'nowrap', py: 1 }}
                              onClick={() => handleAllDataSort('state')}
                              style={{ cursor: 'pointer' }}
                            >
                              State
                            </TableCell>
                            <TableCell
                              sx={{ fontWeight: 600, whiteSpace: 'nowrap', py: 1 }}
                              onClick={() => handleAllDataSort('priorityLabel')}
                              style={{ cursor: 'pointer' }}
                            >
                              Priority
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, py: 1 }} onClick={() => handleAllDataSort('team')} style={{ cursor: 'pointer' }}>
                              Team
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, py: 1 }} onClick={() => handleAllDataSort('assignedTo')} style={{ cursor: 'pointer' }}>
                              Assigned to
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, py: 1 }} onClick={() => handleAllDataSort('iterationPath')} style={{ cursor: 'pointer' }}>
                              Iteration
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, py: 1 }} onClick={() => handleAllDataSort('fixVersion')} style={{ cursor: 'pointer' }}>
                              Fix version
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, py: 1 }} onClick={() => handleAllDataSort('customer')} style={{ cursor: 'pointer' }}>
                              Customer
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ fontWeight: 600, whiteSpace: 'nowrap', py: 1 }}
                              onClick={() => handleAllDataSort('age')}
                              style={{ cursor: 'pointer' }}
                            >
                              Age (days)
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sortedAllData.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={11} align="center">
                                <Typography variant="body2" color="text.secondary">
                                  {allDataBugs.length === 0
                                    ? 'No production bugs were returned from the API.'
                                    : 'No bugs match the selected filters.'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            allDataPagedRows.map((bug) => {
                              const adoUrl = getAdoWorkItemUrl(bug.id);
                              const ageColor = getAgeColorAll(bug.age);
                              const iterDisp = bug.iterationPath ? bug.iterationPath.split('\\').pop() : '—';
                              return (
                                <TableRow key={bug.id} hover>
                                  <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top', py: 1.25 }}>
                                    <Link href={adoUrl} target="_blank" rel="noopener noreferrer" underline="hover">
                                      #{bug.id}
                                    </Link>
                                  </TableCell>
                                  <TableCell align="center" sx={{ verticalAlign: 'top', py: 1.25 }}>
                                    {!isAllDataBugClosedState(bug) ? (
                                      <Typography variant="body2" color="text.secondary">
                                        —
                                      </Typography>
                                    ) : typeof bug.codeFix === 'boolean' ? (
                                      <Checkbox checked={bug.codeFix} disabled size="small" />
                                    ) : (
                                      <Typography variant="body2" color="text.secondary">
                                        —
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell sx={{ verticalAlign: 'top', py: 1.25, overflowWrap: 'break-word' }}>
                                    <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                      {bug.title || '—'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell sx={{ verticalAlign: 'top', py: 1.25 }}>
                                    <Chip
                                      label={bug.state || '—'}
                                      size="small"
                                      variant="outlined"
                                      sx={{ maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ verticalAlign: 'top', py: 1.25, whiteSpace: 'nowrap' }}>
                                    <Chip label={bug.priorityLabel} size="small" variant="outlined" />
                                  </TableCell>
                                  <TableCell sx={{ verticalAlign: 'top', py: 1.25 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'normal' }}>
                                      {bug.team}
                                    </Typography>
                                  </TableCell>
                                  <TableCell sx={{ verticalAlign: 'top', py: 1.25 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'normal' }}>
                                      {bug.assignedToDisplay}
                                    </Typography>
                                  </TableCell>
                                  <TableCell sx={{ verticalAlign: 'top', py: 1.25 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'normal' }}>
                                      {iterDisp}
                                    </Typography>
                                  </TableCell>
                                  <TableCell sx={{ verticalAlign: 'top', py: 1.25 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'normal' }}>
                                      {bug.fixVersion || '—'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell sx={{ verticalAlign: 'top', py: 1.25 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'normal' }}>
                                      {bug.customer || '—'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right" sx={{ verticalAlign: 'top', py: 1.25, whiteSpace: 'nowrap' }}>
                                    <Tooltip
                                      title={
                                        bug.createdDate
                                          ? new Date(bug.createdDate).toLocaleString('en-US', {
                                              year: 'numeric',
                                              month: 'short',
                                              day: 'numeric',
                                              hour: '2-digit',
                                              minute: '2-digit',
                                              hour12: true
                                            })
                                          : 'Created date unknown'
                                      }
                                    >
                                      <Box component="span" sx={{ display: 'inline-flex' }}>
                                        {bug.age != null ? (
                                          <Chip
                                            label={`${bug.age} days`}
                                            size="small"
                                            sx={{
                                              backgroundColor: ageColor,
                                              color: 'white',
                                              fontWeight: 'bold'
                                            }}
                                          />
                                        ) : (
                                          <Typography variant="body2" color="text.secondary">
                                            —
                                          </Typography>
                                        )}
                                      </Box>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        borderTop: 1,
                        borderColor: 'divider',
                        mt: 1
                      }}
                    >
                      {renderAllDataPaginationBar()}
                    </Box>
                  </>
                );
              })()}
            </Paper>
          )}
        </Box>
      )}

      {activeTab === 10 && (
        <Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
              mb: 1
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0 }}>
              Eng-TSI
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ ml: { xs: 0, sm: 'auto' }, textAlign: { xs: 'left', sm: 'right' } }}>
              Initiative started on March 18 2026
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Paste an ADO saved query URL, a work items URL containing{' '}
            <code>wiql=</code>, or raw WIQL. Results are fetched from Azure DevOps and stored in the{' '}
            <code>eng-tsi-items</code> collection.
          </Typography>
          {engTsiSyncedAt && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
              Last synced: {new Date(engTsiSyncedAt).toLocaleString()}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap', mb: 3 }}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              variant="outlined"
              size="small"
              label="ADO filter"
              placeholder="https://dev.azure.com/org/project/_queries/query/... or SELECT [System.Id] FROM WorkItems WHERE ..."
              value={engTsiFilterInput}
              onChange={isAdmin() ? (e) => { setEngTsiFilterInput(e.target.value); setEngTsiError(null); } : undefined}
              InputProps={{ readOnly: !isAdmin() }}
              sx={{ maxWidth: 720 }}
            />
            {isAdmin() && (
              <Button
                variant="contained"
                onClick={async () => {
                  if (!engTsiFilterInput.trim()) return;
                  setEngTsiLoading(true);
                  setEngTsiError(null);
                  try {
                    const data = await syncEngTsiItems(engTsiFilterInput.trim());
                    setEngTsiData({ columns: data.columns || [], rows: data.rows || [] });
                    if (data.syncedAt) setEngTsiSyncedAt(data.syncedAt);
                    setEngTsiPage(0);
                    setEngTsiSortField(null);
                    setEngTsiSortDirection('asc');
                  } catch (err) {
                    setEngTsiError(err.message || 'Failed to fetch from ADO');
                    setEngTsiData({ columns: [], rows: [] });
                  } finally {
                    setEngTsiLoading(false);
                  }
                }}
                disabled={engTsiLoading || !engTsiFilterInput.trim()}
              >
                {engTsiLoading ? 'Fetching…' : 'Fetch from ADO'}
              </Button>
            )}
          </Box>
          {engTsiError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEngTsiError(null)}>
              {engTsiError}
            </Alert>
          )}
          {engTsiLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">
                Querying Azure DevOps…
              </Typography>
            </Box>
          )}
          {engTsiData.columns.length > 0 && (() => {
            const rows = engTsiData.rows || [];
            const displayCols = engTsiData.columns.filter((c) => !isEngTsiHiddenFromDisplayColumn(c));
            const sortedRows =
              engTsiSortField != null
                ? [...rows].sort((a, b) =>
                    compareEngTsiRowsForSort(a, b, engTsiSortField, engTsiSortDirection)
                  )
                : rows;
            const pageCount = Math.max(1, Math.ceil(sortedRows.length / engTsiRowsPerPage));
            const safePage = Math.min(engTsiPage, pageCount - 1);
            const start = safePage * engTsiRowsPerPage;
            const pageRows = sortedRows.slice(start, start + engTsiRowsPerPage);
            if (displayCols.length === 0) {
              return (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Every column in this query is hidden from the table for display; full rows remain in the database.
                </Alert>
              );
            }
            const allCols = engTsiData.columns || [];
            const chartRows = rows;
            const clsCol = findEngTsiClassificationColumnRef(allCols);
            const stateCol = findEngTsiStateColumnRef(allCols);
            const envCol = findEngTsiEnvironmentColumnRef(allCols);
            const clsCounts = aggregateEngTsiFieldCounts(chartRows, clsCol);
            const stateCounts = aggregateEngTsiFieldCounts(chartRows, stateCol);
            const envCounts = aggregateEngTsiFieldCounts(chartRows, envCol);
            const renderEngTsiSummaryTable = (counts, title, missingHint, columnRef) => {
              const entries =
                counts && Object.keys(counts).length > 0
                  ? Object.entries(counts).sort((a, b) => b[1] - a[1])
                  : [];
              const total = entries.reduce((s, [, n]) => s + n, 0);
              return (
                <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, textAlign: 'center' }}>
                    {title}
                  </Typography>
                  {!columnRef ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                      {missingHint}
                    </Typography>
                  ) : entries.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                      No rows to summarize.
                    </Typography>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              Count
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              %
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {entries.map(([label, count]) => (
                            <TableRow key={label}>
                              <TableCell sx={{ wordBreak: 'break-word' }}>{label}</TableCell>
                              <TableCell align="right">{count}</TableCell>
                              <TableCell align="right">
                                {total ? ((count / total) * 100).toFixed(1) : '0.0'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Paper>
              );
            };
            return (
              <>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={4}>
                    {renderEngTsiSummaryTable(
                      clsCounts,
                      'By classification',
                      'Include an Investigation Classification field in your ADO query to see this summary.',
                      clsCol
                    )}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    {renderEngTsiSummaryTable(
                      stateCounts,
                      'By state',
                      'Include System.State in your ADO query to see this summary.',
                      stateCol
                    )}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    {renderEngTsiSummaryTable(
                      envCounts,
                      'By environment',
                      'Include Environment (e.g. Custom.Environment) in your ADO query to see this summary.',
                      envCol
                    )}
                  </Grid>
                </Grid>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 1,
                    mb: 1
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Data table
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    endIcon={engTsiTableExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    onClick={() => setEngTsiTableExpanded((e) => !e)}
                  >
                    {engTsiTableExpanded ? 'Collapse' : 'Expand'}
                  </Button>
                </Box>
                <Collapse in={engTsiTableExpanded}>
                  <TableContainer component={Paper} elevation={1} sx={{ overflowX: 'auto' }}>
                    <Table
                      size="small"
                      stickyHeader
                      sx={{
                        tableLayout: 'fixed',
                        minWidth: Math.max(800, 260 + Math.max(0, displayCols.length - 1) * 112)
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          {displayCols.map((col) => (
                            <TableCell
                              key={col}
                              title={col}
                              sortDirection={engTsiSortField === col ? engTsiSortDirection : false}
                              sx={{
                                fontWeight: 600,
                                ...getEngTsiColumnSx(col, displayCols)
                              }}
                            >
                              <TableSortLabel
                                active={engTsiSortField === col}
                                direction={engTsiSortField === col ? engTsiSortDirection : 'asc'}
                                onClick={() => {
                                  if (engTsiSortField === col) {
                                    setEngTsiSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
                                  } else {
                                    setEngTsiSortField(col);
                                    setEngTsiSortDirection('asc');
                                  }
                                  setEngTsiPage(0);
                                }}
                                sx={{ '& .MuiTableSortLabel-icon': { opacity: engTsiSortField === col ? 1 : 0.35 } }}
                              >
                                {engTsiColumnHeaderLabel(col)}
                              </TableSortLabel>
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pageRows.map((row, idx) => (
                          <TableRow key={start + idx}>
                            {displayCols.map((col) => {
                              const displayVal = engTsiFormatCellDisplay(col, row[col]);
                              const idNum = isEngTsiIdColumn(col) ? parseEngTsiWorkItemId(row[col]) : null;
                              const adoUrl = idNum != null ? getAdoWorkItemUrl(idNum) : null;
                              return (
                                <TableCell key={col} sx={getEngTsiBodyCellSx(col, displayCols)}>
                                  {adoUrl ? (
                                    <Link
                                      href={adoUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      variant="body2"
                                      sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                                    >
                                      {displayVal}
                                    </Link>
                                  ) : (
                                    <Typography
                                      variant="body2"
                                      component="span"
                                      sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                                    >
                                      {displayVal}
                                    </Typography>
                                  )}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        ))}
                      </TableBody>
                      <TableFooter>
                        <TableRow>
                          <TablePagination
                            colSpan={displayCols.length}
                            count={sortedRows.length}
                            rowsPerPage={engTsiRowsPerPage}
                            page={safePage}
                            onPageChange={(_, p) => setEngTsiPage(p)}
                            rowsPerPageOptions={[engTsiRowsPerPage]}
                          />
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </TableContainer>
                </Collapse>
              </>
            );
          })()}
          {!engTsiLoading && engTsiData.columns.length === 0 && !engTsiError && (
            <Alert severity="info">No items loaded yet. Admins can paste a filter and click Fetch from ADO.</Alert>
          )}
        </Box>
      )}

      {activeTab === 2 && (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            Top 15 Customers - Issues Distribution
          </Typography>
          
          <Grid container spacing={3}>
            {/* All Issues Chart */}
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ mb: 2, fontWeight: 'bold' }}>
                  All Issues by Customer
                </Typography>
                {topCustomersAll.length > 0 ? (
                  <Box sx={{ py: 2 }}>
                    <HeatMap 
                      data={topCustomersAll.map(item => ({ customer: item.customer, count: item.count }))}
                      title="All Issues by Customer"
                    />
                  </Box>
                ) : (
                  <Alert severity="info">
                    No customer data available for all issues.
                  </Alert>
                )}
              </Paper>
            </Grid>

            {/* Open Issues Chart */}
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ mb: 2, fontWeight: 'bold' }}>
                  Open Issues by Customer
                </Typography>
                {topCustomersOpen.length > 0 ? (
                  <Box sx={{ py: 2 }}>
                    <HeatMap 
                      data={topCustomersOpen.map(item => ({ customer: item.customer, count: item.count }))}
                      title="Open Issues by Customer"
                    />
                  </Box>
                ) : (
                  <Alert severity="info">
                    No customer data available for open issues.
                  </Alert>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}

      {activeTab === 3 && (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            RCA Distribution (Closed Issues Only)
          </Typography>
          
          {/* RCA Pie Charts */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* All Closed Issues by RCA */}
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ mb: 2, fontWeight: 'bold' }}>
                  All Closed Issues by RCA (Closed after March 2025)
                </Typography>
                {rcaBreakdown.allClosed.length > 0 ? (
                  <Box sx={{ py: 2 }}>
                    <HeatMap 
                      data={rcaBreakdown.allClosed.map(item => ({ rca: item.rca, count: item.count }))}
                      title="All Closed Issues by RCA (Closed after March 2025)"
                      useRcaColors={true}
                    />
                  </Box>
                ) : (
                  <Alert severity="info">
                    No closed issues with RCA data available.
                  </Alert>
                )}
              </Paper>
            </Grid>

            {/* Last 6 Months Closed Issues by RCA */}
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ mb: 2, fontWeight: 'bold' }}>
                  Last 6 Months Closed Issues by RCA (Closed after March 2025)
                </Typography>
                {rcaBreakdown.last6Months.length > 0 ? (
                  <Box sx={{ py: 2 }}>
                    <HeatMap 
                      data={rcaBreakdown.last6Months.map(item => ({ rca: item.rca, count: item.count }))}
                      title="Last 6 Months Closed Issues by RCA (Closed after March 2025)"
                      useRcaColors={true}
                    />
                  </Box>
                ) : (
                  <Alert severity="info">
                    No closed issues with RCA data available for the last 6 months.
                  </Alert>
                )}
              </Paper>
            </Grid>
          </Grid>

          {/* Data Table for Last 6 Months Closed Bugs with RCA */}
          {closedBugsWithRcaLast6Months.length > 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Paper elevation={2} sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Closed Bugs with RCA (Last 6 Months)
                    </Typography>
                    <Tooltip 
                      title="List of all production bugs closed in the last 6 months that have RCA data. Use filters to narrow down by Team."
                      arrow
                      placement="top"
                    >
                      <IconButton size="small">
                        <InfoIcon fontSize="small" color="action" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  
                  {/* Team and RCA Filters */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <InputLabel>Team</InputLabel>
                      <Select
                        value={selectedTeamRca}
                        label="Team"
                        onChange={(e) => setSelectedTeamRca(e.target.value)}
                      >
                        <MenuItem value="">All Teams</MenuItem>
                        {rcaFilterOptions.teams.map((team) => (
                          <MenuItem key={team} value={team}>
                            {team}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <InputLabel>RCA</InputLabel>
                      <Select
                        value={selectedRca}
                        label="RCA"
                        onChange={(e) => setSelectedRca(e.target.value)}
                      >
                        <MenuItem value="">All RCA</MenuItem>
                        {(() => {
                          // Get unique RCAs from the data
                          const uniqueRcas = [...new Set(closedBugsWithRcaLast6Months.map(bug => bug.rca).filter(Boolean))].sort();
                          return uniqueRcas.map((rca) => (
                            <MenuItem key={rca} value={rca}>
                              {rca}
                            </MenuItem>
                          ));
                        })()}
                      </Select>
                    </FormControl>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
                      <Typography variant="body2" color="text.secondary">
                        Showing {(() => {
                          const filtered = closedBugsWithRcaLast6Months.filter(bug => {
                            if (selectedTeamRca && bug.team !== selectedTeamRca) return false;
                            if (selectedRca && bug.rca !== selectedRca) return false;
                            return true;
                          });
                          return filtered.length;
                        })()} of {closedBugsWithRcaLast6Months.length} bugs
                      </Typography>
                    </Box>
                  </Box>
                  
                  {/* Handle sorting for RCA table */}
                  {(() => {
                    const handleSortRca = (field) => {
                      if (sortFieldRca === field) {
                        setSortDirectionRca(sortDirectionRca === 'asc' ? 'desc' : 'asc');
                      } else {
                        setSortFieldRca(field);
                        setSortDirectionRca('asc');
                      }
                    };
                    
                    // Filter and sort bugs
                    const filteredRcaBugs = closedBugsWithRcaLast6Months.filter(bug => {
                      if (selectedTeamRca && bug.team !== selectedTeamRca) return false;
                      if (selectedRca && bug.rca !== selectedRca) return false;
                      return true;
                    });
                    
                    const sortedRcaBugs = [...filteredRcaBugs].sort((a, b) => {
                      let aValue = a[sortFieldRca];
                      let bValue = b[sortFieldRca];
                      
                      // Handle date fields
                      if (sortFieldRca === 'closedDate' || sortFieldRca === 'createdDate') {
                        aValue = aValue ? new Date(aValue) : new Date(0);
                        bValue = bValue ? new Date(bValue) : new Date(0);
                        return sortDirectionRca === 'asc' ? aValue - bValue : bValue - aValue;
                      }
                      
                      // Handle numeric fields
                      if (sortFieldRca === 'age' || sortFieldRca === 'id') {
                        aValue = Number(aValue) || 0;
                        bValue = Number(bValue) || 0;
                        return sortDirectionRca === 'asc' ? aValue - bValue : bValue - aValue;
                      }
                      
                      // Handle priority field
                      if (sortFieldRca === 'priority') {
                        const getPriorityValue = (priority) => {
                          if (!priority) return 999;
                          const match = String(priority).match(/P?(\d+)/i);
                          return match ? parseInt(match[1], 10) : 999;
                        };
                        aValue = getPriorityValue(aValue);
                        bValue = getPriorityValue(bValue);
                        return sortDirectionRca === 'asc' ? aValue - bValue : bValue - aValue;
                      }
                      
                      // Handle string fields
                      aValue = String(aValue || '').toLowerCase();
                      bValue = String(bValue || '').toLowerCase();
                      
                      if (sortDirectionRca === 'asc') {
                        return aValue.localeCompare(bValue);
                      } else {
                        return bValue.localeCompare(aValue);
                      }
                    });
                    
                    return (
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell 
                                sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                                onClick={() => handleSortRca('id')}
                              >
                                <Box display="flex" alignItems="center">
                                  ID
                                  {sortFieldRca === 'id' && (
                                    sortDirectionRca === 'asc' ? 
                                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell 
                                sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                                onClick={() => handleSortRca('title')}
                              >
                                <Box display="flex" alignItems="center">
                                  Title
                                  {sortFieldRca === 'title' && (
                                    sortDirectionRca === 'asc' ? 
                                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell 
                                sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                                onClick={() => handleSortRca('rca')}
                              >
                                <Box display="flex" alignItems="center">
                                  RCA
                                  {sortFieldRca === 'rca' && (
                                    sortDirectionRca === 'asc' ? 
                                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell 
                                sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                                onClick={() => handleSortRca('team')}
                              >
                                <Box display="flex" alignItems="center">
                                  Team
                                  {sortFieldRca === 'team' && (
                                    sortDirectionRca === 'asc' ? 
                                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell 
                                sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                                onClick={() => handleSortRca('priority')}
                              >
                                <Box display="flex" alignItems="center">
                                  Priority
                                  {sortFieldRca === 'priority' && (
                                    sortDirectionRca === 'asc' ? 
                                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell 
                                sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                                onClick={() => handleSortRca('closedDate')}
                              >
                                <Box display="flex" alignItems="center">
                                  Closed Date
                                  {sortFieldRca === 'closedDate' && (
                                    sortDirectionRca === 'asc' ? 
                                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell 
                                sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                                align="right"
                                onClick={() => handleSortRca('age')}
                              >
                                <Box display="flex" alignItems="center" justifyContent="flex-end">
                                  Age (days)
                                  {sortFieldRca === 'age' && (
                                    sortDirectionRca === 'asc' ? 
                                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sortedRcaBugs.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={7} align="center">
                                  <Typography variant="body2" color="text.secondary">
                                    No bugs found matching the selected filters.
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ) : (
                              sortedRcaBugs.map((bug) => {
                                // Generate ADO URL
                                const adoOrg = process.env.REACT_APP_AZURE_DEVOPS_ORG || 'tr-legal.visualstudio.com';
                                const projectName = process.env.REACT_APP_AZURE_DEVOPS_PROJECT || 'HighQ Tech';
                                const baseUrl = adoOrg.includes('visualstudio.com') 
                                  ? `https://${adoOrg}` 
                                  : `https://dev.azure.com/${adoOrg}`;
                                const adoUrl = `${baseUrl}/${encodeURIComponent(projectName)}/_workitems/edit/${bug.id}`;
                                
                                // Get age color
                                const getAgeColor = (age) => {
                                  if (age > 180) return '#d32f2f'; // Red
                                  if (age > 90) return '#f57c00'; // Orange
                                  if (age > 30) return '#fbc02d'; // Yellow
                                  return '#388e3c'; // Green
                                };
                                
                                const ageColor = getAgeColor(bug.age);
                                
                                return (
                                  <TableRow key={bug.id} hover>
                                    <TableCell>
                                      <Link
                                        href={adoUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        underline="hover"
                                        sx={{ 
                                          fontWeight: 'medium',
                                          color: 'primary.main'
                                        }}
                                      >
                                        #{bug.id}
                                      </Link>
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 400, wordWrap: 'break-word' }}>
                                      <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                        {bug.title || 'Untitled'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2">
                                        {bug.rca || 'Unknown/Blank'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2" color="text.secondary">
                                        {bug.team || 'Unknown'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        label={bug.priority || 'Unknown'}
                                        size="small"
                                        variant="outlined"
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2" color="text.secondary">
                                        {bug.closedDate ? new Date(bug.closedDate).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : 'N/A'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Chip
                                        label={`${bug.age} days`}
                                        size="small"
                                        sx={{
                                          backgroundColor: ageColor,
                                          color: 'white',
                                          fontWeight: 'bold'
                                        }}
                                      />
                                    </TableCell>
                                  </TableRow>
                                );
                              })
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    );
                  })()}
                </Paper>
              </Grid>
            </Grid>
          )}
        </Box>
      )}

      {activeTab === 4 && (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            Production Bugs by Environment
          </Typography>
          
          {environmentLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Summary Cards for Non-Closed Bugs by Environment */}
              {(() => {
                // Calculate non-closed bugs by environment
                const nonClosedByEnv = {};
                allNonClosedBugs.forEach(bug => {
                  const env = bug.environment || 'Unknown';
                  nonClosedByEnv[env] = (nonClosedByEnv[env] || 0) + 1;
                });
                
                // Get top 3 environments
                const topEnvironments = Object.entries(nonClosedByEnv)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 3);
                
                return topEnvironments.length > 0 ? (
                  <Grid container spacing={3} sx={{ mb: 3 }}>
                    {topEnvironments.map(([env, count], index) => {
                      const colors = ['#f44336', '#2196f3', '#4caf50'];
                      return (
                        <Grid item xs={12} sm={6} md={4} key={env}>
                          <Paper 
                            elevation={2} 
                            sx={{ 
                              p: 3,
                              textAlign: 'center',
                              background: `linear-gradient(135deg, ${colors[index]}15 0%, ${colors[index]}25 100%)`,
                              border: `2px solid ${colors[index]}40`
                            }}
                          >
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                              {env}
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 'bold', color: colors[index] }}>
                              {count}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              Non-Closed Bugs
                            </Typography>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                ) : null;
              })()}

              {/* Monthly Bug Trends by Environment - Stacked Bar Chart */}
              {environmentData && environmentData.environments && environmentData.environments.length > 0 ? (
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  {/* Stacked Bar: Monthly Bug Trends by Environment */}
                  <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{ p: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Monthly Bug Trends by Environment
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        This chart shows the number of bugs created each month, stacked by environment.
                      </Typography>
                      <Box sx={{ height: 400 }}>
                        <Bar
                          data={{
                            labels: environmentData.created.map(item => item.month),
                            datasets: environmentData.environments.map((env, index) => {
                              const envColors = {
                                'Legacy': '#87CEEB',
                                'Unknown': '#D3D3D3',
                                'Legacy & Modernization': '#B0E0E6',
                                'Legacy and Modernization': '#B0E0E6',
                                'Modernization': '#98FB98'
                              };
                              const fallbackColors = ['#87CEEB', '#D3D3D3', '#B0E0E6', '#98FB98', '#FFB6C1', '#FFE4B5'];
                              const color = envColors[env] || fallbackColors[index % fallbackColors.length];
                              return {
                                label: env,
                                data: environmentData.created.map(item => item[env] || 0),
                                backgroundColor: color,
                                stack: 'stack0'
                              };
                            })
                          }}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            layout: {
                              padding: { bottom: 24 }
                            },
                            plugins: {
                              legend: {
                                position: 'top',
                                labels: {
                                  color: '#000',
                                  font: { size: 14, weight: '600' },
                                  padding: 16
                                }
                              },
                              tooltip: {
                                mode: 'index',
                                intersect: false,
                                bodyFont: { size: 14 },
                                titleFont: { size: 14 },
                                callbacks: {
                                  footer: (items) => {
                                    const total = items.reduce((sum, i) => sum + (i.parsed?.y ?? 0), 0);
                                    return `Total: ${total} bugs`;
                                  }
                                }
                              },
                              datalabels: {
                                anchor: 'center',
                                align: 'center',
                                color: '#000',
                                font: { size: 14, weight: 'bold' },
                                formatter: (value) => (value > 0 ? value : ''),
                                clip: false
                              }
                            },
                            scales: {
                              x: {
                                stacked: true,
                                grid: { display: false },
                                ticks: {
                                  maxRotation: 0,
                                  minRotation: 0,
                                  font: { size: 13 },
                                  color: '#000',
                                  autoSkip: false
                                },
                                title: {
                                  display: true,
                                  text: 'Month',
                                  font: { size: 14, weight: '600' },
                                  color: '#000'
                                }
                              },
                              y: {
                                stacked: true,
                                beginAtZero: true,
                                title: {
                                  display: true,
                                  text: 'Number of Bugs',
                                  font: { size: 14, weight: '600' },
                                  color: '#000'
                                },
                                ticks: {
                                  stepSize: 1,
                                  font: { size: 13 },
                                  color: '#000'
                                }
                              }
                            }
                          }}
                        />
                      </Box>
                    </Paper>
                  </Grid>

                  {/* Created Bugs by Environment - Line Chart */}
                  <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Created Bugs by Environment (Monthly Trend)
                        </Typography>
                        <Tooltip 
                          title="Monthly trend of production bugs created, grouped by environment over the last 12 months."
                          arrow
                          placement="top"
                        >
                          <IconButton size="small">
                            <InfoIcon fontSize="small" color="action" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Box sx={{ height: 400 }}>
                        <Line
                          data={{
                            labels: environmentData.created.map(item => item.month),
                            datasets: environmentData.environments.map((env, index) => {
                              const colors = [
                                '#f44336', '#2196f3', '#4caf50', '#ff9800', '#9c27b0',
                                '#00bcd4', '#ffeb3b', '#e91e63', '#795548', '#607d8b'
                              ];
                              return {
                                label: env,
                                data: environmentData.created.map(item => item[env] || 0),
                                borderColor: colors[index % colors.length],
                                backgroundColor: colors[index % colors.length] + '20',
                                borderWidth: 2,
                                fill: false,
                                tension: 0.4,
                                pointRadius: 4,
                                pointHoverRadius: 6
                              };
                            })
                          }}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: {
                                position: 'bottom',
                              },
                              tooltip: {
                                mode: 'index',
                                intersect: false
                              }
                            },
                            scales: {
                              y: {
                                beginAtZero: true,
                                ticks: {
                                  stepSize: 1
                                }
                              }
                            }
                          }}
                        />
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              ) : (
                <Alert severity="info" sx={{ mb: 3 }}>
                  No environment data available.
                </Alert>
              )}

              {/* Non-Closed Bugs Table with Filters */}
              {allNonClosedBugs.length > 0 && (
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Paper elevation={2} sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Non-Closed Bugs by Environment
                        </Typography>
                        <Tooltip 
                          title="List of all non-closed production bugs. Use filters to narrow down by Environment and Team."
                          arrow
                          placement="top"
                        >
                          <IconButton size="small">
                            <InfoIcon fontSize="small" color="action" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      
                      {/* Filters */}
                      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                          <InputLabel>Environment</InputLabel>
                          <Select
                            value={selectedEnvironment}
                            label="Environment"
                            onChange={(e) => setSelectedEnvironment(e.target.value)}
                          >
                            <MenuItem value="">All Environments</MenuItem>
                            {(() => {
                              const uniqueEnvironments = [...new Set(allNonClosedBugs.map(bug => bug.environment).filter(Boolean))].sort();
                              return uniqueEnvironments.map((env) => (
                                <MenuItem key={env} value={env}>
                                  {env}
                                </MenuItem>
                              ));
                            })()}
                          </Select>
                        </FormControl>
                        
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                          <InputLabel>Team</InputLabel>
                          <Select
                            value={selectedTeamEnv}
                            label="Team"
                            onChange={(e) => setSelectedTeamEnv(e.target.value)}
                          >
                            <MenuItem value="">All Teams</MenuItem>
                            {filterOptions.teams.map((team) => (
                              <MenuItem key={team} value={team}>
                                {team}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
                          <Typography variant="body2" color="text.secondary">
                            {(() => {
                              const filtered = allNonClosedBugs.filter(bug => {
                                if (selectedEnvironment && bug.environment !== selectedEnvironment) return false;
                                if (selectedTeamEnv && bug.team !== selectedTeamEnv) return false;
                                return true;
                              });
                              return `Showing ${filtered.length} of ${allNonClosedBugs.length} bugs`;
                            })()}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell 
                                sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                                onClick={() => {
                                  if (sortFieldEnv === 'id') {
                                    setSortDirectionEnv(sortDirectionEnv === 'asc' ? 'desc' : 'asc');
                                  } else {
                                    setSortFieldEnv('id');
                                    setSortDirectionEnv('asc');
                                  }
                                }}
                              >
                                <Box display="flex" alignItems="center">
                                  ID
                                  {sortFieldEnv === 'id' && (
                                    sortDirectionEnv === 'asc' ? 
                                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Environment</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>State</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Priority</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Team</TableCell>
                              <TableCell 
                                sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                                align="right"
                                onClick={() => {
                                  if (sortFieldEnv === 'age') {
                                    setSortDirectionEnv(sortDirectionEnv === 'asc' ? 'desc' : 'asc');
                                  } else {
                                    setSortFieldEnv('age');
                                    setSortDirectionEnv('desc');
                                  }
                                }}
                              >
                                <Box display="flex" alignItems="center" justifyContent="flex-end">
                                  Age (days)
                                  {sortFieldEnv === 'age' && (
                                    sortDirectionEnv === 'asc' ? 
                                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(() => {
                              const filtered = allNonClosedBugs.filter(bug => {
                                if (selectedEnvironment && bug.environment !== selectedEnvironment) return false;
                                if (selectedTeamEnv && bug.team !== selectedTeamEnv) return false;
                                return true;
                              });
                              
                              const sorted = [...filtered].sort((a, b) => {
                                let aValue = a[sortFieldEnv];
                                let bValue = b[sortFieldEnv];
                                
                                if (sortFieldEnv === 'age' || sortFieldEnv === 'id') {
                                  aValue = Number(aValue) || 0;
                                  bValue = Number(bValue) || 0;
                                  return sortDirectionEnv === 'asc' ? aValue - bValue : bValue - aValue;
                                }
                                
                                aValue = String(aValue || '').toLowerCase();
                                bValue = String(bValue || '').toLowerCase();
                                return sortDirectionEnv === 'asc' 
                                  ? aValue.localeCompare(bValue) 
                                  : bValue.localeCompare(aValue);
                              });
                              
                              return sorted.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={7} align="center">
                                    <Typography variant="body2" color="text.secondary">
                                      No bugs found matching the selected filters.
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ) : (
                                sorted.map((bug) => {
                                  const adoUrl = `https://tr-legal.visualstudio.com/HighQ%20Tech/_workitems/edit/${bug.id}`;
                                  const getAgeColor = (age) => {
                                    if (age > 90) return '#f57c00';
                                    if (age > 30) return '#fbc02d';
                                    return '#388e3c';
                                  };
                                  const ageColor = getAgeColor(bug.age);
                                  
                                  return (
                                    <TableRow key={bug.id} hover>
                                      <TableCell>
                                        <Link
                                          href={adoUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          underline="hover"
                                          sx={{ fontWeight: 'medium', color: 'primary.main' }}
                                        >
                                          #{bug.id}
                                        </Link>
                                      </TableCell>
                                      <TableCell sx={{ maxWidth: 400, wordWrap: 'break-word' }}>
                                        <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                          {bug.title || 'Untitled'}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Chip
                                          label={bug.environment || 'Unknown'}
                                          size="small"
                                          variant="outlined"
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Chip
                                          label={bug.state || 'Unknown'}
                                          size="small"
                                          variant="outlined"
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Chip
                                          label={bug.priority || 'Unknown'}
                                          size="small"
                                          variant="outlined"
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="body2" color="text.secondary">
                                          {bug.team || 'Unknown'}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Chip
                                          label={`${bug.age} days`}
                                          size="small"
                                          sx={{
                                            backgroundColor: ageColor,
                                            color: 'white',
                                            fontWeight: 'bold'
                                          }}
                                        />
                                      </TableCell>
                                    </TableRow>
                                  );
                                })
                              );
                            })()}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  </Grid>
                </Grid>
              )}
            </>
          )}
        </Box>
      )}

      {/* Analysis Tab */}
      {activeTab === 5 && (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            Production Bugs Analysis
          </Typography>
          
          {analysisLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {analysisData && analysisData.length > 0 && (
                <>
                  {/* Priority Filter */}
                  <Box sx={{ mb: 3 }}>
                    <FormControl size="small" sx={{ minWidth: 250 }}>
                      <InputLabel>Priority</InputLabel>
                      <Select
                        multiple
                        value={selectedPriorities}
                        label="Priority"
                        onChange={(e) => setSelectedPriorities(e.target.value)}
                        renderValue={(selected) => {
                          if (selected.length === 0) return 'All Priorities';
                          if (selected.length === 1) return selected[0];
                          return `${selected.length} priorities selected`;
                        }}
                      >
                        {['P1', 'P2', 'P3', 'P4'].map((priority) => (
                          <MenuItem key={priority} value={priority}>
                            <Checkbox checked={selectedPriorities.indexOf(priority) > -1} />
                            {priority}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  
                  {/* Cycle Time Chart */}
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <BugCycleTimeChart 
                        data={cycleTimeData}
                        loading={cycleTimeLoading}
                        error={cycleTimeError}
                      />
                    </Grid>
                  </Grid>
                  
                  {/* Charts Section - Moved to Top */}
                  {(() => {
                    // Calculate monthly averages for resolution time and cycle time
                    const monthlyData = {};
                    
                    // Get filtered data for charts
                    let chartData = analysisData;
                    
                    // Filter by priority if priorities are selected
                    if (selectedPriorities.length > 0) {
                      chartData = chartData.filter(bug => {
                        const bugPriority = bug.priority || '';
                        return selectedPriorities.includes(bugPriority);
                      });
                    }
                    
                    if (showValidOnly) {
                      chartData = chartData.filter(bug => {
                        const fixVersion = bug.fixVersion || bug.fixVersionDummy || '';
                        const { collabVersions } = categorizeFixVersion(fixVersion);
                        const releaseDates = collabVersions
                          .map(version => {
                            const normalizedVersion = normalizeVersion(version);
                            return fixVersionsMap[version] || 
                                   fixVersionsMap[normalizedVersion] ||
                                   staticReleaseDates[version] ||
                                   staticReleaseDates[normalizedVersion];
                          })
                          .filter(Boolean);
                        if (releaseDates.length === 0) return false;
                        if (bug.closedDate && releaseDates.length > 0) {
                          const closedDate = new Date(bug.closedDate);
                          const releaseDate = new Date(releaseDates[0]);
                          const diffTime = releaseDate - closedDate;
                          const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
                          if (diffDays < 0) return false;
                        }
                        return true;
                      });
                    }
                    
                    chartData.forEach(bug => {
                      if (!bug.closedDate) return;
                      
                      const closedDate = new Date(bug.closedDate);
                      const monthKey = `${closedDate.getFullYear()}-${String(closedDate.getMonth() + 1).padStart(2, '0')}`;
                      const monthLabel = closedDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
                      
                      if (!monthlyData[monthKey]) {
                        monthlyData[monthKey] = {
                          label: monthLabel,
                          resolutionTimes: [],
                          cycleTimes: [],
                          assignedToSet: new Set(),
                          teamCounts: {}
                        };
                      }
                      
                      // Calculate resolution time (closed date - created date)
                      if (bug.createdDate) {
                        const createdDate = new Date(bug.createdDate);
                        const resolutionDays = Math.round((closedDate - createdDate) / (1000 * 60 * 60 * 24));
                        monthlyData[monthKey].resolutionTimes.push(resolutionDays);
                      }
                      
                      // Track assigned to engineers
                      if (bug.assignedTo) {
                        monthlyData[monthKey].assignedToSet.add(bug.assignedTo);
                      }
                      
                      // Track team counts
                      const team = bug.team || 'Unknown';
                      monthlyData[monthKey].teamCounts[team] = (monthlyData[monthKey].teamCounts[team] || 0) + 1;
                      
                      // Calculate cycle time (release date - closed date)
                      const fixVersion = bug.fixVersion || bug.fixVersionDummy || '';
                      const { collabVersions } = categorizeFixVersion(fixVersion);
                      const releaseDates = collabVersions
                        .map(version => {
                          const normalizedVersion = normalizeVersion(version);
                          return fixVersionsMap[version] || 
                                 fixVersionsMap[normalizedVersion] ||
                                 staticReleaseDates[version] ||
                                 staticReleaseDates[normalizedVersion];
                        })
                        .filter(Boolean);
                      
                      if (releaseDates.length > 0) {
                        const releaseDate = new Date(releaseDates[0]);
                        const cycleDays = Math.round((releaseDate - closedDate) / (1000 * 60 * 60 * 24));
                        if (cycleDays >= 0) {
                          monthlyData[monthKey].cycleTimes.push(cycleDays);
                        }
                      }
                    });
                    
                    // Sort by month key and calculate averages
                    const sortedMonths = Object.keys(monthlyData).sort();
                    const chartLabels = sortedMonths.map(key => monthlyData[key].label);
                    const avgResolutionTimes = sortedMonths.map(key => {
                      const times = monthlyData[key].resolutionTimes;
                      return times.length > 0 ? times.reduce((a, b) => a + b, 0) / times.length : 0;
                    });
                    const avgCycleTimes = sortedMonths.map(key => {
                      const times = monthlyData[key].cycleTimes;
                      return times.length > 0 ? times.reduce((a, b) => a + b, 0) / times.length : 0;
                    });
                    
                    // Calculate trend lines (simple linear regression)
                    const calculateTrendLine = (values) => {
                      const n = values.length;
                      if (n < 2) return values;
                      
                      const x = Array.from({ length: n }, (_, i) => i);
                      const sumX = x.reduce((a, b) => a + b, 0);
                      const sumY = values.reduce((a, b) => a + b, 0);
                      const sumXY = x.reduce((sum, xi, i) => sum + xi * values[i], 0);
                      const sumX2 = x.reduce((sum, xi) => sum + xi * xi, 0);
                      
                      const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
                      const intercept = (sumY - slope * sumX) / n;
                      
                      return x.map(xi => slope * xi + intercept);
                    };
                    
                    // Get counts for each month
                    const resolutionCounts = sortedMonths.map(key => monthlyData[key].resolutionTimes.length);
                    const cycleCounts = sortedMonths.map(key => monthlyData[key].cycleTimes.length);
                    
                    // Calculate unique assigned to engineers per month
                    const uniqueEngineers = sortedMonths.map(key => monthlyData[key].assignedToSet.size);
                    
                    // Helper function to convert month key (YYYY-MM) to quarter key (YYYY-Q1, YYYY-Q2, etc.)
                    const getQuarterKey = (monthKey) => {
                      const [year, month] = monthKey.split('-').map(Number);
                      const quarter = Math.floor((month - 1) / 3) + 1;
                      return `${year}-Q${quarter}`;
                    };
                    
                    // Helper function to format quarter label
                    const formatQuarterLabel = (quarterKey) => {
                      const [year, quarter] = quarterKey.split('-Q');
                      return `${year} Q${quarter}`;
                    };
                    
                    // Build pivot table data grouped by quarters (Quarter, Team, Count)
                    const quarterlyData = {};
                    sortedMonths.forEach(key => {
                      const quarterKey = getQuarterKey(key);
                      const teamCounts = monthlyData[key].teamCounts;
                      
                      if (!quarterlyData[quarterKey]) {
                        quarterlyData[quarterKey] = {};
                      }
                      
                      Object.entries(teamCounts).forEach(([team, count]) => {
                        if (!quarterlyData[quarterKey][team]) {
                          quarterlyData[quarterKey][team] = 0;
                        }
                        quarterlyData[quarterKey][team] += count;
                      });
                    });
                    
                    // Get sorted quarters and unique teams
                    const sortedQuarters = Object.keys(quarterlyData).sort();
                    const allTeams = [...new Set(
                      Object.values(quarterlyData).flatMap(q => Object.keys(q))
                    )];
                    
                    // Create team to stream mapping from enabled teamsData
                    // teamsData already contains only enabled teams (enabled: true)
                    const teamToStreamMap = {};
                    const enabledTeamSet = new Set();
                    const teamNameVariations = {}; // Map to store all variations of team names
                    
                    console.log('Teams data for mapping:', teamsData);
                    console.log('All teams from bug data:', allTeams);
                    
                    // Filter teamsData to only include enabled teams (double-check)
                    const enabledTeamsOnly = teamsData.filter(team => team.enabled === true);
                    console.log('Enabled teams only (after frontend filter):', enabledTeamsOnly);
                    
                    enabledTeamsOnly.forEach(team => {
                      // Get all possible team name variations
                      const teamId = (team.id || '').trim();
                      const displayName = (team.displayName || '').trim();
                      const teamName = team.name || '';
                      const stream = (team.stream || '').trim();
                      
                      console.log(`Processing enabled team: id="${teamId}", displayName="${displayName}", stream="${stream}"`);
                      
                      // Add all variations to the set and map
                      const variations = [teamId, displayName, teamName].filter(v => v);
                      variations.forEach(variation => {
                        if (variation) {
                          teamToStreamMap[variation] = stream;
                          enabledTeamSet.add(variation);
                          // Store variations for fuzzy matching
                          teamNameVariations[variation.toLowerCase()] = { original: variation, stream };
                          console.log(`  Added variation: "${variation}" -> stream: "${stream}"`);
                        }
                      });
                    });
                    
                    console.log('Enabled team set:', Array.from(enabledTeamSet));
                    console.log('Team to stream map:', teamToStreamMap);
                    console.log('Team name variations (lowercase):', Object.keys(teamNameVariations));
                    
                    // Helper function to find matching enabled team (fuzzy matching)
                    const findMatchingTeam = (teamName) => {
                      const normalized = (teamName || '').trim();
                      if (!normalized) {
                        console.log(`  findMatchingTeam: "${teamName}" -> null (empty after trim)`);
                        return null;
                      }
                      
                      console.log(`  findMatchingTeam: Looking for "${normalized}"`);
                      
                      // Try exact match first
                      if (enabledTeamSet.has(normalized)) {
                        console.log(`  findMatchingTeam: Found exact match for "${normalized}"`);
                        return { name: normalized, stream: teamToStreamMap[normalized] || 'Unassigned' };
                      }
                      
                      // Try case-insensitive match
                      const lowerNormalized = normalized.toLowerCase();
                      if (teamNameVariations[lowerNormalized]) {
                        const match = teamNameVariations[lowerNormalized];
                        console.log(`  findMatchingTeam: Found case-insensitive match for "${normalized}" -> "${match.original}"`);
                        return { name: match.original, stream: match.stream || 'Unassigned' };
                      }
                      
                      // Try removing common suffixes like " Team" or "Team"
                      const normalizedWithoutSuffix = normalized.replace(/\s+[Tt]eam\s*$/, '').trim();
                      if (normalizedWithoutSuffix !== normalized) {
                        if (enabledTeamSet.has(normalizedWithoutSuffix)) {
                          return { name: normalizedWithoutSuffix, stream: teamToStreamMap[normalizedWithoutSuffix] || 'Unassigned' };
                        }
                        const lowerWithoutSuffix = normalizedWithoutSuffix.toLowerCase();
                        if (teamNameVariations[lowerWithoutSuffix]) {
                          const match = teamNameVariations[lowerWithoutSuffix];
                          return { name: match.original, stream: match.stream || 'Unassigned' };
                        }
                      }
                      
                      // Try partial matches only if one is a substring of the other (more conservative)
                      for (const [key, value] of Object.entries(teamNameVariations)) {
                        // Only match if one fully contains the other (not just partial overlap)
                        if (key === lowerNormalized || lowerNormalized === key) {
                          return { name: value.original, stream: value.stream || 'Unassigned' };
                        }
                        // Check if normalized starts with key or key starts with normalized (for cases like "HQ-Core-SQ1" vs "HQ-Core-SQ1 Team")
                        if (lowerNormalized.startsWith(key) || key.startsWith(lowerNormalized)) {
                          // Only if the difference is just a suffix like " Team"
                          const diff = lowerNormalized.length > key.length 
                            ? lowerNormalized.substring(key.length).trim()
                            : key.substring(lowerNormalized.length).trim();
                          if (diff === '' || diff.toLowerCase() === 'team') {
                            return { name: value.original, stream: value.stream || 'Unassigned' };
                          }
                        }
                      }
                      
                      return null;
                    };
                    
                    // Calculate totals for each team
                    const teamsWithTotals = allTeams.map(team => {
                      const teamTotal = sortedQuarters.reduce((sum, quarterKey) => {
                        return sum + (quarterlyData[quarterKey][team] || 0);
                      }, 0);
                      
                      // Try to find matching enabled team
                      console.log(`\nChecking team from bug data: "${team}"`);
                      const match = findMatchingTeam(team);
                      const isEnabled = match !== null;
                      const stream = isEnabled ? (match.stream || 'Unassigned') : 'Others';
                      
                      if (!isEnabled) {
                        console.log(`  ❌ Team "${team}" not found in enabled teams, going to Others`);
                        console.log(`  Available enabled team names:`, Array.from(enabledTeamSet));
                      } else {
                        console.log(`  ✅ Team "${team}" matched to "${match.name}" in stream "${stream}"`);
                      }
                      
                      return { team, total: teamTotal, stream, isEnabled };
                    });
                    
                    // Sort teams directly (no stream grouping)
                    const sortedTeams = [...teamsWithTotals].sort((a, b) => {
                      if (sortFieldPivot === 'total') {
                        return sortDirectionPivot === 'asc' 
                          ? a.total - b.total 
                          : b.total - a.total;
                      } else if (sortFieldPivot === 'team') {
                        return sortDirectionPivot === 'asc'
                          ? a.team.localeCompare(b.team)
                          : b.team.localeCompare(a.team);
                      }
                      return 0;
                    });
                    
                    // Handler for pivot table sorting
                    const handlePivotSort = (field) => {
                      if (sortFieldPivot === field) {
                        setSortDirectionPivot(sortDirectionPivot === 'asc' ? 'desc' : 'asc');
                      } else {
                        setSortFieldPivot(field);
                        setSortDirectionPivot('desc');
                      }
                    };
                    
                    const resolutionTrend = calculateTrendLine(avgResolutionTimes);
                    const cycleTrend = calculateTrendLine(avgCycleTimes);
                    
                    // Calculate total bug counts per month (closed bugs)
                    const totalBugCounts = sortedMonths.map(key => {
                      const resolutionCount = monthlyData[key].resolutionTimes.length;
                      const cycleCount = monthlyData[key].cycleTimes.length;
                      // Use the maximum of the two counts (they should be similar, but use max to be safe)
                      return Math.max(resolutionCount, cycleCount);
                    });
                    const bugCountTrend = calculateTrendLine(totalBugCounts);
                    
                    // Calculate bugs created per month
                    const createdMonthlyData = {};
                    chartData.forEach(bug => {
                      if (!bug.createdDate) return;
                      
                      const createdDate = new Date(bug.createdDate);
                      const monthKey = `${createdDate.getFullYear()}-${String(createdDate.getMonth() + 1).padStart(2, '0')}`;
                      const monthLabel = createdDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
                      
                      if (!createdMonthlyData[monthKey]) {
                        createdMonthlyData[monthKey] = {
                          label: monthLabel,
                          count: 0
                        };
                      }
                      createdMonthlyData[monthKey].count++;
                    });
                    
                    // Sort created months and align with closed months for consistent display
                    const allMonthKeys = new Set([...sortedMonths, ...Object.keys(createdMonthlyData)]);
                    const sortedAllMonths = Array.from(allMonthKeys).sort();
                    const createdBugCounts = sortedAllMonths.map(key => {
                      return createdMonthlyData[key] ? createdMonthlyData[key].count : 0;
                    });
                    const createdBugCountTrend = calculateTrendLine(createdBugCounts);
                    const createdChartLabels = sortedAllMonths.map(key => {
                      if (createdMonthlyData[key]) {
                        return createdMonthlyData[key].label;
                      }
                      // Fallback to closed month label if exists
                      if (monthlyData[key]) {
                        return monthlyData[key].label;
                      }
                      const date = new Date(key + '-01');
                      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
                    });
                    
                    
                    return (
                      <>
                        <Grid container spacing={3} sx={{ mb: 3 }}>
                          {/* Average Resolution Time Chart */}
                          <Grid item xs={12} md={6}>
                            <Paper elevation={2} sx={{ p: 3 }}>
                              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                Average Resolution Time (Days)
                              </Typography>
                              <Box sx={{ height: 400 }}>
                                <Bar
                                  data={{
                                    labels: chartLabels,
                                    datasets: [
                                      {
                                        label: 'Average Resolution Time',
                                        data: avgResolutionTimes,
                                        backgroundColor: 'rgba(54, 162, 235, 0.7)',
                                        borderColor: 'rgba(54, 162, 235, 1)',
                                        borderWidth: 2
                                      },
                                      {
                                        label: 'Trend Line',
                                        data: resolutionTrend,
                                        type: 'line',
                                        borderColor: 'rgba(255, 99, 132, 1)',
                                        borderWidth: 3,
                                        borderDash: [5, 5],
                                        fill: false,
                                        pointRadius: 4,
                                        pointHoverRadius: 6,
                                        pointBackgroundColor: 'rgba(255, 99, 132, 1)',
                                        tension: 0.4
                                      }
                                    ]
                                  }}
                                  options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: {
                                      legend: {
                                        position: 'bottom',
                                        labels: {
                                          usePointStyle: true,
                                          padding: 15,
                                          font: {
                                            size: 12
                                          }
                                        }
                                      },
                                      tooltip: {
                                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                        padding: 12,
                                        titleFont: {
                                          size: 14,
                                          weight: 'bold'
                                        },
                                        bodyFont: {
                                          size: 13
                                        },
                                        callbacks: {
                                          title: function(context) {
                                            return context[0].label;
                                          },
                                          label: function(context) {
                                            if (!context.parsed) return '';
                                            const value = Math.round(context.parsed.y);
                                            const index = context.dataIndex;
                                            if (context.datasetIndex === 0) {
                                              const count = resolutionCounts[index];
                                              return [
                                                `Average Resolution Time: ${value} days`,
                                                `Number of bugs: ${count}`
                                              ];
                                            } else {
                                              return `Trend: ${value} days`;
                                            }
                                          }
                                        }
                                      },
                                      datalabels: {
                                        anchor: 'end',
                                        align: 'top',
                                        color: '#333',
                                        font: {
                                          weight: 'bold',
                                          size: 11
                                        },
                                        formatter: function(value, context) {
                                          if (context && context.datasetIndex === 0 && value > 0) {
                                            return Math.round(value).toString();
                                          }
                                          return '';
                                        },
                                        display: function(context) {
                                          return context && context.datasetIndex === 0 && context.parsed && context.parsed.y > 0;
                                        }
                                      }
                                    },
                                    scales: {
                                      x: {
                                        ticks: {
                                          maxRotation: 45,
                                          minRotation: 45,
                                          font: {
                                            size: 10
                                          }
                                        },
                                        grid: {
                                          display: false
                                        }
                                      },
                                      y: {
                                        beginAtZero: true,
                                        title: {
                                          display: true,
                                          text: 'Days',
                                          font: {
                                            size: 12,
                                            weight: 'bold'
                                          }
                                        },
                                        ticks: {
                                          font: {
                                            size: 11
                                          },
                                          precision: 0
                                        },
                                        grid: {
                                          color: 'rgba(0, 0, 0, 0.1)'
                                        }
                                      }
                                    }
                                  }}
                                />
                              </Box>
                            </Paper>
                          </Grid>
                          
                          {/* Average Deployment Time Chart */}
                          <Grid item xs={12} md={6}>
                            <Paper elevation={2} sx={{ p: 3 }}>
                              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                Average Deployment Time (Days)
                              </Typography>
                              <Box sx={{ height: 400 }}>
                                <Bar
                                  data={{
                                    labels: chartLabels,
                                    datasets: [
                                      {
                                        label: 'Average Deployment Time',
                                        data: avgCycleTimes,
                                        backgroundColor: 'rgba(75, 192, 192, 0.7)',
                                        borderColor: 'rgba(75, 192, 192, 1)',
                                        borderWidth: 2
                                      },
                                      {
                                        label: 'Trend Line',
                                        data: cycleTrend,
                                        type: 'line',
                                        borderColor: 'rgba(255, 99, 132, 1)',
                                        borderWidth: 3,
                                        borderDash: [5, 5],
                                        fill: false,
                                        pointRadius: 4,
                                        pointHoverRadius: 6,
                                        pointBackgroundColor: 'rgba(255, 99, 132, 1)',
                                        tension: 0.4
                                      }
                                    ]
                                  }}
                                  options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: {
                                      legend: {
                                        position: 'bottom',
                                        labels: {
                                          usePointStyle: true,
                                          padding: 15,
                                          font: {
                                            size: 12
                                          }
                                        }
                                      },
                                      tooltip: {
                                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                        padding: 12,
                                        titleFont: {
                                          size: 14,
                                          weight: 'bold'
                                        },
                                        bodyFont: {
                                          size: 13
                                        },
                                        callbacks: {
                                          title: function(context) {
                                            return context[0].label;
                                          },
                                          label: function(context) {
                                            if (!context.parsed) return '';
                                            const value = Math.round(context.parsed.y);
                                            const index = context.dataIndex;
                                            if (context.datasetIndex === 0) {
                                              const count = cycleCounts[index];
                                              return [
                                                `Average Deployment Time: ${value} days`,
                                                `Number of bugs: ${count}`
                                              ];
                                            } else {
                                              return `Trend: ${value} days`;
                                            }
                                          }
                                        }
                                      },
                                      datalabels: {
                                        anchor: 'end',
                                        align: 'top',
                                        color: '#333',
                                        font: {
                                          weight: 'bold',
                                          size: 11
                                        },
                                        formatter: function(value, context) {
                                          if (context && context.datasetIndex === 0 && value > 0) {
                                            return Math.round(value).toString();
                                          }
                                          return '';
                                        },
                                        display: function(context) {
                                          return context && context.datasetIndex === 0 && context.parsed && context.parsed.y > 0;
                                        }
                                      }
                                    },
                                    scales: {
                                      x: {
                                        ticks: {
                                          maxRotation: 45,
                                          minRotation: 45,
                                          font: {
                                            size: 10
                                          }
                                        },
                                        grid: {
                                          display: false
                                        }
                                      },
                                      y: {
                                        beginAtZero: true,
                                        title: {
                                          display: true,
                                          text: 'Days',
                                          font: {
                                            size: 12,
                                            weight: 'bold'
                                          }
                                        },
                                        ticks: {
                                          font: {
                                            size: 11
                                          },
                                          precision: 0
                                        },
                                        grid: {
                                          color: 'rgba(0, 0, 0, 0.1)'
                                        }
                                      }
                                    }
                                  }}
                                />
                              </Box>
                            </Paper>
                          </Grid>
                          
                          {/* Number of Bugs Closed per Month Chart */}
                          <Grid item xs={12} md={6}>
                            <Paper elevation={2} sx={{ p: 3 }}>
                              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                Number of Bugs Closed per Month
                              </Typography>
                              <Box sx={{ height: 400 }}>
                                <Bar
                                  data={{
                                    labels: chartLabels,
                                    datasets: [
                                      {
                                        label: 'Number of Bugs',
                                        data: totalBugCounts,
                                        backgroundColor: 'rgba(153, 102, 255, 0.7)',
                                        borderColor: 'rgba(153, 102, 255, 1)',
                                        borderWidth: 2
                                      },
                                      {
                                        label: 'Trend Line',
                                        data: bugCountTrend,
                                        type: 'line',
                                        borderColor: 'rgba(255, 99, 132, 1)',
                                        borderWidth: 3,
                                        borderDash: [5, 5],
                                        fill: false,
                                        pointRadius: 4,
                                        pointHoverRadius: 6,
                                        pointBackgroundColor: 'rgba(255, 99, 132, 1)',
                                        tension: 0.4
                                      }
                                    ]
                                  }}
                                  options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: {
                                      legend: {
                                        position: 'bottom',
                                        labels: {
                                          usePointStyle: true,
                                          padding: 15,
                                          font: {
                                            size: 12
                                          }
                                        }
                                      },
                                      tooltip: {
                                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                        padding: 12,
                                        titleFont: {
                                          size: 14,
                                          weight: 'bold'
                                        },
                                        bodyFont: {
                                          size: 13
                                        },
                                      callbacks: {
                                        title: function(context) {
                                          return context[0].label;
                                        },
                                        label: function(context) {
                                          if (!context.parsed) return '';
                                          const value = Math.round(context.parsed.y);
                                          if (context.datasetIndex === 0) {
                                            return `Number of Bugs: ${value}`;
                                          } else {
                                            return `Trend: ${value}`;
                                          }
                                        }
                                      }
                                    },
                                    datalabels: {
                                      anchor: 'end',
                                      align: 'top',
                                      color: '#333',
                                      font: {
                                        weight: 'bold',
                                        size: 11
                                      },
                                      formatter: function(value, context) {
                                        if (context && context.datasetIndex === 0 && value > 0) {
                                          return Math.round(value).toString();
                                        }
                                        return '';
                                      },
                                      display: function(context) {
                                        return context && context.datasetIndex === 0 && context.parsed && context.parsed.y > 0;
                                      }
                                    }
                                  },
                                  scales: {
                                    x: {
                                      ticks: {
                                        maxRotation: 45,
                                        minRotation: 45,
                                        font: {
                                          size: 10
                                        }
                                      },
                                      grid: {
                                        display: false
                                      }
                                    },
                                    y: {
                                      beginAtZero: true,
                                      title: {
                                        display: true,
                                        text: 'Number of Bugs',
                                        font: {
                                          size: 12,
                                          weight: 'bold'
                                        }
                                      },
                                      ticks: {
                                        font: {
                                          size: 11
                                        },
                                        precision: 0,
                                        stepSize: 1
                                      },
                                      grid: {
                                        color: 'rgba(0, 0, 0, 0.1)'
                                      }
                                    }
                                  }
                                }}
                              />
                            </Box>
                          </Paper>
                        </Grid>
                        
                        {/* Number of Bugs Created per Month Chart */}
                        <Grid item xs={12} md={6}>
                          <Paper elevation={2} sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                              Number of Bugs Created per Month
                            </Typography>
                            <Box sx={{ height: 400 }}>
                              <Bar
                                data={{
                                  labels: createdChartLabels,
                                  datasets: [
                                    {
                                      label: 'Number of Bugs Created',
                                      data: createdBugCounts,
                                      backgroundColor: 'rgba(255, 159, 64, 0.7)',
                                      borderColor: 'rgba(255, 159, 64, 1)',
                                      borderWidth: 2
                                    },
                                    {
                                      label: 'Trend Line',
                                      data: createdBugCountTrend,
                                      type: 'line',
                                      borderColor: 'rgba(255, 99, 132, 1)',
                                      borderWidth: 3,
                                      borderDash: [5, 5],
                                      fill: false,
                                      pointRadius: 4,
                                      pointHoverRadius: 6,
                                      pointBackgroundColor: 'rgba(255, 99, 132, 1)',
                                      tension: 0.4
                                    }
                                  ]
                                }}
                                options={{
                                  responsive: true,
                                  maintainAspectRatio: false,
                                  plugins: {
                                    legend: {
                                      position: 'bottom',
                                      labels: {
                                        usePointStyle: true,
                                        padding: 15,
                                        font: {
                                          size: 12
                                        }
                                      }
                                    },
                                    tooltip: {
                                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                      padding: 12,
                                      titleFont: {
                                        size: 14,
                                        weight: 'bold'
                                      },
                                      bodyFont: {
                                        size: 13
                                      },
                                      callbacks: {
                                        title: function(context) {
                                          return context[0].label;
                                        },
                                        label: function(context) {
                                          if (!context.parsed) return '';
                                          const value = Math.round(context.parsed.y);
                                          if (context.datasetIndex === 0) {
                                            return `Number of Bugs Created: ${value}`;
                                          } else {
                                            return `Trend: ${value}`;
                                          }
                                        }
                                      }
                                    },
                                    datalabels: {
                                      anchor: 'end',
                                      align: 'top',
                                      color: '#333',
                                      font: {
                                        weight: 'bold',
                                        size: 11
                                      },
                                      formatter: function(value, context) {
                                        if (context && context.datasetIndex === 0 && value > 0) {
                                          return Math.round(value).toString();
                                        }
                                        return '';
                                      },
                                      display: function(context) {
                                        return context && context.datasetIndex === 0 && context.parsed && context.parsed.y > 0;
                                      }
                                    }
                                  },
                                  scales: {
                                    x: {
                                      ticks: {
                                        maxRotation: 45,
                                        minRotation: 45,
                                        font: {
                                          size: 10
                                        }
                                      },
                                      grid: {
                                        display: false
                                      }
                                    },
                                    y: {
                                      beginAtZero: true,
                                      title: {
                                        display: true,
                                        text: 'Number of Bugs',
                                        font: {
                                          size: 12,
                                          weight: 'bold'
                                        }
                                      },
                                      ticks: {
                                        font: {
                                          size: 11
                                        },
                                        precision: 0,
                                        stepSize: 1
                                      },
                                      grid: {
                                        color: 'rgba(0, 0, 0, 0.1)'
                                      }
                                    }
                                  }
                                }}
                              />
                            </Box>
                          </Paper>
                        </Grid>
                        
                        {/* Unique Engineers per Month Chart */}
                        <Grid item xs={12} md={6}>
                          <Paper elevation={2} sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                              Unique Engineers Assigned per Month
                            </Typography>
                            <Box sx={{ height: 400 }}>
                              <Bar
                                data={{
                                  labels: chartLabels,
                                  datasets: [
                                    {
                                      label: 'Unique Engineers',
                                      data: uniqueEngineers,
                                      backgroundColor: 'rgba(201, 203, 207, 0.7)',
                                      borderColor: 'rgba(201, 203, 207, 1)',
                                      borderWidth: 2
                                    },
                                  ]
                                }}
                                options={{
                                  responsive: true,
                                  maintainAspectRatio: false,
                                  plugins: {
                                    legend: {
                                      position: 'bottom',
                                      labels: {
                                        usePointStyle: true,
                                        padding: 15,
                                        font: {
                                          size: 12
                                        }
                                      }
                                    },
                                    tooltip: {
                                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                      padding: 12,
                                      titleFont: {
                                        size: 14,
                                        weight: 'bold'
                                      },
                                      bodyFont: {
                                        size: 13
                                      },
                                      callbacks: {
                                        title: function(context) {
                                          return context[0].label;
                                        },
                                        label: function(context) {
                                          if (!context.parsed) return '';
                                          const value = Math.round(context.parsed.y);
                                          return `Unique Engineers: ${value}`;
                                        }
                                      }
                                    },
                                    datalabels: {
                                      anchor: 'end',
                                      align: 'top',
                                      color: '#333',
                                      font: {
                                        weight: 'bold',
                                        size: 11
                                      },
                                      formatter: function(value, context) {
                                        if (context && context.datasetIndex === 0 && value > 0) {
                                          return Math.round(value).toString();
                                        }
                                        return '';
                                      },
                                      display: function(context) {
                                        return context && context.datasetIndex === 0 && context.parsed && context.parsed.y > 0;
                                      }
                                    }
                                  },
                                  scales: {
                                    x: {
                                      ticks: {
                                        maxRotation: 45,
                                        minRotation: 45,
                                        font: {
                                          size: 10
                                        }
                                      },
                                      grid: {
                                        display: false
                                      }
                                    },
                                    y: {
                                      beginAtZero: true,
                                      title: {
                                        display: true,
                                        text: 'Number of Engineers',
                                        font: {
                                          size: 12,
                                          weight: 'bold'
                                        }
                                      },
                                      ticks: {
                                        font: {
                                          size: 11
                                        },
                                        precision: 0,
                                        stepSize: 1
                                      },
                                      grid: {
                                        color: 'rgba(0, 0, 0, 0.1)'
                                      }
                                    }
                                  }
                                }}
                              />
                            </Box>
                          </Paper>
                        </Grid>
                        
                        {/* Pivot Table: Team (rows), Quarter (columns), Count */}
                        <Grid item xs={12}>
                          <Paper elevation={2} sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                              Bugs Closed by Team and Quarter
                            </Typography>
                            <TableContainer>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell 
                                      sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                                      onClick={() => handlePivotSort('team')}
                                    >
                                      <Box display="flex" alignItems="center">
                                        Team
                                        {sortFieldPivot === 'team' && (
                                          sortDirectionPivot === 'asc' ? 
                                            <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                                            <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                                        )}
                                      </Box>
                                    </TableCell>
                                    {sortedQuarters.map(quarterKey => (
                                      <TableCell key={quarterKey} align="right" sx={{ fontWeight: 600 }}>
                                        {formatQuarterLabel(quarterKey)}
                                      </TableCell>
                                    ))}
                                    <TableCell 
                                      align="right" 
                                      sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                                      onClick={() => handlePivotSort('total')}
                                    >
                                      <Box display="flex" alignItems="center" justifyContent="flex-end">
                                        Total
                                        {sortFieldPivot === 'total' && (
                                          sortDirectionPivot === 'asc' ? 
                                            <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                                            <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                                        )}
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {sortedTeams.map(({ team, total }) => (
                                    <TableRow key={team}>
                                      <TableCell sx={{ fontWeight: 600 }}>{team}</TableCell>
                                      {sortedQuarters.map(quarterKey => {
                                        const count = quarterlyData[quarterKey][team] || 0;
                                        return (
                                          <TableCell key={quarterKey} align="right">
                                            {count > 0 ? count : '-'}
                                          </TableCell>
                                        );
                                      })}
                                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                                        {total}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                  {/* Total row */}
                                  <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.05)' }}>
                                    <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                                    {sortedQuarters.map(quarterKey => {
                                      const quarterTotal = Object.values(quarterlyData[quarterKey] || {}).reduce((sum, count) => sum + count, 0);
                                      return (
                                        <TableCell key={quarterKey} align="right" sx={{ fontWeight: 600 }}>
                                          {quarterTotal}
                                        </TableCell>
                                      );
                                    })}
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                      {sortedQuarters.reduce((sum, quarterKey) => {
                                        return sum + Object.values(quarterlyData[quarterKey] || {}).reduce((s, c) => s + c, 0);
                                      }, 0)}
                                    </TableCell>
                                  </TableRow>
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Paper>
                        </Grid>
                      </Grid>

                      {/* Data Table Section - Moved to Bottom */}
                      <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Production Bugs with Fix Version
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip 
                          title="All production bugs that have a fix version assigned. Shows ADO ID, Priority, Title, Team, Created Date, Closed Date, Age, and Fix Version."
                          arrow
                          placement="top"
                        >
                          <IconButton size="small">
                            <InfoIcon fontSize="small" color="action" />
                          </IconButton>
                        </Tooltip>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<DownloadIcon />}
                          onClick={() => {
                          // Prepare CSV data
                          const headers = ['ADO ID', 'Priority', 'Title', 'Team', 'Created Date', 'Closed Date', 'Age (Days)', 'Collab Version', 'MS Version', 'Release Date', 'Cycle Time (Days)'];
                          
                          // Apply same filtering logic as table display
                          let dataToExport = analysisData;
                          
                          // Filter by priority if priorities are selected
                          if (selectedPriorities.length > 0) {
                            dataToExport = dataToExport.filter(bug => {
                              const bugPriority = bug.priority || '';
                              return selectedPriorities.includes(bugPriority);
                            });
                          }
                          
                          if (showValidOnly) {
                            dataToExport = dataToExport.filter(bug => {
                              const fixVersion = bug.fixVersion || bug.fixVersionDummy || '';
                              const { collabVersions } = categorizeFixVersion(fixVersion);
                              
                              const releaseDates = collabVersions
                                .map(version => {
                                  const normalizedVersion = normalizeVersion(version);
                                  return fixVersionsMap[version] || 
                                         fixVersionsMap[normalizedVersion] ||
                                         staticReleaseDates[version] ||
                                         staticReleaseDates[normalizedVersion];
                                })
                                .filter(Boolean);
                              
                              if (releaseDates.length === 0) {
                                return false;
                              }
                              
                              if (bug.closedDate && releaseDates.length > 0) {
                                const closedDate = new Date(bug.closedDate);
                                const releaseDate = new Date(releaseDates[0]);
                                const diffTime = releaseDate - closedDate;
                                const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
                                
                                if (diffDays < 0) {
                                  return false;
                                }
                              }
                              
                              return true;
                            });
                          }
                          
                          const rows = dataToExport.map(bug => {
                            const fixVersion = bug.fixVersion || bug.fixVersionDummy || '';
                            const { collabVersions, msVersions } = categorizeFixVersion(fixVersion);
                            
                            // Get release dates for Collab versions (with normalization)
                            const releaseDates = collabVersions
                              .map(version => {
                                const normalizedVersion = normalizeVersion(version);
                                return fixVersionsMap[version] || 
                                       fixVersionsMap[normalizedVersion] ||
                                       staticReleaseDates[version] ||
                                       staticReleaseDates[normalizedVersion];
                              })
                              .filter(Boolean);
                            
                            // Calculate cycle time (difference between closed date and release date)
                            let cycleTime = '';
                            if (bug.closedDate && releaseDates.length > 0) {
                              const closedDate = new Date(bug.closedDate);
                              // Use the first release date if multiple exist
                              const releaseDate = new Date(releaseDates[0]);
                              const diffTime = releaseDate - closedDate;
                              const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
                              cycleTime = diffDays.toString();
                            }
                            
                            const formatDateForCSV = (date) => {
                              if (!date) return '';
                              try {
                                return new Date(date).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: '2-digit',
                                  day: '2-digit'
                                });
                              } catch {
                                return '';
                              }
                            };
                            
                            return [
                              bug.id || '',
                              bug.priority || '',
                              `"${(bug.title || '').replace(/"/g, '""')}"`, // Escape quotes in CSV
                              bug.team || '',
                              formatDateForCSV(bug.createdDate),
                              formatDateForCSV(bug.closedDate),
                              bug.age || '',
                              collabVersions.join('; ') || '',
                              msVersions.join('; ') || '',
                              releaseDates.map(rd => formatDateForCSV(rd)).join('; ') || '',
                              cycleTime || ''
                            ];
                          });
                          
                          // Create CSV content
                          const csvContent = [
                            headers.join(','),
                            ...rows.map(row => row.join(','))
                          ].join('\n');
                          
                          // Download CSV
                          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                          const link = document.createElement('a');
                          const url = URL.createObjectURL(blob);
                          link.setAttribute('href', url);
                          link.setAttribute('download', `production-bugs-analysis-${new Date().toISOString().split('T')[0]}.csv`);
                          link.style.visibility = 'hidden';
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }}
                      >
                        Download CSV
                      </Button>
                    </Box>
                  </Box>
                  
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      {(() => {
                        // Calculate filtered count
                        // Calculate filtered count with same logic as table
                        let filteredCountData = analysisData;
                        
                        // Filter by priority if priorities are selected
                        if (selectedPriorities.length > 0) {
                          filteredCountData = filteredCountData.filter(bug => {
                            const bugPriority = bug.priority || '';
                            return selectedPriorities.includes(bugPriority);
                          });
                        }
                        
                        let filteredCount = filteredCountData.length;
                        if (showValidOnly) {
                          filteredCount = filteredCountData.filter(bug => {
                            const fixVersion = bug.fixVersion || bug.fixVersionDummy || '';
                            const { collabVersions } = categorizeFixVersion(fixVersion);
                            const releaseDates = collabVersions
                              .map(version => {
                                const normalizedVersion = normalizeVersion(version);
                                return fixVersionsMap[version] || 
                                       fixVersionsMap[normalizedVersion] ||
                                       staticReleaseDates[version] ||
                                       staticReleaseDates[normalizedVersion];
                              })
                              .filter(Boolean);
                            if (releaseDates.length === 0) return false;
                            if (bug.closedDate && releaseDates.length > 0) {
                              const closedDate = new Date(bug.closedDate);
                              const releaseDate = new Date(releaseDates[0]);
                              const diffTime = releaseDate - closedDate;
                              const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
                              if (diffDays < 0) return false;
                            }
                            return true;
                          }).length;
                        }
                        return `Showing ${filteredCount} of ${analysisData.length} production bugs with fix version`;
                      })()}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={showValidOnly}
                            onChange={(e) => setShowValidOnly(e.target.checked)}
                            size="small"
                          />
                        }
                        label="Show Valid Only"
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setTableExpanded(!tableExpanded)}
                        endIcon={tableExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      >
                        {tableExpanded ? 'Hide Table' : 'Show Table'}
                      </Button>
                    </Box>
                  </Box>
                  
                  {tableExpanded && (
                    <TableContainer>
                      <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell 
                            sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => {
                              if (sortFieldAnalysis === 'id') {
                                setSortDirectionAnalysis(sortDirectionAnalysis === 'asc' ? 'desc' : 'asc');
                              } else {
                                setSortFieldAnalysis('id');
                                setSortDirectionAnalysis('asc');
                              }
                            }}
                          >
                            <Box display="flex" alignItems="center">
                              ADO ID
                              {sortFieldAnalysis === 'id' && (
                                sortDirectionAnalysis === 'asc' ? 
                                  <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                                  <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell 
                            sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => {
                              if (sortFieldAnalysis === 'priority') {
                                setSortDirectionAnalysis(sortDirectionAnalysis === 'asc' ? 'desc' : 'asc');
                              } else {
                                setSortFieldAnalysis('priority');
                                setSortDirectionAnalysis('asc');
                              }
                            }}
                          >
                            <Box display="flex" alignItems="center">
                              Priority
                              {sortFieldAnalysis === 'priority' && (
                                sortDirectionAnalysis === 'asc' ? 
                                  <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                                  <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Team</TableCell>
                          <TableCell 
                            sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => {
                              if (sortFieldAnalysis === 'createdDate') {
                                setSortDirectionAnalysis(sortDirectionAnalysis === 'asc' ? 'desc' : 'asc');
                              } else {
                                setSortFieldAnalysis('createdDate');
                                setSortDirectionAnalysis('desc');
                              }
                            }}
                          >
                            <Box display="flex" alignItems="center">
                              Created Date
                              {sortFieldAnalysis === 'createdDate' && (
                                sortDirectionAnalysis === 'asc' ? 
                                  <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                                  <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell 
                            sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => {
                              if (sortFieldAnalysis === 'closedDate') {
                                setSortDirectionAnalysis(sortDirectionAnalysis === 'asc' ? 'desc' : 'asc');
                              } else {
                                setSortFieldAnalysis('closedDate');
                                setSortDirectionAnalysis('desc');
                              }
                            }}
                          >
                            <Box display="flex" alignItems="center">
                              Closed Date
                              {sortFieldAnalysis === 'closedDate' && (
                                sortDirectionAnalysis === 'asc' ? 
                                  <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                                  <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell 
                            sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                            align="right"
                            onClick={() => {
                              if (sortFieldAnalysis === 'age') {
                                setSortDirectionAnalysis(sortDirectionAnalysis === 'asc' ? 'desc' : 'asc');
                              } else {
                                setSortFieldAnalysis('age');
                                setSortDirectionAnalysis('desc');
                              }
                            }}
                          >
                            <Box display="flex" alignItems="center" justifyContent="flex-end">
                              Age (Days)
                              {sortFieldAnalysis === 'age' && (
                                sortDirectionAnalysis === 'asc' ? 
                                  <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                                  <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Collab Version</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>MS Version</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Release Date</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Cycle Time (Days)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(() => {
                          // Filter data based on showValidOnly checkbox and priority
                          let filteredData = analysisData;
                          
                          // Filter by priority if priorities are selected
                          if (selectedPriorities.length > 0) {
                            filteredData = filteredData.filter(bug => {
                              const bugPriority = bug.priority || '';
                              return selectedPriorities.includes(bugPriority);
                            });
                          }
                          
                          if (showValidOnly) {
                            filteredData = filteredData.filter(bug => {
                              const fixVersion = bug.fixVersion || bug.fixVersionDummy || '';
                              const { collabVersions } = categorizeFixVersion(fixVersion);
                              
                              // Check if release date exists
                              const releaseDates = collabVersions
                                .map(version => {
                                  const normalizedVersion = normalizeVersion(version);
                                  return fixVersionsMap[version] || 
                                         fixVersionsMap[normalizedVersion] ||
                                         staticReleaseDates[version] ||
                                         staticReleaseDates[normalizedVersion];
                                })
                                .filter(Boolean);
                              
                              // Must have release date
                              if (releaseDates.length === 0) {
                                return false;
                              }
                              
                              // Check if cycle time is negative
                              if (bug.closedDate && releaseDates.length > 0) {
                                const closedDate = new Date(bug.closedDate);
                                const releaseDate = new Date(releaseDates[0]);
                                const diffTime = releaseDate - closedDate;
                                const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
                                
                                // Exclude if cycle time is negative
                                if (diffDays < 0) {
                                  return false;
                                }
                              }
                              
                              return true;
                            });
                          }
                          
                          const sorted = [...filteredData].sort((a, b) => {
                            let aValue = a[sortFieldAnalysis];
                            let bValue = b[sortFieldAnalysis];
                            
                            // Handle date fields
                            if (sortFieldAnalysis === 'createdDate' || sortFieldAnalysis === 'closedDate') {
                              aValue = aValue ? new Date(aValue).getTime() : 0;
                              bValue = bValue ? new Date(bValue).getTime() : 0;
                              return sortDirectionAnalysis === 'asc' ? aValue - bValue : bValue - aValue;
                            }
                            
                            // Handle numeric fields
                            if (sortFieldAnalysis === 'age' || sortFieldAnalysis === 'id') {
                              aValue = Number(aValue) || 0;
                              bValue = Number(bValue) || 0;
                              return sortDirectionAnalysis === 'asc' ? aValue - bValue : bValue - aValue;
                            }
                            
                            // Handle priority field (P1, P2, P3, etc.)
                            if (sortFieldAnalysis === 'priority') {
                              const getPriorityValue = (priority) => {
                                if (!priority) return 999;
                                const match = String(priority).match(/P?(\d+)/i);
                                return match ? parseInt(match[1], 10) : 999;
                              };
                              aValue = getPriorityValue(aValue);
                              bValue = getPriorityValue(bValue);
                              return sortDirectionAnalysis === 'asc' ? aValue - bValue : bValue - aValue;
                            }
                            
                            // Handle string fields
                            aValue = String(aValue || '').toLowerCase();
                            bValue = String(bValue || '').toLowerCase();
                            return sortDirectionAnalysis === 'asc' 
                              ? aValue.localeCompare(bValue) 
                              : bValue.localeCompare(aValue);
                          });
                          
                          return sorted.map((bug) => {
                            const adoUrl = `https://dev.azure.com/highqsoftware/HighQ/_workitems/edit/${bug.id}`;
                            const getAgeColor = (age) => {
                              if (age > 90) return '#f57c00';
                              if (age > 30) return '#fbc02d';
                              return '#388e3c';
                            };
                            const age = bug.age || 0;
                            const ageColor = getAgeColor(age);
                            
                            // Format dates
                            const formatDate = (date) => {
                              if (!date) return 'N/A';
                              try {
                                return new Date(date).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                });
                              } catch {
                                return 'N/A';
                              }
                            };
                            
                            return (
                              <TableRow key={bug.id} hover>
                                <TableCell>
                                  <Link
                                    href={adoUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    underline="hover"
                                    sx={{ fontWeight: 'medium', color: 'primary.main' }}
                                  >
                                    #{bug.id}
                                  </Link>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={bug.priority || 'Unknown'}
                                    size="small"
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell sx={{ maxWidth: 400, wordWrap: 'break-word' }}>
                                  <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                    {bug.title || 'Untitled'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary">
                                    {bug.team || 'Unknown'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {formatDate(bug.createdDate)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {formatDate(bug.closedDate)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Chip
                                    label={`${age} days`}
                                    size="small"
                                    sx={{
                                      backgroundColor: ageColor,
                                      color: 'white',
                                      fontWeight: 'bold'
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  {(() => {
                                    const fixVersion = bug.fixVersion || bug.fixVersionDummy || '';
                                    const { collabVersions } = categorizeFixVersion(fixVersion);
                                    
                                    if (collabVersions.length === 0) {
                                      return <Typography variant="body2" color="text.secondary">-</Typography>;
                                    }
                                    
                                    return (
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {collabVersions.map((version, idx) => (
                                          <Chip
                                            key={`collab-${idx}`}
                                            label={version}
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                          />
                                        ))}
                                      </Box>
                                    );
                                  })()}
                                </TableCell>
                                <TableCell>
                                  {(() => {
                                    const fixVersion = bug.fixVersion || bug.fixVersionDummy || '';
                                    const { msVersions } = categorizeFixVersion(fixVersion);
                                    
                                    if (msVersions.length === 0) {
                                      return <Typography variant="body2" color="text.secondary">-</Typography>;
                                    }
                                    
                                    return (
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {msVersions.map((version, idx) => (
                                          <Chip
                                            key={`ms-${idx}`}
                                            label={version}
                                            size="small"
                                            variant="outlined"
                                            color="secondary"
                                          />
                                        ))}
                                      </Box>
                                    );
                                  })()}
                                </TableCell>
                                <TableCell>
                                  {(() => {
                                    const fixVersion = bug.fixVersion || bug.fixVersionDummy || '';
                                    const { collabVersions } = categorizeFixVersion(fixVersion);
                                    
                                    if (collabVersions.length === 0) {
                                      return <Typography variant="body2" color="text.secondary">-</Typography>;
                                    }
                                    
                                    // Get release date for the first Collab version (or all if multiple)
                                    // Check fixVersionsMap first, then staticReleaseDates as fallback
                                    // Normalize versions for matching
                                    const releaseDates = collabVersions
                                      .map(version => {
                                        const normalizedVersion = normalizeVersion(version);
                                        const releaseDate = fixVersionsMap[version] || 
                                                           fixVersionsMap[normalizedVersion] ||
                                                           staticReleaseDates[version] ||
                                                           staticReleaseDates[normalizedVersion];
                                        return releaseDate ? releaseDate : null;
                                      })
                                      .filter(Boolean);
                                    
                                    if (releaseDates.length === 0) {
                                      return <Typography variant="body2" color="text.secondary">N/A</Typography>;
                                    }
                                    
                                    // If multiple dates, show them separated by line breaks
                                    return (
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        {releaseDates.map((releaseDate, idx) => (
                                          <Typography key={`release-${idx}`} variant="body2">
                                            {formatDate(releaseDate)}
                                          </Typography>
                                        ))}
                                      </Box>
                                    );
                                  })()}
                                </TableCell>
                                <TableCell>
                                  {(() => {
                                    const fixVersion = bug.fixVersion || bug.fixVersionDummy || '';
                                    const { collabVersions } = categorizeFixVersion(fixVersion);
                                    
                                    if (collabVersions.length === 0 || !bug.closedDate) {
                                      return <Typography variant="body2" color="text.secondary">-</Typography>;
                                    }
                                    
                                    // Get release dates for Collab versions
                                    // Check fixVersionsMap first, then staticReleaseDates as fallback
                                    // Normalize versions for matching
                                    const releaseDates = collabVersions
                                      .map(version => {
                                        const normalizedVersion = normalizeVersion(version);
                                        return fixVersionsMap[version] || 
                                               fixVersionsMap[normalizedVersion] ||
                                               staticReleaseDates[version] ||
                                               staticReleaseDates[normalizedVersion];
                                      })
                                      .filter(Boolean);
                                    
                                    if (releaseDates.length === 0) {
                                      return <Typography variant="body2" color="text.secondary">N/A</Typography>;
                                    }
                                    
                                    // Calculate cycle time (difference between closed date and release date)
                                    const closedDate = new Date(bug.closedDate);
                                    const cycleTimes = releaseDates.map(releaseDate => {
                                      const release = new Date(releaseDate);
                                      const diffTime = release - closedDate;
                                      const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
                                      return diffDays;
                                    });
                                    
                                    // If multiple release dates, show all cycle times
                                    if (cycleTimes.length === 1) {
                                      const cycleTime = cycleTimes[0];
                                      const cycleTimeColor = cycleTime < 0 ? '#f44336' : cycleTime === 0 ? '#ff9800' : '#4caf50';
                                      return (
                                        <Chip
                                          label={`${cycleTime} days`}
                                          size="small"
                                          sx={{
                                            backgroundColor: cycleTimeColor,
                                            color: 'white',
                                            fontWeight: 'bold'
                                          }}
                                        />
                                      );
                                    } else {
                                      return (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                          {cycleTimes.map((cycleTime, idx) => {
                                            const cycleTimeColor = cycleTime < 0 ? '#f44336' : cycleTime === 0 ? '#ff9800' : '#4caf50';
                                            return (
                                              <Chip
                                                key={`cycle-${idx}`}
                                                label={`${cycleTime} days`}
                                                size="small"
                                                sx={{
                                                  backgroundColor: cycleTimeColor,
                                                  color: 'white',
                                                  fontWeight: 'bold'
                                                }}
                                              />
                                            );
                                          })}
                                        </Box>
                                      );
                                    }
                                  })()}
                                </TableCell>
                              </TableRow>
                            );
                          });
                        })()}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  )}
                </Paper>
                      </>
                    );
                  })()}
                </>
              )}
            </>
          )}
        </Box>
      )}

      {activeTab === 6 && (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            WIP Analysis
          </Typography>
          
          {wipLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Production bugs resolved by team (last 12 months) - at top of page */}
              {wipResolvedByMonthByTeam?.monthLabels?.length > 0 && (() => {
                const { rows, monthKeys, monthLabels } = wipResolvedByMonthByTeam;
                const streamToRows = {};
                rows.forEach(row => {
                  const stream = getStreamForResolvedTeam(row.team, teamsWithStreamsForResolved);
                  if (!streamToRows[stream]) streamToRows[stream] = [];
                  streamToRows[stream].push(row);
                });
                const orderedStreams = [...RESOLVED_STREAM_ORDER.filter(s => (streamToRows[s]?.length || 0) > 0)];
                Object.keys(streamToRows).forEach(s => {
                  if (!RESOLVED_STREAM_ORDER.includes(s)) orderedStreams.push(s);
                });
                const isStreamExpanded = (stream) => expandedResolvedStreams[stream] === true;
                const toggleStream = (stream) => {
                  setExpandedResolvedStreams(prev => ({ ...prev, [stream]: !isStreamExpanded(stream) }));
                };
                return (
                  <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12}>
                      <Paper elevation={2} sx={{ p: 1.5 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontSize: '1rem' }}>
                          Production bugs resolved by team (last 12 months)
                        </Typography>
                        <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
                          <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', minWidth: 600 }}>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600, position: 'sticky', left: 0, backgroundColor: 'background.paper', zIndex: 2, py: 1, px: 2, fontSize: '0.875rem', minWidth: 220 }}>
                                  Stream / Team
                                </TableCell>
                                {monthLabels.map((label, idx) => (
                                  <TableCell
                                    key={monthKeys[idx]}
                                    align="center"
                                    sx={{ fontWeight: 600, minWidth: 70, py: 1, px: 1, fontSize: '0.875rem' }}
                                  >
                                    {label}
                                  </TableCell>
                                ))}
                                <TableCell align="right" sx={{ fontWeight: 600, minWidth: 70, py: 1, px: 1.5, backgroundColor: 'action.hover' }}>
                                  Total
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {orderedStreams.map((stream) => {
                                const streamRows = streamToRows[stream] || [];
                                const streamTotals = monthKeys.reduce((acc, mk) => {
                                  acc[mk] = streamRows.reduce((sum, r) => sum + (r[mk] || 0), 0);
                                  return acc;
                                }, {});
                                const streamTotal = streamRows.reduce((sum, r) => sum + (r.total || 0), 0);
                                const expanded = isStreamExpanded(stream);
                                return (
                                  <React.Fragment key={stream}>
                                    <TableRow
                                      sx={{
                                        backgroundColor: expanded ? 'action.hover' : 'grey.50',
                                        '&:hover': { backgroundColor: 'action.selected' },
                                        cursor: 'pointer'
                                      }}
                                      onClick={() => toggleStream(stream)}
                                    >
                                      <TableCell sx={{ position: 'sticky', left: 0, backgroundColor: 'inherit', zIndex: 1, py: 1, px: 2, fontSize: '0.875rem', fontWeight: 600, minWidth: 220 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                          <IconButton size="small" sx={{ p: 0, mt: -0.25 }} aria-label={expanded ? 'Collapse' : 'Expand'}>
                                            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                          </IconButton>
                                          <Box sx={{ display: 'block', minWidth: 0 }}>
                                            <Typography component="span" sx={{ display: 'block', fontSize: '0.875rem' }}>
                                              {stream}
                                            </Typography>
                                            <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.75rem' }}>
                                              {stream === 'SWAT' ? '1 team' : `${streamRows.length} team${streamRows.length !== 1 ? 's' : ''}`}
                                            </Typography>
                                          </Box>
                                        </Box>
                                      </TableCell>
                                      {monthKeys.map((mk) => (
                                        <TableCell key={mk} align="center" sx={{ py: 1, px: 1, fontWeight: 500 }}>
                                          {streamTotals[mk] > 0 ? streamTotals[mk] : '-'}
                                        </TableCell>
                                      ))}
                                      <TableCell align="right" sx={{ py: 1, px: 1.5, backgroundColor: 'action.hover', fontWeight: 600 }}>
                                        {streamTotal}
                                      </TableCell>
                                    </TableRow>
                                    <TableRow>
                                      <TableCell colSpan={monthKeys.length + 2} sx={{ py: 0, borderBottom: 'none', verticalAlign: 'top' }}>
                                        <Collapse in={expanded}>
                                          <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 600 }}>
                                            <TableBody>
                                              {stream === 'SWAT' ? (
                                                <TableRow>
                                                  <TableCell sx={{ pl: 6, py: 0.75, fontSize: '0.8125rem', color: 'text.secondary' }}>
                                                    SWAT
                                                  </TableCell>
                                                  {monthKeys.map((mk) => (
                                                    <TableCell key={mk} align="center" sx={{ py: 0.75, fontSize: '0.8125rem' }}>
                                                      {streamTotals[mk] > 0 ? streamTotals[mk] : '-'}
                                                    </TableCell>
                                                  ))}
                                                  <TableCell align="right" sx={{ py: 0.75, px: 1.5, fontSize: '0.8125rem' }}>
                                                    {streamTotal}
                                                  </TableCell>
                                                </TableRow>
                                              ) : (
                                                streamRows.map((row) => (
                                                  <TableRow key={row.team}>
                                                    <TableCell sx={{ pl: 6, py: 0.75, fontSize: '0.8125rem', color: 'text.secondary' }}>
                                                      {row.team}
                                                    </TableCell>
                                                    {monthKeys.map((monthKey) => (
                                                      <TableCell key={monthKey} align="center" sx={{ py: 0.75, fontSize: '0.8125rem' }}>
                                                        {row[monthKey] > 0 ? row[monthKey] : '-'}
                                                      </TableCell>
                                                    ))}
                                                    <TableCell align="right" sx={{ py: 0.75, px: 1.5, fontSize: '0.8125rem' }}>
                                                      {row.total}
                                                    </TableCell>
                                                  </TableRow>
                                                ))
                                              )}
                                            </TableBody>
                                          </Table>
                                        </Collapse>
                                      </TableCell>
                                    </TableRow>
                                  </React.Fragment>
                                );
                              })}
                            </TableBody>
                            <TableFooter>
                              <TableRow sx={{ '& td': { borderTop: '2px solid', borderColor: 'divider', backgroundColor: 'grey.700', color: 'white', fontSize: '1.0625rem', fontWeight: 700 } }}>
                                <TableCell sx={{ fontWeight: 700, position: 'sticky', left: 0, backgroundColor: 'grey.700', zIndex: 1, py: 1.5, px: 2, color: 'white', minWidth: 220, fontSize: '1.0625rem' }}>
                                  Total
                                </TableCell>
                                {monthKeys.map((monthKey) => {
                                  const colTotal = rows.reduce((sum, r) => sum + (r[monthKey] || 0), 0);
                                  return (
                                    <TableCell key={monthKey} align="center" sx={{ py: 1.5, px: 1, color: 'white', fontSize: '1.0625rem' }}>
                                      {colTotal > 0 ? colTotal : '-'}
                                    </TableCell>
                                  );
                                })}
                                <TableCell align="right" sx={{ py: 1.5, px: 1.5, color: 'white', fontWeight: 700, fontSize: '1.0625rem' }}>
                                  {rows.reduce((sum, r) => sum + (r.total || 0), 0)}
                                </TableCell>
                              </TableRow>
                            </TableFooter>
                          </Table>
                        </TableContainer>
                      </Paper>
                    </Grid>
                  </Grid>
                );
              })()}

              {wipData.length > 0 && (() => {
                // Determine current and next sprints
                const { currentSprint, nextSprint } = getCurrentAndNextSprints(wipData);
                
                // Categorize bugs by sprint
                // SWAT team bugs always go to current sprint regardless of iteration path
                const currentSprintBugs = wipData.filter(bug => {
                  const team = (bug.team || '').trim();
                  const isSWAT = team.toLowerCase().includes('swat');
                  
                  // SWAT team bugs always go to current sprint
                  if (isSWAT) return true;
                  
                  // Otherwise, check iteration path
                  if (!bug.iterationPath) return false;
                  const sprintName = extractSprintName(bug.iterationPath);
                  return sprintName === currentSprint;
                });
                
                const nextSprintBugs = wipData.filter(bug => {
                  if (!bug.iterationPath) return false;
                  const sprintName = extractSprintName(bug.iterationPath);
                  return sprintName === nextSprint;
                });
                
                const backlogBugs = wipData.filter(bug => {
                  const team = (bug.team || '').trim();
                  const isSWAT = team.toLowerCase().includes('swat');
                  
                  // Exclude SWAT team bugs from backlog
                  if (isSWAT) return false;
                  
                  // No iteration = backlog (unless SWAT)
                  if (!bug.iterationPath) return true;
                  const sprintName = extractSprintName(bug.iterationPath);
                  return sprintName !== currentSprint && sprintName !== nextSprint;
                });
                
                // Build 2D matrix: teams (rows) × states (columns)
                const buildStateMatrix = (bugs) => {
                  const teams = new Set();
                  const states = new Set();
                  const matrix = new Map(); // team -> Map(state -> count)
                  
                  // Collect all teams and states, and count bugs
                  bugs.forEach(bug => {
                    const team = bug.team || 'Unknown';
                    const state = bug.state || 'Unknown';
                    teams.add(team);
                    states.add(state);
                    
                    if (!matrix.has(team)) {
                      matrix.set(team, new Map());
                    }
                    const teamMap = matrix.get(team);
                    teamMap.set(state, (teamMap.get(state) || 0) + 1);
                  });
                  
                  // Convert to sorted arrays
                  const sortedTeams = Array.from(teams).sort();
                  const sortedStates = Array.from(states).sort();
                  
                  // Build result array with totals
                  const result = sortedTeams.map(team => {
                    const teamMap = matrix.get(team);
                    const row = { team };
                    let total = 0;
                    
                    sortedStates.forEach(state => {
                      const count = teamMap.get(state) || 0;
                      row[state] = count;
                      total += count;
                    });
                    
                    row.total = total;
                    return row;
                  });
                  
                  // Sort by total descending
                  result.sort((a, b) => b.total - a.total);
                  
                  return { rows: result, states: sortedStates };
                };
                
                // Count bugs by team for each category (for Next Sprint and Backlog)
                const countByTeam = (bugs) => {
                  const counts = {};
                  bugs.forEach(bug => {
                    const team = bug.team || 'Unknown';
                    counts[team] = (counts[team] || 0) + 1;
                  });
                  return Object.entries(counts)
                    .map(([team, count]) => ({ team, count }))
                    .sort((a, b) => b.count - a.count);
                };
                
                // Merge closed bugs with current sprint bugs for 2D table
                const currentSprintBugsWithClosed = [...currentSprintBugs, ...closedBugsForCurrentSprint];
                
                // Filter SWAT bugs for left table (Assigned To × States)
                // Only include bugs where team name is exactly "SWAT Team" or contains "SWAT Team"
                // AND created after October 2025
                const october2025 = new Date('2025-10-01T00:00:00.000Z');
                const swatBugs = currentSprintBugsWithClosed.filter(bug => {
                  const team = (bug.team || '').trim();
                  if (!team) return false;
                  const teamLower = team.toLowerCase();
                  // Match "SWAT Team" - be very strict, only match if it's "SWAT Team" or contains "SWAT Team"
                  const isSWAT = teamLower === 'swat team' || teamLower.includes('swat team');
                  if (!isSWAT) return false;
                  
                  // Filter by creation date - must be created after October 2025
                  if (!bug.createdDate) return false;
                  const createdDate = new Date(bug.createdDate);
                  if (isNaN(createdDate.getTime())) return false;
                  
                  return createdDate >= october2025;
                });
                
                // Debug: Log SWAT bugs count and sample team names
                console.log('SWAT bugs count (after Oct 2025 filter):', swatBugs.length);
                if (swatBugs.length > 0) {
                  const swatTeamNames = [...new Set(swatBugs.map(b => b.team))];
                  console.log('SWAT team names found:', swatTeamNames);
                }
                
                // Filter out SWAT bugs from the right table (Teams × States)
                // Include all non-SWAT bugs (including closed bugs that are not SWAT)
                const currentSprintBugsWithoutSWAT = currentSprintBugsWithClosed.filter(bug => {
                  const team = (bug.team || '').trim();
                  if (!team) return true; // Include bugs without team (they're not SWAT)
                  const teamLower = team.toLowerCase();
                  // Exclude if it's SWAT team
                  return !(teamLower === 'swat team' || 
                          teamLower === 'swat' ||
                          teamLower.startsWith('swat ') ||
                          teamLower.endsWith(' swat') ||
                          teamLower.includes(' swat '));
                });
                
                // Helper function to clean assigned to name (remove TR Technology prefix and suffix)
                const cleanAssignedToName = (name) => {
                  if (!name) return 'Unassigned';
                  let cleaned = name;
                  // Remove "TR Technology\" prefix
                  cleaned = cleaned.replace(/^TR Technology\\/i, '');
                  // Remove "TR Technology" prefix
                  cleaned = cleaned.replace(/^TR Technology/i, '');
                  // Remove "(TR Technology)" suffix (with parentheses)
                  cleaned = cleaned.replace(/\s*\(TR Technology\)\s*$/i, '');
                  // Remove "TR Technology" suffix (without parentheses, with optional spaces)
                  cleaned = cleaned.replace(/\s+TR Technology\s*$/i, '');
                  // Trim and return, or 'Unassigned' if empty
                  cleaned = cleaned.trim();
                  return cleaned || 'Unassigned';
                };
                
                // Build 2D matrix: assigned to (rows) × states (columns)
                const buildAssignedToMatrix = (bugs) => {
                  const assignedTos = new Set();
                  const states = new Set();
                  const matrix = new Map(); // assignedTo -> Map(state -> count)
                  
                  // Collect all assignedTo and states, and count bugs
                  bugs.forEach(bug => {
                    const rawAssignedTo = bug.assignedTo || bug.assignee || 'Unassigned';
                    const assignedTo = cleanAssignedToName(rawAssignedTo);
                    const state = bug.state || 'Unknown';
                    assignedTos.add(assignedTo);
                    states.add(state);
                    
                    if (!matrix.has(assignedTo)) {
                      matrix.set(assignedTo, new Map());
                    }
                    const assignedToMap = matrix.get(assignedTo);
                    assignedToMap.set(state, (assignedToMap.get(state) || 0) + 1);
                  });
                  
                  // Convert to sorted arrays
                  const sortedAssignedTos = Array.from(assignedTos).sort();
                  const sortedStates = Array.from(states).sort();
                  
                  // Build result array with totals
                  const result = sortedAssignedTos.map(assignedTo => {
                    const assignedToMap = matrix.get(assignedTo);
                    const row = { assignedTo };
                    let total = 0;
                    
                    sortedStates.forEach(state => {
                      const count = assignedToMap.get(state) || 0;
                      row[state] = count;
                      total += count;
                    });
                    
                    row.total = total;
                    return row;
                  });
                  
                  // Sort by total descending
                  result.sort((a, b) => b.total - a.total);
                  
                  return { rows: result, states: sortedStates };
                };
                
                // Collect all unique states from all current sprint bugs (including SWAT) for the Teams table
                const allStates = new Set();
                currentSprintBugsWithClosed.forEach(bug => {
                  const state = bug.state || 'Unknown';
                  allStates.add(state);
                });
                const sortedAllStates = Array.from(allStates).sort();
                
                // Build state matrix for Teams table, but use all states from all bugs
                const buildStateMatrixWithAllStates = (bugs, allStatesList) => {
                  const teams = new Set();
                  const matrix = new Map(); // team -> Map(state -> count)
                  
                  // Collect all teams and count bugs
                  bugs.forEach(bug => {
                    const team = bug.team || 'Unknown';
                    const state = bug.state || 'Unknown';
                    teams.add(team);
                    
                    if (!matrix.has(team)) {
                      matrix.set(team, new Map());
                    }
                    const teamMap = matrix.get(team);
                    teamMap.set(state, (teamMap.get(state) || 0) + 1);
                  });
                  
                  // Convert to sorted arrays
                  const sortedTeams = Array.from(teams).sort();
                  
                  // Build result array with totals, using allStatesList for columns
                  const result = sortedTeams.map(team => {
                    const teamMap = matrix.get(team);
                    const row = { team };
                    let total = 0;
                    
                    allStatesList.forEach(state => {
                      const count = teamMap.get(state) || 0;
                      row[state] = count;
                      total += count;
                    });
                    
                    row.total = total;
                    return row;
                  });
                  
                  // Sort by total descending
                  result.sort((a, b) => b.total - a.total);
                  
                  return { rows: result, states: allStatesList };
                };
                
                const currentSprintMatrix = buildStateMatrixWithAllStates(currentSprintBugsWithoutSWAT, sortedAllStates);
                const assignedToMatrix = buildAssignedToMatrix(swatBugs);
                const nextSprintCounts = countByTeam(nextSprintBugs).sort((a, b) => a.team.localeCompare(b.team));
                const backlogCounts = countByTeam(backlogBugs).sort((a, b) => a.team.localeCompare(b.team));
                
                // Generate consistent background color for a team name
                const getTeamColor = (teamName) => {
                  // Generate a hash from the team name
                  let hash = 0;
                  for (let i = 0; i < teamName.length; i++) {
                    hash = teamName.charCodeAt(i) + ((hash << 5) - hash);
                  }
                  // Generate a darker color from the hash (similar to chip colors)
                  const hue = Math.abs(hash % 360);
                  const saturation = 50 + (Math.abs(hash) % 30); // 50-80% saturation
                  const lightness = 60 + (Math.abs(hash) % 20); // 60-80% lightness for darker colors
                  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
                };
                
                return (
                  <>
                    {/* Next Sprint Cards */}
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                      <Grid item xs={12}>
                        <Paper elevation={2} sx={{ p: 2 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                            Next Sprint
                            {nextSprint && (
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                ({nextSprint})
                              </Typography>
                            )}
                          </Typography>
                          {nextSprintCounts.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                              {nextSprintCounts.map(({ team, count }) => {
                                const teamColor = getTeamColor(team);
                                return (
                                  <Card 
                                    key={team}
                                    elevation={1}
                                    sx={{ 
                                      minWidth: 140,
                                      maxWidth: 200,
                                      backgroundColor: 'white',
                                      borderLeft: `4px solid ${teamColor}`,
                                      borderRadius: 2,
                                      transition: 'transform 0.2s, box-shadow 0.2s',
                                      '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: 3
                                      }
                                    }}
                                  >
                                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                      <Typography 
                                        variant="h5" 
                                        sx={{ 
                                          fontWeight: 700,
                                          mb: 1,
                                          color: teamColor,
                                          fontSize: '1.75rem',
                                          lineHeight: 1.2
                                        }}
                                      >
                                        {count}
                                      </Typography>
                                      <Typography 
                                        variant="body2" 
                                        sx={{ 
                                          fontWeight: 400,
                                          fontSize: '0.875rem',
                                          color: 'text.secondary',
                                          wordBreak: 'break-word'
                                        }}
                                      >
                                        {team}
                                      </Typography>
                                    </CardContent>
                                  </Card>
                                );
                              })}
                            </Box>
                          ) : (
                            <Box sx={{ textAlign: 'center', py: 3 }}>
                              <Typography variant="body2" color="text.secondary">
                                {nextSprint ? 'No issues in next sprint' : 'No next sprint found'}
                              </Typography>
                            </Box>
                          )}
                        </Paper>
                      </Grid>
                    </Grid>
                    
                    {/* Backlog Cards */}
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                      <Grid item xs={12}>
                        <Paper elevation={2} sx={{ p: 2 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                            Backlog
                          </Typography>
                          {backlogCounts.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                              {backlogCounts.map(({ team, count }) => {
                                const teamColor = getTeamColor(team);
                                return (
                                  <Card 
                                    key={team}
                                    elevation={1}
                                    sx={{ 
                                      minWidth: 140,
                                      maxWidth: 200,
                                      backgroundColor: 'white',
                                      borderLeft: `4px solid ${teamColor}`,
                                      borderRadius: 2,
                                      transition: 'transform 0.2s, box-shadow 0.2s',
                                      '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: 3
                                      }
                                    }}
                                  >
                                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                      <Typography 
                                        variant="h5" 
                                        sx={{ 
                                          fontWeight: 700,
                                          mb: 1,
                                          color: teamColor,
                                          fontSize: '1.75rem',
                                          lineHeight: 1.2
                                        }}
                                      >
                                        {count}
                                      </Typography>
                                      <Typography 
                                        variant="body2" 
                                        sx={{ 
                                          fontWeight: 400,
                                          fontSize: '0.875rem',
                                          color: 'text.secondary',
                                          wordBreak: 'break-word'
                                        }}
                                      >
                                        {team}
                                      </Typography>
                                    </CardContent>
                                  </Card>
                                );
                              })}
                            </Box>
                          ) : (
                            <Box sx={{ textAlign: 'center', py: 3 }}>
                              <Typography variant="body2" color="text.secondary">
                                No backlog issues
                              </Typography>
                            </Box>
                          )}
                        </Paper>
                      </Grid>
                    </Grid>
                    
                    {/* Current Sprint Tables - SWAT and Teams in separate rows */}
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                      {/* SWAT Table - By Assigned To (Top Row) */}
                      <Grid item xs={12}>
                        <Paper elevation={2} sx={{ p: 1.5 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontSize: '1rem' }}>
                            SWAT Bugs
                          </Typography>
                          <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
                            <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 600, position: 'sticky', left: 0, backgroundColor: 'background.paper', zIndex: 2, py: 1, px: 2, fontSize: '0.875rem', minWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    Assigned To
                                  </TableCell>
                                  {assignedToMatrix.states.map((state) => (
                                    <TableCell 
                                      key={state} 
                                      align="center" 
                                      sx={{ fontWeight: 600, minWidth: 70, py: 1, px: 1, fontSize: '0.875rem', lineHeight: 1.2 }}
                                    >
                                      <Box sx={{ 
                                        overflow: 'hidden', 
                                        textOverflow: 'ellipsis', 
                                        whiteSpace: 'nowrap',
                                        maxWidth: 70
                                      }}>
                                        {state}
                                      </Box>
                                    </TableCell>
                                  ))}
                                  <TableCell align="right" sx={{ fontWeight: 600, backgroundColor: 'action.hover', py: 1, px: 1.5, fontSize: '0.875rem', minWidth: 70 }}>
                                    Total
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {assignedToMatrix.rows.length > 0 ? (
                                  assignedToMatrix.rows.map((row) => (
                                    <TableRow key={row.assignedTo} hover>
                                      <TableCell sx={{ fontWeight: 500, position: 'sticky', left: 0, backgroundColor: 'background.paper', zIndex: 1, py: 1.5, px: 2, fontSize: '0.875rem', minWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {row.assignedTo}
                                      </TableCell>
                                      {assignedToMatrix.states.map((state) => {
                                        const count = row[state] || 0;
                                        const stateColor = getStateColor(state);
                                        // Determine if color is light or dark for better contrast
                                        let textColor = '#ffffff'; // default to white
                                        try {
                                          const hex = stateColor.replace('#', '');
                                          if (hex.length >= 6) {
                                            const r = parseInt(hex.substr(0, 2), 16);
                                            const g = parseInt(hex.substr(2, 2), 16);
                                            const b = parseInt(hex.substr(4, 2), 16);
                                            const brightness = (r * 299 + g * 587 + b * 114) / 1000;
                                            textColor = brightness > 128 ? '#000000' : '#ffffff';
                                          }
                                        } catch (e) {
                                          // If parsing fails, use default white text
                                          console.warn('Error parsing color:', stateColor, e);
                                        }
                                        
                                        return (
                                          <TableCell key={state} align="center" sx={{ py: 1.5, px: 1, minWidth: 70 }}>
                                            {count > 0 ? (
                                              <Chip 
                                                label={count} 
                                                size="small" 
                                                onClick={() => {
                                                  // Filter bugs for this assignedTo and state
                                                  const filteredBugs = swatBugs.filter(bug => {
                                                    const rawAssignedTo = bug.assignedTo || bug.assignee || 'Unassigned';
                                                    const cleanedAssignedTo = cleanAssignedToName(rawAssignedTo);
                                                    const bugState = bug.state || 'Unknown';
                                                    return cleanedAssignedTo === row.assignedTo && bugState === state;
                                                  });
                                                  setBugDetailsDialogBugs(filteredBugs);
                                                  setBugDetailsDialogTitle(`${row.assignedTo} - ${state} (${count})`);
                                                  setBugDetailsDialogOpen(true);
                                                }}
                                                sx={{ 
                                                  backgroundColor: stateColor,
                                                  color: textColor,
                                                  fontWeight: 500,
                                                  height: 28,
                                                  fontSize: '0.875rem',
                                                  '& .MuiChip-label': { px: 1.25 },
                                                  cursor: 'pointer',
                                                  '&:hover': {
                                                    opacity: 0.8,
                                                    transform: 'scale(1.05)'
                                                  }
                                                }}
                                              />
                                            ) : (
                                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                                                -
                                              </Typography>
                                            )}
                                          </TableCell>
                                        );
                                      })}
                                      <TableCell align="right" sx={{ backgroundColor: 'action.hover', py: 1.5, px: 1.5, minWidth: 70 }}>
                                        <Chip 
                                          label={row.total} 
                                          size="small" 
                                          color="primary" 
                                          onClick={() => {
                                            // Filter bugs for this assignedTo (all states)
                                            const filteredBugs = swatBugs.filter(bug => {
                                              const rawAssignedTo = bug.assignedTo || bug.assignee || 'Unassigned';
                                              const cleanedAssignedTo = cleanAssignedToName(rawAssignedTo);
                                              return cleanedAssignedTo === row.assignedTo;
                                            });
                                            setBugDetailsDialogBugs(filteredBugs);
                                            setBugDetailsDialogTitle(`${row.assignedTo} - All States (${row.total})`);
                                            setBugDetailsDialogOpen(true);
                                          }}
                                          sx={{ 
                                            height: 28, 
                                            fontSize: '0.875rem', 
                                            fontWeight: 500,
                                            '& .MuiChip-label': { px: 1.25 },
                                            cursor: 'pointer',
                                            '&:hover': {
                                              opacity: 0.8,
                                              transform: 'scale(1.05)'
                                            }
                                          }} 
                                        />
                                      </TableCell>
                                    </TableRow>
                                  ))
                                ) : (
                                  <TableRow>
                                    <TableCell colSpan={assignedToMatrix.states.length + 2} align="center">
                                      <Typography variant="body2" color="text.secondary">
                                        No SWAT team bugs in current sprint
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </TableBody>
                              <TableFooter>
                                <TableRow sx={{ '& td': { borderTop: '2px solid', borderColor: 'divider', backgroundColor: 'grey.700', color: 'white' } }}>
                                  <TableCell sx={{ fontWeight: 700, position: 'sticky', left: 0, backgroundColor: 'grey.700', zIndex: 1, py: 1.5, px: 2, fontSize: '1.125rem', minWidth: 150, color: 'white' }}>
                                    Total
                                  </TableCell>
                                  {assignedToMatrix.states.map((state) => {
                                    const stateTotal = assignedToMatrix.rows.reduce((sum, row) => sum + (row[state] || 0), 0);
                                    return (
                                      <TableCell key={state} align="center" sx={{ py: 1.5, px: 1, minWidth: 70, backgroundColor: 'grey.700' }}>
                                        {stateTotal > 0 ? (
                                          <Typography 
                                            variant="body2" 
                                            onClick={() => {
                                              // Filter bugs for this state (all assignedTo)
                                              const filteredBugs = swatBugs.filter(bug => {
                                                const bugState = bug.state || 'Unknown';
                                                return bugState === state;
                                              });
                                              setBugDetailsDialogBugs(filteredBugs);
                                              setBugDetailsDialogTitle(`All SWAT Assignees - ${state} (${stateTotal})`);
                                              setBugDetailsDialogOpen(true);
                                            }}
                                            sx={{ 
                                              fontWeight: 700,
                                              fontSize: '1.125rem',
                                              color: 'white',
                                              cursor: 'pointer',
                                              '&:hover': {
                                                opacity: 0.7
                                              }
                                            }}
                                          >
                                            {stateTotal}
                                          </Typography>
                                        ) : (
                                          <Typography variant="body2" sx={{ fontSize: '1.125rem', fontWeight: 700, color: 'white' }}>
                                            -
                                          </Typography>
                                        )}
                                      </TableCell>
                                    );
                                  })}
                                  <TableCell align="right" sx={{ py: 1.5, px: 1.5, minWidth: 70, backgroundColor: 'grey.700' }}>
                                    <Typography 
                                      variant="body2"
                                      onClick={() => {
                                        // Show all SWAT bugs
                                        setBugDetailsDialogBugs(swatBugs);
                                        setBugDetailsDialogTitle(`All SWAT Team Bugs (${swatBugs.length})`);
                                        setBugDetailsDialogOpen(true);
                                      }}
                                      sx={{ 
                                        fontWeight: 700, 
                                        fontSize: '1.125rem',
                                        color: 'white',
                                        cursor: 'pointer',
                                        '&:hover': {
                                          opacity: 0.7
                                        }
                                      }} 
                                    >
                                      {assignedToMatrix.rows.reduce((sum, row) => sum + (row.total || 0), 0)}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              </TableFooter>
                            </Table>
                          </TableContainer>
                        </Paper>
                      </Grid>
                      
                      {/* Teams Table - By Team (Bottom Row) */}
                      <Grid item xs={12}>
                        <Paper elevation={2} sx={{ p: 1.5 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontSize: '1rem' }}>
                            Current Sprint - By Team
                            {currentSprint && (
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                ({currentSprint})
                              </Typography>
                            )}
                          </Typography>
                          <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
                            <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 600, position: 'sticky', left: 0, backgroundColor: 'background.paper', zIndex: 2, py: 1, px: 2, fontSize: '0.875rem', minWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    Team
                                  </TableCell>
                                  {currentSprintMatrix.states.map((state) => (
                                    <TableCell 
                                      key={state} 
                                      align="center" 
                                      sx={{ fontWeight: 600, minWidth: 70, py: 1, px: 1, fontSize: '0.875rem', lineHeight: 1.2 }}
                                    >
                                      <Box sx={{ 
                                        overflow: 'hidden', 
                                        textOverflow: 'ellipsis', 
                                        whiteSpace: 'nowrap',
                                        maxWidth: 70
                                      }}>
                                        {state}
                                      </Box>
                                    </TableCell>
                                  ))}
                                  <TableCell align="right" sx={{ fontWeight: 600, backgroundColor: 'action.hover', py: 1, px: 1.5, fontSize: '0.875rem', minWidth: 70 }}>
                                    Total
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {currentSprintMatrix.rows.length > 0 ? (
                                  currentSprintMatrix.rows.map((row) => (
                                    <TableRow key={row.team} hover>
                                      <TableCell sx={{ fontWeight: 500, position: 'sticky', left: 0, backgroundColor: 'background.paper', zIndex: 1, py: 1.5, px: 2, fontSize: '0.875rem', minWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {row.team}
                                      </TableCell>
                                      {currentSprintMatrix.states.map((state) => {
                                        const count = row[state] || 0;
                                        const stateColor = getStateColor(state);
                                        // Determine if color is light or dark for better contrast
                                        let textColor = '#ffffff'; // default to white
                                        try {
                                          const hex = stateColor.replace('#', '');
                                          if (hex.length >= 6) {
                                            const r = parseInt(hex.substr(0, 2), 16);
                                            const g = parseInt(hex.substr(2, 2), 16);
                                            const b = parseInt(hex.substr(4, 2), 16);
                                            const brightness = (r * 299 + g * 587 + b * 114) / 1000;
                                            textColor = brightness > 128 ? '#000000' : '#ffffff';
                                          }
                                        } catch (e) {
                                          // If parsing fails, use default white text
                                          console.warn('Error parsing color:', stateColor, e);
                                        }
                                        
                                        return (
                                          <TableCell key={state} align="center" sx={{ py: 1.5, px: 1, minWidth: 70 }}>
                                            {count > 0 ? (
                                              <Chip 
                                                label={count} 
                                                size="small" 
                                                onClick={() => {
                                                  // Filter bugs for this team and state
                                                  const filteredBugs = currentSprintBugsWithoutSWAT.filter(bug => {
                                                    const bugTeam = bug.team || 'Unknown';
                                                    const bugState = bug.state || 'Unknown';
                                                    return bugTeam === row.team && bugState === state;
                                                  });
                                                  setBugDetailsDialogBugs(filteredBugs);
                                                  setBugDetailsDialogTitle(`${row.team} - ${state} (${count})`);
                                                  setBugDetailsDialogOpen(true);
                                                }}
                                                sx={{ 
                                                  backgroundColor: stateColor,
                                                  color: textColor,
                                                  fontWeight: 500,
                                                  height: 28,
                                                  fontSize: '0.875rem',
                                                  '& .MuiChip-label': { px: 1.25 },
                                                  cursor: 'pointer',
                                                  '&:hover': {
                                                    opacity: 0.8,
                                                    transform: 'scale(1.05)'
                                                  }
                                                }}
                                              />
                                            ) : (
                                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                                                -
                                              </Typography>
                                            )}
                                          </TableCell>
                                        );
                                      })}
                                      <TableCell align="right" sx={{ backgroundColor: 'action.hover', py: 1.5, px: 1.5, minWidth: 70 }}>
                                        <Chip 
                                          label={row.total} 
                                          size="small" 
                                          color="primary" 
                                          onClick={() => {
                                            // Filter bugs for this team (all states)
                                            const filteredBugs = currentSprintBugsWithoutSWAT.filter(bug => {
                                              const bugTeam = bug.team || 'Unknown';
                                              return bugTeam === row.team;
                                            });
                                            setBugDetailsDialogBugs(filteredBugs);
                                            setBugDetailsDialogTitle(`${row.team} - All States (${row.total})`);
                                            setBugDetailsDialogOpen(true);
                                          }}
                                          sx={{ 
                                            height: 24, 
                                            fontSize: '0.875rem', 
                                            '& .MuiChip-label': { px: 1 },
                                            cursor: 'pointer',
                                            '&:hover': {
                                              opacity: 0.8,
                                              transform: 'scale(1.05)'
                                            }
                                          }} 
                                        />
                                      </TableCell>
                                    </TableRow>
                                  ))
                                ) : (
                                  <TableRow>
                                    <TableCell colSpan={currentSprintMatrix.states.length + 2} align="center">
                                      <Typography variant="body2" color="text.secondary">
                                        {currentSprint ? 'No issues in current sprint' : 'No current sprint found'}
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </TableBody>
                              <TableFooter>
                                <TableRow sx={{ '& td': { borderTop: '2px solid', borderColor: 'divider', backgroundColor: 'grey.700', color: 'white' } }}>
                                  <TableCell sx={{ fontWeight: 700, position: 'sticky', left: 0, backgroundColor: 'grey.700', zIndex: 1, py: 1.5, px: 2, fontSize: '1.125rem', minWidth: 150, color: 'white' }}>
                                    Total
                                  </TableCell>
                                  {currentSprintMatrix.states.map((state) => {
                                    const stateTotal = currentSprintMatrix.rows.reduce((sum, row) => sum + (row[state] || 0), 0);
                                    return (
                                      <TableCell key={state} align="center" sx={{ py: 1.5, px: 1, minWidth: 70, backgroundColor: 'grey.700' }}>
                                        {stateTotal > 0 ? (
                                          <Typography 
                                            variant="body2" 
                                            onClick={() => {
                                              // Filter bugs for this state (all teams)
                                              const filteredBugs = currentSprintBugsWithoutSWAT.filter(bug => {
                                                const bugState = bug.state || 'Unknown';
                                                return bugState === state;
                                              });
                                              setBugDetailsDialogBugs(filteredBugs);
                                              setBugDetailsDialogTitle(`All Teams - ${state} (${stateTotal})`);
                                              setBugDetailsDialogOpen(true);
                                            }}
                                            sx={{ 
                                              fontWeight: 700,
                                              fontSize: '1.125rem',
                                              color: 'white',
                                              cursor: 'pointer',
                                              '&:hover': {
                                                opacity: 0.7
                                              }
                                            }}
                                          >
                                            {stateTotal}
                                          </Typography>
                                        ) : (
                                          <Typography variant="body2" sx={{ fontSize: '1.125rem', fontWeight: 700, color: 'white' }}>
                                            -
                                          </Typography>
                                        )}
                                      </TableCell>
                                    );
                                  })}
                                  <TableCell align="right" sx={{ py: 1.5, px: 1.5, minWidth: 70, backgroundColor: 'grey.700' }}>
                                    <Typography 
                                      variant="body2"
                                      onClick={() => {
                                        // Show all non-SWAT bugs
                                        setBugDetailsDialogBugs(currentSprintBugsWithoutSWAT);
                                        setBugDetailsDialogTitle(`All Teams - All States (${currentSprintBugsWithoutSWAT.length})`);
                                        setBugDetailsDialogOpen(true);
                                      }}
                                      sx={{ 
                                        fontWeight: 700, 
                                        fontSize: '1.125rem',
                                        color: 'white',
                                        cursor: 'pointer',
                                        '&:hover': {
                                          opacity: 0.7
                                        }
                                      }} 
                                    >
                                      {currentSprintMatrix.rows.reduce((sum, row) => sum + (row.total || 0), 0)}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              </TableFooter>
                            </Table>
                          </TableContainer>
                        </Paper>
                      </Grid>
                    </Grid>
                    
                    {/* Main Bugs Table */}
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                    <Paper elevation={2} sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          All Production Bugs
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Tooltip 
                            title="List of all production bugs. Use filters to narrow down by Team, Fix Version, and Iteration."
                            arrow
                            placement="top"
                          >
                            <IconButton size="small">
                              <InfoIcon fontSize="small" color="action" />
                            </IconButton>
                          </Tooltip>
                          <IconButton
                            size="small"
                            onClick={() => setWipExpanded(!wipExpanded)}
                            aria-label={wipExpanded ? 'Collapse table' : 'Expand table'}
                          >
                            {wipExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </Box>
                      </Box>
                      
                      {/* Filters */}
                      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                        {(() => {
                          // Calculate available filter options based on current filter state
                          // For each filter, apply the other filters and extract unique values
                          
                          // Get data filtered by other filters (excluding the filter being populated)
                          const getFilteredDataForTeam = () => {
                            return wipData.filter(bug => {
                              if (wipFilterFixVersion.length > 0 && !wipFilterFixVersion.includes(bug.fixVersion)) return false;
                              if (wipFilterIteration.length > 0 && !wipFilterIteration.includes(bug.iterationPath)) return false;
                              return true;
                            });
                          };
                          
                          const getFilteredDataForFixVersion = () => {
                            return wipData.filter(bug => {
                              if (wipFilterTeam.length > 0 && !wipFilterTeam.includes(bug.team)) return false;
                              if (wipFilterIteration.length > 0 && !wipFilterIteration.includes(bug.iterationPath)) return false;
                              return true;
                            });
                          };
                          
                          const getFilteredDataForIteration = () => {
                            return wipData.filter(bug => {
                              if (wipFilterTeam.length > 0 && !wipFilterTeam.includes(bug.team)) return false;
                              if (wipFilterFixVersion.length > 0 && !wipFilterFixVersion.includes(bug.fixVersion)) return false;
                              return true;
                            });
                          };
                          
                          // Extract unique values from filtered data
                          const availableTeamsSet = new Set(getFilteredDataForTeam().map(bug => bug.team).filter(Boolean));
                          // Include currently selected teams even if not in filtered data (so users can deselect them)
                          wipFilterTeam.forEach(team => availableTeamsSet.add(team));
                          const availableTeams = [...availableTeamsSet].sort();
                          
                          const availableFixVersionsSet = new Set(getFilteredDataForFixVersion().map(bug => bug.fixVersion).filter(Boolean));
                          // Include currently selected fix versions even if not in filtered data
                          wipFilterFixVersion.forEach(version => availableFixVersionsSet.add(version));
                          const availableFixVersions = [...availableFixVersionsSet].sort();
                          
                          const availableIterationsSet = new Set(getFilteredDataForIteration().map(bug => bug.iterationPath).filter(Boolean));
                          // Include currently selected iterations even if not in filtered data
                          wipFilterIteration.forEach(iteration => availableIterationsSet.add(iteration));
                          const availableIterations = [...availableIterationsSet].sort();
                          
                          return (
                            <>
                              <FormControl size="small" sx={{ minWidth: 200 }}>
                                <InputLabel>Team</InputLabel>
                                <Select
                                  multiple
                                  value={wipFilterTeam}
                                  label="Team"
                                  onChange={(e) => setWipFilterTeam(e.target.value)}
                                  renderValue={(selected) => {
                                    if (selected.length === 0) return 'All Teams';
                                    if (selected.length === 1) return selected[0];
                                    return `${selected.length} teams selected`;
                                  }}
                                  MenuProps={{
                                    PaperProps: {
                                      style: {
                                        maxHeight: 300,
                                      },
                                    },
                                  }}
                                >
                                  {availableTeams.map((team) => (
                                    <MenuItem key={team} value={team}>
                                      <Checkbox checked={wipFilterTeam.indexOf(team) > -1} />
                                      <ListItemText primary={team} />
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              
                              <FormControl size="small" sx={{ minWidth: 200 }}>
                                <InputLabel>Fix Version</InputLabel>
                                <Select
                                  multiple
                                  value={wipFilterFixVersion}
                                  label="Fix Version"
                                  onChange={(e) => setWipFilterFixVersion(e.target.value)}
                                  renderValue={(selected) => {
                                    if (selected.length === 0) return 'All Fix Versions';
                                    if (selected.length === 1) return selected[0];
                                    return `${selected.length} versions selected`;
                                  }}
                                  MenuProps={{
                                    PaperProps: {
                                      style: {
                                        maxHeight: 300,
                                      },
                                    },
                                  }}
                                >
                                  {availableFixVersions.map((version) => (
                                    <MenuItem key={version} value={version}>
                                      <Checkbox checked={wipFilterFixVersion.indexOf(version) > -1} />
                                      <ListItemText primary={version} />
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              
                              <FormControl size="small" sx={{ minWidth: 200 }}>
                                <InputLabel>Iteration</InputLabel>
                                <Select
                                  multiple
                                  value={wipFilterIteration}
                                  label="Iteration"
                                  onChange={(e) => setWipFilterIteration(e.target.value)}
                                  renderValue={(selected) => {
                                    if (selected.length === 0) return 'All Iterations';
                                    if (selected.length === 1) {
                                      const iter = selected[0];
                                      return iter.split('\\').pop() || iter;
                                    }
                                    return `${selected.length} iterations selected`;
                                  }}
                                  MenuProps={{
                                    PaperProps: {
                                      style: {
                                        maxHeight: 300,
                                      },
                                    },
                                  }}
                                >
                                  {availableIterations.map((iteration) => (
                                    <MenuItem key={iteration} value={iteration}>
                                      <Checkbox checked={wipFilterIteration.indexOf(iteration) > -1} />
                                      <ListItemText primary={iteration.split('\\').pop() || iteration} />
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </>
                          );
                        })()}
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
                          <Typography variant="body2" color="text.secondary">
                            {(() => {
                              const filtered = wipData.filter(bug => {
                                if (wipFilterTeam.length > 0 && !wipFilterTeam.includes(bug.team)) return false;
                                if (wipFilterFixVersion.length > 0 && !wipFilterFixVersion.includes(bug.fixVersion)) return false;
                                if (wipFilterIteration.length > 0 && !wipFilterIteration.includes(bug.iterationPath)) return false;
                                return true;
                              });
                              return `Showing ${filtered.length} of ${wipData.length} bugs`;
                            })()}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Collapse in={wipExpanded}>
                        <TableContainer>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell 
                                  sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                                  onClick={() => {
                                    if (wipSortField === 'id') {
                                      setWipSortDirection(wipSortDirection === 'asc' ? 'desc' : 'asc');
                                    } else {
                                      setWipSortField('id');
                                      setWipSortDirection('asc');
                                    }
                                  }}
                                >
                                  <Box display="flex" alignItems="center">
                                    ID
                                    {wipSortField === 'id' && (
                                      wipSortDirection === 'asc' ? 
                                        <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                                        <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>State</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Priority</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Team</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Fix Version</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Iteration</TableCell>
                                <TableCell 
                                  sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                                  onClick={() => {
                                    if (wipSortField === 'createdDate') {
                                      setWipSortDirection(wipSortDirection === 'asc' ? 'desc' : 'asc');
                                    } else {
                                      setWipSortField('createdDate');
                                      setWipSortDirection('desc');
                                    }
                                  }}
                                >
                                  <Box display="flex" alignItems="center">
                                    Created Date
                                    {wipSortField === 'createdDate' && (
                                      wipSortDirection === 'asc' ? 
                                        <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                                        <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell 
                                  sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                                  align="right"
                                  onClick={() => {
                                    if (wipSortField === 'age') {
                                      setWipSortDirection(wipSortDirection === 'asc' ? 'desc' : 'asc');
                                    } else {
                                      setWipSortField('age');
                                      setWipSortDirection('desc');
                                    }
                                  }}
                                >
                                  <Box display="flex" alignItems="center" justifyContent="flex-end">
                                    Age (days)
                                    {wipSortField === 'age' && (
                                      wipSortDirection === 'asc' ? 
                                        <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                                        <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Environment</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(() => {
                                const filtered = wipData.filter(bug => {
                                  if (wipFilterTeam.length > 0 && !wipFilterTeam.includes(bug.team)) return false;
                                  if (wipFilterFixVersion.length > 0 && !wipFilterFixVersion.includes(bug.fixVersion)) return false;
                                  if (wipFilterIteration.length > 0 && !wipFilterIteration.includes(bug.iterationPath)) return false;
                                  return true;
                                });
                                
                                const sorted = [...filtered].sort((a, b) => {
                                  let aValue = a[wipSortField];
                                  let bValue = b[wipSortField];
                                  
                                  if (wipSortField === 'age' || wipSortField === 'id') {
                                    aValue = Number(aValue) || 0;
                                    bValue = Number(bValue) || 0;
                                    return wipSortDirection === 'asc' ? aValue - bValue : bValue - aValue;
                                  }
                                  
                                  if (wipSortField === 'createdDate') {
                                    aValue = a.createdDate ? new Date(a.createdDate).getTime() : 0;
                                    bValue = b.createdDate ? new Date(b.createdDate).getTime() : 0;
                                    return wipSortDirection === 'asc' ? aValue - bValue : bValue - aValue;
                                  }
                                  
                                  aValue = String(aValue || '').toLowerCase();
                                  bValue = String(bValue || '').toLowerCase();
                                  return wipSortDirection === 'asc' 
                                    ? aValue.localeCompare(bValue) 
                                    : bValue.localeCompare(aValue);
                                });
                                
                                return sorted.length === 0 ? (
                                  <TableRow>
                                    <TableCell colSpan={10} align="center">
                                      <Typography variant="body2" color="text.secondary">
                                        No bugs found matching the selected filters.
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                ) : (
                                  sorted.map((bug) => {
                                    const adoUrl = `https://tr-legal.visualstudio.com/HighQ%20Tech/_workitems/edit/${bug.id}`;
                                    const getAgeColor = (age) => {
                                      if (!age) return '#9e9e9e';
                                      if (age > 90) return '#f57c00';
                                      if (age > 30) return '#fbc02d';
                                      return '#388e3c';
                                    };
                                    const ageColor = getAgeColor(bug.age);
                                    const iterationDisplay = bug.iterationPath ? bug.iterationPath.split('\\').pop() : 'Not Specified';
                                    
                                    return (
                                      <TableRow key={bug.id} hover>
                                        <TableCell>
                                          <Link
                                            href={adoUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            underline="hover"
                                            sx={{ fontWeight: 'medium', color: 'primary.main' }}
                                          >
                                            #{bug.id}
                                          </Link>
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 400, wordWrap: 'break-word' }}>
                                          <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                            {bug.title || 'Untitled'}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Chip
                                            label={bug.state || 'Unknown'}
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                              borderColor: getStateColor(bug.state),
                                              color: getStateColor(bug.state)
                                            }}
                                          />
                                        </TableCell>
                                        <TableCell>
                                          <Chip
                                            label={(() => {
                                              const priority = bug.priority;
                                              if (!priority) return 'P?';
                                              // If priority is already a string starting with P, use it as-is
                                              if (typeof priority === 'string' && priority.toUpperCase().startsWith('P')) {
                                                return priority.toUpperCase();
                                              }
                                              // Otherwise, it's a number, prepend P
                                              return `P${priority}`;
                                            })()}
                                            size="small"
                                            variant="outlined"
                                          />
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="body2" color="text.secondary">
                                            {bug.team || 'Unknown'}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="body2" color="text.secondary">
                                            {bug.fixVersion || '-'}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="body2" color="text.secondary">
                                            {iterationDisplay}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="body2" color="text.secondary">
                                            {bug.createdDate ? new Date(bug.createdDate).toLocaleDateString() : '-'}
                                          </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                          {bug.age !== null ? (
                                            <Chip
                                              label={`${bug.age} days`}
                                              size="small"
                                              sx={{
                                                backgroundColor: ageColor,
                                                color: 'white',
                                                fontWeight: 'bold'
                                              }}
                                            />
                                          ) : (
                                            <Typography variant="body2" color="text.secondary">-</Typography>
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          {bug.environment ? (
                                            <Chip
                                              label={bug.environment}
                                              size="small"
                                              variant="outlined"
                                            />
                                          ) : (
                                            <Typography variant="body2" color="text.secondary">-</Typography>
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })
                                );
                              })()}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Collapse>
                    </Paper>
                      </Grid>
                    </Grid>
                  </>
                );
              })()}

              {wipData.length === 0 && !wipLoading && (
                <Alert severity="info">
                  No production bugs found.
                </Alert>
              )}
            </>
          )}
        </Box>
      )}

      {activeTab === 7 && (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            A11Y Bug Analysis
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter an ADO saved filter/query URL to fetch bugs and see Cycle time, Activated on, Resolved on, and Time to Fix (from work item state transition history). Results are saved to the a11y-bugs collection.
          </Typography>
          {a11ySyncedAt && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
              Last synced: {new Date(a11ySyncedAt).toLocaleString()}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap', mb: 3 }}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              label="ADO filter URL"
              placeholder="https://dev.azure.com/org/project/_workitems/?_a=query..."
              value={a11yFilterUrl}
              onChange={isAdmin() ? (e) => { setA11yFilterUrl(e.target.value); setA11yError(null); } : undefined}
              InputProps={{ readOnly: !isAdmin() }}
              sx={{ maxWidth: 560 }}
            />
            {isAdmin() && (
              <>
                <Button
                  variant="contained"
                  onClick={async () => {
                    if (!a11yFilterUrl.trim()) return;
                    setA11yLoading(true);
                    setA11yError(null);
                    try {
                      const data = await fetchA11yBugAnalysis(a11yFilterUrl.trim());
                      setA11yData({ columns: data.columns || [], rows: data.rows || [] });
                      if (data.syncedAt) setA11ySyncedAt(data.syncedAt);
                    } catch (err) {
                      setA11yError(err.message || 'Failed to fetch from ADO');
                      setA11yData({ columns: [], rows: [] });
                    } finally {
                      setA11yLoading(false);
                    }
                  }}
                  disabled={a11yLoading || !a11yFilterUrl.trim()}
                >
                  {a11yLoading ? 'Fetching...' : 'Fetch from ADO'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={async () => {
                    if (!a11yFilterUrl.trim()) return;
                    setA11yLoading(true);
                    setA11yError(null);
                    try {
                      const data = await fetchA11yBugAnalysis(a11yFilterUrl.trim(), { limit: 100 });
                      setA11yData({ columns: data.columns || [], rows: data.rows || [] });
                      if (data.syncedAt) setA11ySyncedAt(data.syncedAt);
                    } catch (err) {
                      setA11yError(err.message || 'Failed to fetch from ADO');
                      setA11yData({ columns: [], rows: [] });
                    } finally {
                      setA11yLoading(false);
                    }
                  }}
                  disabled={a11yLoading || !a11yFilterUrl.trim()}
                  title="Fetch and process only the first 100 items from the filter (for testing)"
                >
                  {a11yLoading ? 'Fetching...' : 'Fetch First 100 items'}
                </Button>
              </>
            )}
          </Box>
          {a11yError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setA11yError(null)}>
              {a11yError}
            </Alert>
          )}
          {a11yData.columns.length > 0 && (() => {
            const rows = a11yData.rows || [];
            const hasActivated = (r) => {
              const v = r['Activated on'];
              return v != null && String(v).trim() !== '' && String(v).trim() !== '—';
            };
            const hasResolved = (r) => {
              const v = r['Resolved on'];
              return v != null && String(v).trim() !== '' && String(v).trim() !== '—';
            };
            const filteredRows = a11yExcludeMissingDates
              ? rows.filter((r) => hasActivated(r) && hasResolved(r))
              : rows;
            const parseNum = (val) => {
              if (val == null || val === '' || val === '—') return null;
              const n = parseFloat(String(val).trim());
              return isNaN(n) ? null : n;
            };
            const cycleValues = filteredRows.map((r) => parseNum(r['Cycle time'])).filter((v) => v != null);
            const timeToFixValues = filteredRows.map((r) => parseNum(r['Time to Fix'])).filter((v) => v != null);
            const avgCycleTime = cycleValues.length ? (cycleValues.reduce((a, b) => a + b, 0) / cycleValues.length).toFixed(1) : '—';
            const avgTimeToFix = timeToFixValues.length ? (timeToFixValues.reduce((a, b) => a + b, 0) / timeToFixValues.length).toFixed(1) : '—';

            const bucketLabels = ['< 2 days', '2-7 days', '8-14 days', '14-30 days', '30-60 days', '60+ days'];
            const getCycleBucket = (n) => {
              if (n < 2) return 0;
              if (n < 8) return 1;
              if (n < 14) return 2;
              if (n <= 30) return 3;
              if (n <= 60) return 4;
              return 5;
            };
            const getTimeToFixBucket = (n) => getCycleBucket(n);
            const cycleBucketCounts = [0, 0, 0, 0, 0, 0];
            const cycleBucketSums = [0, 0, 0, 0, 0, 0];
            const timeToFixBucketCounts = [0, 0, 0, 0, 0, 0];
            const timeToFixBucketSums = [0, 0, 0, 0, 0, 0];
            cycleValues.forEach((n) => {
              const b = getCycleBucket(n);
              cycleBucketCounts[b]++;
              cycleBucketSums[b] += n;
            });
            timeToFixValues.forEach((n) => {
              const b = getTimeToFixBucket(n);
              timeToFixBucketCounts[b]++;
              timeToFixBucketSums[b] += n;
            });
            const avgCycleByBucket = cycleBucketCounts.map((c, i) => (c > 0 ? Math.round(cycleBucketSums[i] / c) : null));
            const avgTimeToFixByBucket = timeToFixBucketCounts.map((c, i) => (c > 0 ? Math.round(timeToFixBucketSums[i] / c) : null));

            const cycleChartData = {
              labels: bucketLabels,
              datasets: [{
                label: 'Avg Cycle time (days)',
                data: avgCycleByBucket,
                backgroundColor: ['#90caf9', '#64b5f6', '#42a5f5', '#2196f3', '#1976d2', '#0d47a1'],
                borderColor: ['#64b5f6', '#42a5f5', '#2196f3', '#1976d2', '#1565c0', '#0d47a1'],
                borderWidth: 1
              }]
            };
            const timeToFixChartData = {
              labels: bucketLabels,
              datasets: [{
                label: 'Avg Time to Fix (days)',
                data: avgTimeToFixByBucket,
                backgroundColor: ['#a5d6a7', '#81c784', '#66bb6a', '#4caf50', '#43a047', '#2e7d32'],
                borderColor: ['#81c784', '#66bb6a', '#4caf50', '#43a047', '#388e3c', '#2e7d32'],
                borderWidth: 1
              }]
            };
            const cycleBucketChartOptions = {
              responsive: true,
              maintainAspectRatio: false,
              layout: { padding: 4 },
              plugins: {
                legend: { display: false },
                datalabels: {
                  anchor: 'end',
                  align: 'top',
                  formatter: (v, ctx) => {
                    if (v == null || !Number.isFinite(v)) return '';
                    const n = cycleBucketCounts[ctx.dataIndex] || 0;
                    return `${v} (${n})`;
                  },
                  font: { weight: 'bold', size: 13 }
                }
              },
              scales: {
                y: { beginAtZero: true, ticks: { callback: (v) => (Number.isFinite(v) ? Math.round(v) : v) } },
                x: {}
              }
            };
            const timeToFixBucketChartOptions = {
              responsive: true,
              maintainAspectRatio: false,
              layout: { padding: 4 },
              plugins: {
                legend: { display: false },
                datalabels: {
                  anchor: 'end',
                  align: 'top',
                  formatter: (v, ctx) => {
                    if (v == null || !Number.isFinite(v)) return '';
                    const n = timeToFixBucketCounts[ctx.dataIndex] || 0;
                    return `${v} (${n})`;
                  },
                  font: { weight: 'bold', size: 13 }
                }
              },
              scales: {
                y: { beginAtZero: true, ticks: { callback: (v) => (Number.isFinite(v) ? Math.round(v) : v) } },
                x: {}
              }
            };

            // Timeline: Before 2023, then every quarter from 2023 to last date in data
            const parseDateFromRow = (row, keys) => {
              for (const k of keys) {
                const raw = row[k];
                if (raw == null || raw === '') continue;
                const d = new Date(raw);
                if (!isNaN(d.getTime())) return d;
              }
              return null;
            };
            // Try any value in row that looks like a date (catch alternate key names)
            const parseAnyDateFromRow = (row) => {
              const tried = parseDateFromRow(row, ['System.CreatedDate', 'Created Date', 'CreatedDate', 'Created'])
                || parseDateFromRow(row, ['Microsoft.VSTS.Common.ClosedDate', 'Closed Date', 'Resolved on', 'Activated on']);
              if (tried) return tried;
              for (const key of Object.keys(row)) {
                const raw = row[key];
                if (raw == null || raw === '') continue;
                if (typeof raw !== 'string' && typeof raw !== 'number') continue;
                const d = new Date(raw);
                if (!isNaN(d.getTime()) && d.getFullYear() >= 2000 && d.getFullYear() <= 2100) return d;
              }
              return null;
            };
            const parseCreated = (row) => parseAnyDateFromRow(row);
            const getPeriodKey = (d) => {
              if (d.getFullYear() < 2023) return 'Before 2023';
              const q = Math.ceil((d.getMonth() + 1) / 3);
              return `${d.getFullYear()} Q${q}`;
            };
            const UNKNOWN_PERIOD = 'Unknown';
            // Assign every row to a period first, then build labels from periods that appear in data
            const periodToCycleSum = {};
            const periodToCycleCount = {};
            const periodToFixSum = {};
            const periodToFixCount = {};
            filteredRows.forEach((row) => {
              const created = parseCreated(row);
              const period = created ? getPeriodKey(created) : UNKNOWN_PERIOD;
              if (!periodToCycleSum[period]) {
                periodToCycleSum[period] = 0;
                periodToCycleCount[period] = 0;
                periodToFixSum[period] = 0;
                periodToFixCount[period] = 0;
              }
              const cycle = parseNum(row['Cycle time']);
              const fix = parseNum(row['Time to Fix']);
              if (cycle != null) {
                periodToCycleSum[period] += cycle;
                periodToCycleCount[period]++;
              }
              if (fix != null) {
                periodToFixSum[period] += fix;
                periodToFixCount[period]++;
              }
            });
            const periodsInData = Object.keys(periodToCycleSum);
            let maxYear = 2022;
            let maxQuarter = 4;
            periodsInData.forEach((p) => {
              if (p === UNKNOWN_PERIOD || p === 'Before 2023') return;
              const m = p.match(/^(\d{4})\s*Q(\d)$/);
              if (m) {
                const y = parseInt(m[1], 10);
                const q = parseInt(m[2], 10);
                if (y > maxYear || (y === maxYear && q > maxQuarter)) {
                  maxYear = y;
                  maxQuarter = q;
                }
              }
            });
            const periodLabels = ['Before 2023'];
            for (let y = 2023; y <= maxYear; y++) {
              for (let q = 1; q <= 4; q++) {
                if (y === maxYear && q > maxQuarter) break;
                periodLabels.push(`${y} Q${q}`);
              }
            }
            if (periodToCycleSum[UNKNOWN_PERIOD] !== undefined || periodToFixSum[UNKNOWN_PERIOD] !== undefined) {
              periodLabels.push(UNKNOWN_PERIOD);
            }
            const avgCycleByPeriod = periodLabels.map((p) =>
              (periodToCycleCount[p] || 0) > 0
                ? Math.round(periodToCycleSum[p] / periodToCycleCount[p])
                : null
            );
            const avgTimeToFixByPeriod = periodLabels.map((p) =>
              (periodToFixCount[p] || 0) > 0
                ? Math.round(periodToFixSum[p] / periodToFixCount[p])
                : null
            );
            const timelineCycleChartData = {
              labels: periodLabels,
              datasets: [{
                label: 'Avg Cycle time (days)',
                data: avgCycleByPeriod,
                backgroundColor: '#64b5f6',
                borderColor: '#1976d2',
                borderWidth: 1
              }]
            };
            const timelineFixChartData = {
              labels: periodLabels,
              datasets: [{
                label: 'Avg Time to Fix (days)',
                data: avgTimeToFixByPeriod,
                backgroundColor: '#81c784',
                borderColor: '#43a047',
                borderWidth: 1
              }]
            };
            const timelineCycleChartOptions = {
              responsive: true,
              maintainAspectRatio: false,
              layout: { padding: 4 },
              plugins: {
                legend: { display: false },
                datalabels: {
                  anchor: 'end',
                  align: 'top',
                  formatter: (v, ctx) => {
                    if (v == null || v === '' || !Number.isFinite(v)) return '';
                    const period = periodLabels[ctx.dataIndex];
                    const n = (period && periodToCycleCount[period]) || 0;
                    return `${Math.round(v)} (${n})`;
                  }
                }
              },
              scales: {
                y: { beginAtZero: true, ticks: { callback: (v) => (Number.isFinite(v) ? Math.round(v) : v) } },
                x: {}
              }
            };
            const timelineFixChartOptions = {
              responsive: true,
              maintainAspectRatio: false,
              layout: { padding: 4 },
              plugins: {
                legend: { display: false },
                datalabels: {
                  anchor: 'end',
                  align: 'top',
                  formatter: (v, ctx) => {
                    if (v == null || v === '' || !Number.isFinite(v)) return '';
                    const period = periodLabels[ctx.dataIndex];
                    const n = (period && periodToFixCount[period]) || 0;
                    return `${Math.round(v)} (${n})`;
                  }
                }
              },
              scales: {
                y: { beginAtZero: true, ticks: { callback: (v) => (Number.isFinite(v) ? Math.round(v) : v) } },
                x: {}
              }
            };

            // Columns to show: ID (link), Title, State, dates, Cycle time, Time to Fix
            const displayColumns = [
              { key: 'id', label: 'ID', keys: ['System.Id', 'ID'], link: true },
              { key: 'title', label: 'Title', keys: ['System.Title', 'Title'] },
              { key: 'state', label: 'State', keys: ['System.State', 'State'] },
              { key: 'created', label: 'Created', keys: ['System.CreatedDate', 'Created Date'], date: true },
              { key: 'closed', label: 'Closed', keys: ['Microsoft.VSTS.Common.ClosedDate', 'Closed Date'], date: true },
              { key: 'activated', label: 'Activated on', keys: ['Activated on'], date: true },
              { key: 'resolved', label: 'Resolved on', keys: ['Resolved on'], date: true },
              { key: 'cycleTime', label: 'Cycle time', keys: ['Cycle time'] },
              { key: 'timeToFix', label: 'Time to Fix', keys: ['Time to Fix'] }
            ];
            const getCellValue = (row, keyOptions) => {
              for (const k of keyOptions) {
                const v = row[k];
                if (v != null && v !== '') return String(v);
              }
              return '—';
            };
            const formatDateToMinutes = (value) => {
              if (value == null || value === '' || value === '—') return '—';
              try {
                const d = new Date(value);
                if (isNaN(d.getTime())) return value;
                const y = d.getFullYear();
                const m = String(d.getMonth() + 1).padStart(2, '0');
                const day = String(d.getDate()).padStart(2, '0');
                const h = String(d.getHours()).padStart(2, '0');
                const min = String(d.getMinutes()).padStart(2, '0');
                return `${y}-${m}-${day} ${h}:${min}`;
              } catch (e) {
                return value;
              }
            };
            const getAdoEditUrl = (id) => {
              if (!id || !a11yFilterUrl.trim()) return null;
              try {
                const u = a11yFilterUrl.trim();
                const m = u.match(/^(https?:\/\/[^/]+\/[^/]+\/[^/]+)/);
                if (m) return `${m[1]}/_workitems/edit/${encodeURIComponent(id)}`;
              } catch (e) {}
              return null;
            };
            const getSortValue = (row, col) => {
              const raw = getCellValue(row, col.keys);
              if (raw === '—' || raw == null || raw === '') return null;
              if (col.date) {
                const d = new Date(raw);
                return isNaN(d.getTime()) ? null : d.getTime();
              }
              if (col.key === 'cycleTime' || col.key === 'timeToFix') {
                const n = parseNum(raw);
                return n != null ? n : null;
              }
              return String(raw).toLowerCase();
            };
            const sortedRows = (() => {
              if (!a11ySortBy) return filteredRows;
              const col = displayColumns.find((c) => c.key === a11ySortBy);
              if (!col) return filteredRows;
              const order = a11ySortOrder === 'asc' ? 1 : -1;
              return [...filteredRows].sort((a, b) => {
                const va = getSortValue(a, col);
                const vb = getSortValue(b, col);
                if (va == null && vb == null) return 0;
                if (va == null) return order;
                if (vb == null) return -order;
                if (typeof va === 'number' && typeof vb === 'number') return order * (va - vb);
                return order * String(va).localeCompare(String(vb));
              });
            })();
            const a11yPageMax = Math.max(0, Math.ceil(sortedRows.length / a11yRowsPerPage) - 1);
            const effectivePage = Math.min(a11yPage, a11yPageMax);
            const paginatedRows = sortedRows.slice(effectivePage * a11yRowsPerPage, (effectivePage + 1) * a11yRowsPerPage);
            const handleA11ySort = (colKey) => {
              const isAsc = a11ySortBy === colKey && a11ySortOrder === 'asc';
              setA11ySortBy(colKey);
              setA11ySortOrder(isAsc ? 'desc' : 'asc');
              setA11yPage(0);
            };
            return (
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={a11yExcludeMissingDates}
                        onChange={(e) => setA11yExcludeMissingDates(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Exclude items with missing dates"
                  />
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Paper elevation={2} sx={{ p: 2, minWidth: 160, textAlign: 'center', background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)', border: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Average Cycle time
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                        {avgCycleTime}{avgCycleTime !== '—' ? ' days' : ''}
                      </Typography>
                    </Paper>
                    <Paper elevation={2} sx={{ p: 2, minWidth: 160, textAlign: 'center', background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)', border: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Average Time to Fix
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.dark' }}>
                        {avgTimeToFix}{avgTimeToFix !== '—' ? ' days' : ''}
                      </Typography>
                    </Paper>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                  <Paper elevation={2} sx={{ p: 2, flex: '1 1 45%', minWidth: 280, boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                      Issues by Cycle time
                    </Typography>
                    <Box sx={{ width: '100%', minHeight: 280, flex: 1 }}>
                      <Bar data={cycleChartData} options={cycleBucketChartOptions} />
                    </Box>
                  </Paper>
                  <Paper elevation={2} sx={{ p: 2, flex: '1 1 45%', minWidth: 280, boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                      Issues by Time to Fix
                    </Typography>
                    <Box sx={{ width: '100%', minHeight: 280, flex: 1 }}>
                      <Bar data={timeToFixChartData} options={timeToFixBucketChartOptions} />
                    </Box>
                  </Paper>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                  <Paper elevation={2} sx={{ p: 2, flex: '1 1 45%', minWidth: 280, boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                      Average Cycle time (by quarter raised)
                    </Typography>
                    <Box sx={{ width: '100%', minHeight: 280, flex: 1 }}>
                      <Bar data={timelineCycleChartData} options={timelineCycleChartOptions} />
                    </Box>
                  </Paper>
                  <Paper elevation={2} sx={{ p: 2, flex: '1 1 45%', minWidth: 280, boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                      Average Time to Fix (by quarter raised)
                    </Typography>
                    <Box sx={{ width: '100%', minHeight: 280, flex: 1 }}>
                      <Bar data={timelineFixChartData} options={timelineFixChartOptions} />
                    </Box>
                  </Paper>
                </Box>
                <TableContainer component={Paper} elevation={2} sx={{ overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {displayColumns.map((col) => (
                        <TableCell key={col.key} sx={{ fontWeight: 700 }} sortDirection={a11ySortBy === col.key ? a11ySortOrder : false}>
                          <TableSortLabel
                            active={a11ySortBy === col.key}
                            direction={a11ySortBy === col.key ? a11ySortOrder : 'asc'}
                            onClick={() => handleA11ySort(col.key)}
                          >
                            {col.label}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedRows.map((row, idx) => (
                      <TableRow key={effectivePage * a11yRowsPerPage + idx} hover>
                        {displayColumns.map((col) => {
                          const raw = getCellValue(row, col.keys);
                          const value = col.date ? formatDateToMinutes(raw) : raw;
                          const adoUrl = col.link ? getAdoEditUrl(raw !== '—' ? raw : row['System.Id'] ?? row['ID']) : null;
                          return (
                            <TableCell key={col.key}>
                              {col.link && adoUrl ? (
                                <Link href={adoUrl} target="_blank" rel="noopener noreferrer" underline="hover">
                                  {value}
                                </Link>
                              ) : (
                                value
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TablePagination
                        rowsPerPageOptions={[100]}
                        colSpan={displayColumns.length}
                        count={sortedRows.length}
                        rowsPerPage={a11yRowsPerPage}
                        page={effectivePage}
                        onPageChange={(_, newPage) => setA11yPage(newPage)}
                        labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`}
                      />
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
              </Box>
            );
          })()}
        </Box>
      )}

      {activeTab === 8 && (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            Code Analysis
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter an Azure DevOps work item ID (bug) to run the enhanced bug analysis agent. The report is generated using Claude and optional codebase analysis, then displayed below.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 3 }}>
            <TextField
              variant="outlined"
              size="small"
              label="ADO ID"
              placeholder="e.g. 12345"
              value={codeAnalysisAdoId}
              onChange={(e) => { setCodeAnalysisAdoId(e.target.value); setCodeAnalysisError(null); }}
              sx={{ width: 180 }}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            />
            <Button
              variant="contained"
              onClick={async () => {
                const id = codeAnalysisAdoId.trim();
                if (!id) return;
                setCodeAnalysisLoading(true);
                setCodeAnalysisError(null);
                try {
                  const data = await fetchBugCodeAnalysis(id);
                  if (data.scanLog || data.lastRefreshAt) {
                    persistCodeAnalysisScanLog({
                      scanLog: data.scanLog,
                      lastRefreshAt: data.lastRefreshAt,
                      adoId: data.adoId || id
                    });
                  }
                  loadAnalysisHistory(analysisHistoryPage);
                } catch (err) {
                  setCodeAnalysisError(err.message || 'Failed to run code analysis');
                } finally {
                  setCodeAnalysisLoading(false);
                }
              }}
              disabled={codeAnalysisLoading || !codeAnalysisAdoId.trim()}
            >
              {codeAnalysisLoading ? 'Running analysis…' : 'Run Analysis'}
            </Button>
          </Box>
          {codeAnalysisError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCodeAnalysisError(null)}>
              {codeAnalysisError}
            </Alert>
          )}
          {codeAnalysisLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">
                Running enhanced bug analysis (this may take 1–3 minutes)…
              </Typography>
            </Box>
          )}

          {/* Analysis History Table */}
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              Analysis History
            </Typography>
            {analysisHistoryLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={1}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ADO ID</TableCell>
                      <TableCell>Analysis Date</TableCell>
                      <TableCell>ADO Bug State</TableCell>
                      <TableCell>Created Date</TableCell>
                      <TableCell>Analysis Link</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analysisHistory.map((record) => (
                      <React.Fragment key={record._id}>
                        <TableRow>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <IconButton
                                size="small"
                                onClick={() => toggleAnalysisExpansion(record._id)}
                              >
                                {expandedAnalysis[record._id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </IconButton>
                              <Chip label={record.adoId} size="small" />
                            </Box>
                          </TableCell>
                          <TableCell>
                            {new Date(record.analysisDate).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </TableCell>
                          <TableCell>
                            {record.adoBugState ? (
                              <Chip
                                label={record.adoBugState}
                                size="small"
                                color={record.adoBugState === 'Resolved' ? 'success' :
                                       record.adoBugState === 'Active' ? 'error' : 'default'}
                              />
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                N/A
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {record.adoCreatedDate ?
                              new Date(record.adoCreatedDate).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              }) : 'N/A'
                            }
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Tooltip title="Sync details from ADO and update table">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleSyncFromAdo(record)}
                                    disabled={!!syncingAdoId}
                                  >
                                    {syncingAdoId === record.adoId ? (
                                      <CircularProgress size={20} />
                                    ) : (
                                      <SyncIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Regenerate analysis">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRegenerateAnalysis(record)}
                                    disabled={!!regeneratingAdoId}
                                  >
                                    {regeneratingAdoId === record.adoId ? (
                                      <CircularProgress size={20} />
                                    ) : (
                                      <ReplayIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Delete this analysis record">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  if (window.confirm('Are you sure you want to delete this analysis record?')) {
                                    handleDeleteAnalysis(record._id);
                                  }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={6} sx={{ p: 0 }}>
                            <Collapse in={!!expandedAnalysis[record._id]} timeout="auto">
                              <Box sx={{ p: 3, backgroundColor: 'action.hover' }}>
                                {expandedAnalysis[record._id] ? (
                                  <Paper elevation={0} sx={{ p: 2, backgroundColor: 'background.paper' }}>
                                    <Typography component="div" sx={{
                                      '& pre': { whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
                                      '& code': { backgroundColor: 'action.hover', p: 0.5, borderRadius: 1 },
                                      '& table': { borderCollapse: 'collapse', width: '100%' },
                                      '& th, & td': { border: '1px solid', borderColor: 'divider', p: 1 }
                                    }}>
                                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                        {expandedAnalysis[record._id]}
                                      </ReactMarkdown>
                                    </Typography>
                                  </Paper>
                                ) : (
                                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                    <CircularProgress size={24} />
                                  </Box>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                    {analysisHistory.length === 0 && !analysisHistoryLoading && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                            No analysis records found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TablePagination
                        rowsPerPageOptions={[]}
                        component="td"
                        colSpan={6}
                        count={analysisHistoryTotal}
                        rowsPerPage={analysisHistoryLimit}
                        page={analysisHistoryPage}
                        onPageChange={handleAnalysisHistoryPageChange}
                        showFirstButton
                        showLastButton
                      />
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            )}
          </Box>

          {codeAnalysisScanLog && (codeAnalysisScanLog.scanLog || codeAnalysisScanLog.lastRefreshAt) && (
            <Accordion
              defaultExpanded={false}
              disableGutters
              sx={{
                mt: 3,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                '&:before': { display: 'none' },
                bgcolor: 'action.hover'
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, minHeight: 48 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Repositories used for code scan
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
                {codeAnalysisScanLog.lastRefreshAt && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    <strong>Last analysis refresh:</strong>{' '}
                    {new Date(codeAnalysisScanLog.lastRefreshAt).toLocaleString()}
                    {codeAnalysisScanLog.adoId && (
                      <> · ADO ID: {codeAnalysisScanLog.adoId}</>
                    )}
                  </Typography>
                )}
                {codeAnalysisScanLog.scanLog?.reposRoot && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Root: <code style={{ wordBreak: 'break-all' }}>{codeAnalysisScanLog.scanLog.reposRoot}</code>
                  </Typography>
                )}
                {codeAnalysisScanLog.scanLog?.error && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    {codeAnalysisScanLog.scanLog.error}
                  </Alert>
                )}
                {Array.isArray(codeAnalysisScanLog.scanLog?.repos) && codeAnalysisScanLog.scanLog.repos.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Folder</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Branch</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Last commit (local)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {codeAnalysisScanLog.scanLog.repos.map((r) => (
                          <TableRow key={r.name}>
                            <TableCell>{r.name}</TableCell>
                            <TableCell>{r.branch || '—'}</TableCell>
                            <TableCell>
                              {r.lastCommitAt
                                ? new Date(r.lastCommitAt).toLocaleString()
                                : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  !codeAnalysisScanLog.scanLog?.error && (
                    <Typography variant="body2" color="text.secondary">
                      No repository folders found under the configured path.
                    </Typography>
                  )
                )}
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  This log updates each time you run analysis. It reflects folders under <code>BUG_ANALYSIS_REPOS_DIR</code> and each clone&apos;s current branch and latest commit time.
                </Typography>
              </AccordionDetails>
            </Accordion>
          )}
        </Box>
      )}

      {/* Bug Details Dialog */}
      <WorkItemsDialog
        open={bugDetailsDialogOpen}
        onClose={() => setBugDetailsDialogOpen(false)}
        workItems={bugDetailsDialogBugs}
        title={bugDetailsDialogTitle}
      />
    </Box>
  );
};

export default ProductionDefects;
